// ============================================================================
// SS1 CHEMISTRY — COMPLETE LEARNING MODULE (27 TOPICS)
// NERDC Scheme of Work — First Term to Third Term
// ============================================================================

export const WAEC_CHEMISTRY_LEARN_SS1 = [
  {
    "topic": "Introduction to Chemistry",
    "topicCode": "SS1-CHEM-01",
    "module": "Foundations of Chemistry",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Chemistry</span> is the branch of science that studies the <span class=\"learn-keyword\">composition, structure, properties, and changes</span> of matter. It is often called the <span class=\"learn-keyword\">central science</span> because it connects other natural sciences such as physics, biology, geology, and environmental science.\n</div>\n\n<h3 class=\"learn-subheading\">1. Definition and Scope</h3>\n<p class=\"learn-p\">Chemistry is the scientific study of matter, its properties, the changes it undergoes, and the energy involved in those changes. Matter is anything that has mass and occupies space.</p>\n\n<h3 class=\"learn-subheading\">2. Brief History</h3>\n<ul class=\"learn-list\">\n  <li><strong>Robert Boyle (1627–1691):</strong> Distinguished chemistry from alchemy; formulated Boyle's Law.</li>\n  <li><strong>Antoine Lavoisier (1743–1794):</strong> \"Father of Modern Chemistry\"; discovered oxygen's role in combustion.</li>\n  <li><strong>John Dalton (1766–1844):</strong> Proposed the atomic theory of matter.</li>\n  <li><strong>Dmitri Mendeleev (1834–1907):</strong> Developed the Periodic Table.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Branches of Chemistry</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Branch</th><th>Focus</th></tr></thead>\n    <tbody>\n      <tr><td>Organic Chemistry</td><td>Carbon-containing compounds</td></tr>\n      <tr><td>Inorganic Chemistry</td><td>Non-carbon compounds; metals, minerals</td></tr>\n      <tr><td>Physical Chemistry</td><td>Energy and physical properties in reactions</td></tr>\n      <tr><td>Analytical Chemistry</td><td>Identifying and measuring substances</td></tr>\n      <tr><td>Biochemistry</td><td>Chemical processes in living organisms</td></tr>\n      <tr><td>Industrial Chemistry</td><td>Large-scale chemical production</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Importance of Chemistry</h3>\n<ul class=\"learn-list\">\n  <li><strong>Medicine:</strong> Development of drugs and vaccines.</li>\n  <li><strong>Agriculture:</strong> Fertilisers and pesticides improve yields.</li>\n  <li><strong>Industry:</strong> Manufacture of plastics, textiles, metals, and fuels.</li>\n  <li><strong>Environment:</strong> Understanding and reducing pollution.</li>\n  <li><strong>Food Science:</strong> Preservation and nutritional analysis.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Antoine Lavoisier is the \"Father of Modern Chemistry\" — a popular exam question. Know the six branches and their focus areas.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Chemistry is the study of matter, its composition, properties, and transformations. It has six main branches and is important in medicine, agriculture, industry, food science, and environmental protection.\n</div>\n  ",
    "questions": [
      {
        "q": "Which scientist is known as the 'Father of Modern Chemistry'?",
        "o": ["Robert Boyle", "John Dalton", "Antoine Lavoisier", "Dmitri Mendeleev"],
        "a": 2,
        "e": "Antoine Lavoisier (1743–1794) is called the Father of Modern Chemistry. He discovered the role of oxygen in combustion and introduced systematic chemical nomenclature.",
        "h": "He discovered oxygen's role in burning and named many chemical substances.",
        "yr": "SS1"
      },
      {
        "q": "Chemistry is best defined as the scientific study of:",
        "o": ["Living organisms and their functions", "Matter, its properties, changes, and energy involved", "Forces and motion in the universe", "The earth's structure and rocks"],
        "a": 1,
        "e": "Chemistry is the scientific study of matter — its composition, structure, properties, the changes it undergoes, and the energy involved in those changes.",
        "h": "Chemistry focuses on matter and its transformations.",
        "yr": "SS1"
      },
      {
        "q": "Which branch of chemistry deals with carbon-containing compounds?",
        "o": ["Inorganic chemistry", "Physical chemistry", "Organic chemistry", "Analytical chemistry"],
        "a": 2,
        "e": "Organic chemistry is the branch that deals with carbon-containing compounds, including hydrocarbons, polymers, and biochemicals.",
        "h": "Carbon is the key element in this branch.",
        "yr": "SS1"
      },
      {
        "q": "Who proposed the atomic theory of matter?",
        "o": ["Antoine Lavoisier", "John Dalton", "Robert Boyle", "Dmitri Mendeleev"],
        "a": 1,
        "e": "John Dalton (1766–1844) proposed the atomic theory of matter, which stated that all matter is made up of tiny, indivisible particles called atoms.",
        "h": "His theory described atoms as the basic building blocks of matter.",
        "yr": "SS1"
      },
      {
        "q": "The ancient practice that preceded modern chemistry is called:",
        "o": ["Alchemy", "Astronomy", "Astrology", "Anatomy"],
        "a": 0,
        "e": "Alchemy was the ancient practice that preceded chemistry. Alchemists sought to transform base metals into gold and unknowingly laid the groundwork for modern chemistry.",
        "h": "It aimed to turn metals into gold.",
        "yr": "SS1"
      },
      {
        "q": "Which branch of chemistry studies energy and physical properties of chemical reactions?",
        "o": ["Biochemistry", "Organic chemistry", "Physical chemistry", "Industrial chemistry"],
        "a": 2,
        "e": "Physical chemistry studies the energy and physical properties involved in chemical reactions, including thermodynamics, kinetics, and quantum mechanics.",
        "h": "It bridges chemistry and physics.",
        "yr": "SS1"
      },
      {
        "q": "The use of fertilisers and pesticides in agriculture is an application of:",
        "o": ["Biochemistry", "Agricultural chemistry", "Analytical chemistry", "Nuclear chemistry"],
        "a": 1,
        "e": "Agricultural chemistry applies chemical knowledge to improve farming — including fertilisers, pesticides, herbicides, and soil amendments to boost crop yields.",
        "h": "Fertilisers and pesticides are chemical products applied to farming.",
        "yr": "SS1"
      },
      {
        "q": "Analytical chemistry is primarily concerned with:",
        "o": ["Producing industrial chemicals at large scale", "Identifying and measuring substances in samples", "Studying chemical processes in living things", "Developing new polymers"],
        "a": 1,
        "e": "Analytical chemistry focuses on identifying the components of substances and measuring their quantities. Techniques include chromatography, spectroscopy, and titration.",
        "h": "Analysis means identification and measurement.",
        "yr": "SS1"
      },
      {
        "q": "Chemistry is called the 'central science' because:",
        "o": ["It was the first science to be studied", "It connects and overlaps with other natural sciences", "It has the most practising scientists worldwide", "It is studied at the centre of universities"],
        "a": 1,
        "e": "Chemistry is called the central science because it bridges and connects physics, biology, geology, and environmental science.",
        "h": "It sits at the centre, connecting all other sciences.",
        "yr": "SS1"
      },
      {
        "q": "Which scientist developed the Periodic Table of Elements?",
        "o": ["Robert Boyle", "Antoine Lavoisier", "John Dalton", "Dmitri Mendeleev"],
        "a": 3,
        "e": "Dmitri Mendeleev (1834–1907) developed the Periodic Table, arranging elements by increasing atomic mass and grouping them by similar properties. He even predicted properties of undiscovered elements.",
        "h": "He arranged elements into the famous table still used today.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT a branch of chemistry?",
        "o": ["Biochemistry", "Inorganic chemistry", "Seismology", "Analytical chemistry"],
        "a": 2,
        "e": "Seismology is the study of earthquakes and is a branch of geophysics, not chemistry. The branches of chemistry include organic, inorganic, physical, analytical, biochemistry, and industrial chemistry.",
        "h": "Seismology deals with earthquakes — not chemicals.",
        "yr": "SS1"
      },
      {
        "q": "Biochemistry is the branch of chemistry that studies:",
        "o": ["Chemical reactions in laboratories", "Chemical processes in living organisms", "Properties of metals and alloys", "Production of synthetic fibres"],
        "a": 1,
        "e": "Biochemistry studies the chemical processes and substances that occur within living organisms — including metabolism, DNA, proteins, and enzymes.",
        "h": "Bio = life; it applies chemistry to biological systems.",
        "yr": "SS1"
      },
      {
        "q": "Robert Boyle's major contribution to chemistry is:",
        "o": ["Discovering oxygen", "Proposing the atomic theory", "Distinguishing chemistry from alchemy and formulating Boyle's Law", "Developing the Periodic Table"],
        "a": 2,
        "e": "Robert Boyle (1627–1691) helped separate chemistry from alchemy by emphasising experimentation and formulated Boyle's Law, relating gas pressure and volume.",
        "h": "He separated chemistry from alchemy through experimental approach.",
        "yr": "SS1"
      },
      {
        "q": "The manufacture of plastics, textiles, and fuels is an application of:",
        "o": ["Organic chemistry only", "Physical chemistry", "Industrial chemistry", "Biochemistry"],
        "a": 2,
        "e": "Industrial chemistry involves the large-scale production of chemical products including plastics, synthetic textiles, fuels, fertilisers, and pharmaceuticals for commercial use.",
        "h": "Industrial = large-scale manufacturing.",
        "yr": "SS1"
      },
      {
        "q": "What is matter in the context of chemistry?",
        "o": ["Any living substance", "Anything that has mass and occupies space", "Any substance visible to the naked eye", "Any pure substance with a fixed composition"],
        "a": 1,
        "e": "In chemistry, matter is defined as anything that has mass and occupies space (has volume). This includes solids, liquids, gases, and plasma — whether visible or not.",
        "h": "Matter must have both mass and volume.",
        "yr": "SS1"
      },
      {
        "q": "Which scientist discovered the role of oxygen in combustion?",
        "o": ["John Dalton", "Robert Boyle", "Dmitri Mendeleev", "Antoine Lavoisier"],
        "a": 3,
        "e": "Antoine Lavoisier discovered that combustion requires oxygen. He debunked the earlier phlogiston theory and showed that substances gain mass when they burn in oxygen.",
        "h": "He showed that burning consumes oxygen.",
        "yr": "SS1"
      },
      {
        "q": "The development of drugs and vaccines involves which application of chemistry?",
        "o": ["Industrial chemistry", "Nuclear chemistry", "Pharmaceutical/medicinal chemistry", "Geochemistry"],
        "a": 2,
        "e": "Chemistry is fundamental to medicine. Drug development, vaccine production, and diagnostic tests all rely heavily on chemical knowledge, particularly organic and biochemistry.",
        "h": "Drugs and vaccines are chemical products used in medicine.",
        "yr": "SS1"
      },
      {
        "q": "Inorganic chemistry deals primarily with:",
        "o": ["Carbon compounds", "Compounds of living organisms", "Non-carbon compounds including metals and minerals", "Reaction rates and energy"],
        "a": 2,
        "e": "Inorganic chemistry studies non-carbon-containing compounds, including metals, minerals, salts, acids, bases, and coordination compounds.",
        "h": "Inorganic = without carbon (mainly).",
        "yr": "SS1"
      },
      {
        "q": "Which century did modern chemistry begin to emerge from alchemy?",
        "o": ["12th century", "17th century", "19th century", "20th century"],
        "a": 1,
        "e": "Modern chemistry began to emerge from alchemy in the 17th century, largely due to Robert Boyle who emphasised systematic experimentation over mysticism.",
        "h": "Boyle lived in the 1600s — the 17th century.",
        "yr": "SS1"
      },
      {
        "q": "Understanding and reducing pollution is an application of chemistry in:",
        "o": ["Food science", "Environmental science", "Nuclear physics", "Astronomy"],
        "a": 1,
        "e": "Environmental chemistry involves understanding chemical pollutants in air, water, and soil, and developing methods to reduce or remediate pollution.",
        "h": "Pollution control is an environmental application.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Chemical Industries and Laboratory Apparatus",
    "topicCode": "SS1-CHEM-02",
    "module": "Foundations of Chemistry",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The <span class=\"learn-keyword\">chemical laboratory</span> is a specially equipped room for conducting experiments safely. The <span class=\"learn-keyword\">chemical industry</span> uses chemical processes on a large scale to manufacture useful products. Knowing laboratory apparatus and their correct uses is fundamental to practical chemistry.\n</div>\n\n<h3 class=\"learn-subheading\">1. Common Laboratory Apparatus and Uses</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Apparatus</th><th>Use</th></tr></thead>\n    <tbody>\n      <tr><td>Beaker</td><td>Holding and heating liquids; mixing solutions</td></tr>\n      <tr><td>Conical (Erlenmeyer) Flask</td><td>Mixing and heating; used in titrations</td></tr>\n      <tr><td>Test tube</td><td>Carrying out small-scale reactions</td></tr>\n      <tr><td>Burette</td><td>Delivering accurate volumes of liquid in titrations</td></tr>\n      <tr><td>Pipette</td><td>Transferring a fixed accurate volume of liquid</td></tr>\n      <tr><td>Measuring cylinder</td><td>Measuring approximate volumes of liquids</td></tr>\n      <tr><td>Volumetric flask</td><td>Preparing standard solutions of exact volume</td></tr>\n      <tr><td>Bunsen burner</td><td>Heating substances in the laboratory</td></tr>\n      <tr><td>Condenser</td><td>Cooling vapour back to liquid during distillation</td></tr>\n      <tr><td>Tripod stand and wire gauze</td><td>Supporting glassware above a flame</td></tr>\n      <tr><td>Evaporating dish</td><td>Evaporating solvent to obtain a solid solute</td></tr>\n      <tr><td>Crucible</td><td>Strongly heating substances at high temperatures</td></tr>\n      <tr><td>Watch glass</td><td>Covering beakers; small-scale evaporation</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Safety Rules in the Laboratory</h3>\n<ul class=\"learn-list\">\n  <li>Always wear protective clothing: lab coat, goggles, and gloves.</li>\n  <li>Never taste or smell chemicals directly.</li>\n  <li>Label all reagent bottles correctly.</li>\n  <li>Never point a heated test tube at anyone.</li>\n  <li>Know the location of fire extinguishers and first aid kits.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Chemical Industries in Nigeria</h3>\n<ul class=\"learn-list\">\n  <li><strong>Petroleum refining:</strong> NNPC refineries in Warri, Port Harcourt, and Kaduna.</li>\n  <li><strong>Fertiliser production:</strong> Notore Chemical Industries produces urea and ammonia-based fertilisers.</li>\n  <li><strong>Cement industry:</strong> Dangote Cement and others produce Portland cement.</li>\n  <li><strong>Pharmaceutical industry:</strong> Companies produce drugs such as paracetamol and antimalarials.</li>\n  <li><strong>Textile/Dye industry:</strong> Uses chemical dyes and bleaches to colour fabrics.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Know the difference between burette and pipette. A burette measures variable volumes; a pipette measures a single fixed volume. The volumetric flask is used only for preparing standard solutions.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Laboratory apparatus each have specific purposes. Safety rules are essential. Nigeria's chemical industries include petroleum refining, fertilisers, cement, pharmaceuticals, and textiles.\n</div>\n  ",
    "questions": [
      {
        "q": "Which apparatus is used to deliver accurate variable volumes of liquid during titration?",
        "o": ["Pipette", "Measuring cylinder", "Burette", "Volumetric flask"],
        "a": 2,
        "e": "A burette is used in titrations to deliver accurately measured, variable volumes of a solution. It has a stopcock at the bottom and a graduated scale.",
        "h": "It has a tap (stopcock) and is clamped vertically during titration.",
        "yr": "SS1"
      },
      {
        "q": "A pipette is used in the laboratory to:",
        "o": ["Measure approximate volumes of any liquid", "Transfer a fixed, accurate volume of liquid", "Heat liquids to boiling point", "Filter solid particles from a liquid"],
        "a": 1,
        "e": "A pipette is designed to transfer a specific, fixed volume of liquid accurately. Common sizes are 10 cm³ and 25 cm³.",
        "h": "It delivers one fixed volume — not variable.",
        "yr": "SS1"
      },
      {
        "q": "Which apparatus is best used for preparing a standard solution of exact volume?",
        "o": ["Beaker", "Measuring cylinder", "Conical flask", "Volumetric flask"],
        "a": 3,
        "e": "A volumetric flask has a narrow neck with a precise calibration mark. It is designed to contain a specific, exact volume (e.g., 250 cm³) when filled to the mark.",
        "h": "It has one calibration mark on its narrow neck.",
        "yr": "SS1"
      },
      {
        "q": "The Bunsen burner is used in the laboratory primarily for:",
        "o": ["Measuring temperature", "Filtering mixtures", "Heating substances", "Storing chemicals"],
        "a": 2,
        "e": "The Bunsen burner is a gas-powered heating device. The air hole controls the flame type — a roaring blue flame for strong heating; a luminous yellow flame for gentle heating.",
        "h": "It uses gas to produce a flame for heating.",
        "yr": "SS1"
      },
      {
        "q": "Which apparatus is used to support a beaker above a Bunsen flame?",
        "o": ["Retort stand", "Evaporating dish", "Tripod stand and wire gauze", "Condenser"],
        "a": 2,
        "e": "A tripod stand supports the beaker at a height above the Bunsen burner. Wire gauze sits on top and distributes heat evenly to prevent cracking glassware.",
        "h": "Tripod + wire gauze = platform for heating glassware.",
        "yr": "SS1"
      },
      {
        "q": "During distillation, the condenser is used to:",
        "o": ["Heat the liquid to boiling point", "Cool vapour back into liquid", "Separate solids from liquids", "Measure the boiling point"],
        "a": 1,
        "e": "A condenser cools hot vapour by passing it through a jacket of cold water, causing the vapour to condense back into liquid. It is essential in distillation.",
        "h": "Condenser = cooling device that turns vapour to liquid.",
        "yr": "SS1"
      },
      {
        "q": "The evaporating dish is used to:",
        "o": ["Collect gases evolved in a reaction", "Evaporate solvent to obtain a solid solute", "Measure small volumes of liquid", "Hold and heat large volumes of water"],
        "a": 1,
        "e": "An evaporating dish is a shallow, wide dish used to evaporate a solvent (usually water) from a solution, leaving behind the dissolved solid as crystals.",
        "h": "It is wide and shallow — designed for evaporation.",
        "yr": "SS1"
      },
      {
        "q": "Which safety rule should be followed when heating a test tube?",
        "o": ["Point the open end away from people", "Hold it directly with bare hands", "Seal the mouth with a cork while heating", "Always add cold water before heating"],
        "a": 0,
        "e": "When heating a test tube, the open end must be pointed away from people to prevent hot liquid or gas from spraying onto someone if the contents erupt.",
        "h": "Hot contents can spray out — never point it at anyone.",
        "yr": "SS1"
      },
      {
        "q": "A crucible is most suitable for:",
        "o": ["Filtering solutions", "Strongly heating substances at very high temperatures", "Measuring the volume of gases", "Titration procedures"],
        "a": 1,
        "e": "A crucible is a small, heat-resistant vessel (usually porcelain or platinum) used to heat substances at very high temperatures — for combustion analysis or determining water of crystallisation.",
        "h": "Crucibles withstand very high temperatures without breaking.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a chemical industry in Nigeria?",
        "o": ["Delta Steel Company (primary steel)", "Notore Chemical Industries (fertilisers)", "Nigerian Railway Corporation", "National Electric Power Authority"],
        "a": 1,
        "e": "Notore Chemical Industries is a major Nigerian company that produces ammonia-based fertilisers including urea, supporting agriculture across Nigeria and West Africa.",
        "h": "Notore makes fertilisers — a key chemical product.",
        "yr": "SS1"
      },
      {
        "q": "The measuring cylinder is used to:",
        "o": ["Prepare exact standard solutions", "Deliver precise titration volumes", "Measure approximate volumes of liquids", "Store concentrated acids safely"],
        "a": 2,
        "e": "A measuring cylinder is graduated and used to measure approximate volumes of liquids. It is less accurate than a burette or volumetric flask.",
        "h": "It gives approximate, not precise, volume measurements.",
        "yr": "SS1"
      },
      {
        "q": "The conical flask is preferred in titrations because:",
        "o": ["It can withstand very high temperatures", "Its shape makes swirling easy without spilling", "It measures exact volumes accurately", "It can be attached directly to a Bunsen burner"],
        "a": 1,
        "e": "The conical (Erlenmeyer) flask has a wide base tapering to a narrow neck. This makes it easy to swirl contents without spilling — essential during titration to mix reactants.",
        "h": "Wide base + narrow neck = easy swirling.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT a safety precaution in the chemistry laboratory?",
        "o": ["Wearing goggles and gloves", "Tasting chemicals to identify them", "Labelling all reagent bottles", "Knowing the location of fire extinguishers"],
        "a": 1,
        "e": "Tasting chemicals is extremely dangerous and strictly forbidden in any laboratory. Chemicals can be toxic, corrosive, or carcinogenic. Identification is done by chemical tests, not tasting.",
        "h": "Never taste chemicals — it is a dangerous and forbidden act.",
        "yr": "SS1"
      },
      {
        "q": "Petroleum refining in Nigeria takes place at refineries located in:",
        "o": ["Lagos, Abuja, Kano", "Warri, Port Harcourt, Kaduna", "Ibadan, Benin City, Enugu", "Jos, Maiduguri, Sokoto"],
        "a": 1,
        "e": "Nigeria's main NNPC petroleum refineries are located in Warri (Delta State), Port Harcourt (Rivers State), and Kaduna (Kaduna State), processing crude oil into petrol, diesel, kerosene, and LPG.",
        "h": "Warri, Port Harcourt, and Kaduna are Nigeria's refinery cities.",
        "yr": "SS1"
      },
      {
        "q": "A retort stand and clamp is used in the laboratory to:",
        "o": ["Measure the volume of gases produced", "Support apparatus at a fixed height during experiments", "Strongly heat substances to high temperatures", "Separate immiscible liquids"],
        "a": 1,
        "e": "A retort stand is a vertical metal rod fixed to a heavy base. The clamp attaches to hold apparatus such as a burette, condenser, or flask at the required height.",
        "h": "It holds and supports apparatus — a mechanical support.",
        "yr": "SS1"
      },
      {
        "q": "Which apparatus is used for small-scale reactions in the chemistry laboratory?",
        "o": ["Beaker", "Conical flask", "Test tube", "Volumetric flask"],
        "a": 2,
        "e": "Test tubes are small, cylindrical glass tubes used for carrying out small-scale chemical reactions, especially qualitative tests requiring only a few drops of reagents.",
        "h": "Test tubes are small — for small-scale reactions.",
        "yr": "SS1"
      },
      {
        "q": "The cement industry is an example of a chemical industry because it involves:",
        "o": ["Physical separation of rocks", "Chemical reactions to produce Portland cement", "Biological fermentation processes", "Electrical extraction of metals"],
        "a": 1,
        "e": "Cement production involves calcination — heating limestone (CaCO₃) to produce calcium oxide (CaO) and CO₂. This chemical reaction makes the cement industry a chemical industry.",
        "h": "Cement is made by chemical reaction — not just physical crushing.",
        "yr": "SS1"
      },
      {
        "q": "What is the purpose of wire gauze in the laboratory?",
        "o": ["To filter solutions in titration", "To distribute heat evenly and support glassware over a flame", "To cool down hot vapours during distillation", "To measure the temperature of a heated substance"],
        "a": 1,
        "e": "Wire gauze is placed on top of a tripod stand. Its clay centre absorbs and distributes heat evenly, preventing localised intense heat that could crack glass.",
        "h": "It spreads heat evenly under glassware.",
        "yr": "SS1"
      },
      {
        "q": "A pharmaceutical industry is classified as a chemical industry because it:",
        "o": ["Manufactures machinery and equipment", "Uses chemical processes to produce drugs and medicines", "Provides medical services in hospitals", "Transports chemicals between cities"],
        "a": 1,
        "e": "A pharmaceutical industry uses chemical synthesis, extraction, and fermentation to produce drugs such as antibiotics, analgesics, and antimalarials.",
        "h": "Drugs are chemical compounds — made by chemical processes.",
        "yr": "SS1"
      },
      {
        "q": "Which apparatus would you choose to cover a beaker to prevent dust contamination?",
        "o": ["Crucible", "Watch glass", "Evaporating dish", "Funnel"],
        "a": 1,
        "e": "A watch glass is a small, circular curved glass disc used to cover beakers and containers to prevent contamination while still allowing observation.",
        "h": "Watch glass = a cover for beakers and flasks.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Nature of Matter",
    "topicCode": "SS1-CHEM-03",
    "module": "Foundations of Chemistry",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Matter is <span class=\"learn-keyword\">anything that has mass and occupies space</span>. Matter exists in three common physical states — <span class=\"learn-keyword\">solid, liquid, and gas</span> — and can change from one state to another through heating or cooling.\n</div>\n\n<h3 class=\"learn-subheading\">1. States of Matter</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Property</th><th>Solid</th><th>Liquid</th><th>Gas</th></tr></thead>\n    <tbody>\n      <tr><td>Shape</td><td>Fixed</td><td>Takes container shape</td><td>Takes container shape</td></tr>\n      <tr><td>Volume</td><td>Fixed</td><td>Fixed</td><td>Expands to fill container</td></tr>\n      <tr><td>Compressibility</td><td>Not compressible</td><td>Slightly compressible</td><td>Highly compressible</td></tr>\n      <tr><td>Particle arrangement</td><td>Closely packed, regular</td><td>Close, less regular</td><td>Far apart, random</td></tr>\n      <tr><td>Particle movement</td><td>Vibrate in fixed positions</td><td>Slide past each other</td><td>Move freely at high speed</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Changes of State</h3>\n<ul class=\"learn-list\">\n  <li><strong>Melting:</strong> Solid → Liquid (absorbs heat). Temperature = melting point.</li>\n  <li><strong>Freezing:</strong> Liquid → Solid (releases heat). Temperature = freezing point.</li>\n  <li><strong>Vaporisation/Boiling:</strong> Liquid → Gas (absorbs heat). Temperature = boiling point.</li>\n  <li><strong>Condensation:</strong> Gas → Liquid (releases heat).</li>\n  <li><strong>Sublimation:</strong> Solid → Gas directly (e.g., iodine, dry ice, naphthalene).</li>\n  <li><strong>Deposition:</strong> Gas → Solid directly.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Elements, Compounds, and Mixtures</h3>\n<ul class=\"learn-list\">\n  <li><strong>Element:</strong> A pure substance made of only one type of atom (e.g., Fe, O₂, Au).</li>\n  <li><strong>Compound:</strong> A pure substance formed when two or more elements are chemically combined in a fixed ratio (e.g., H₂O, NaCl, CO₂).</li>\n  <li><strong>Mixture:</strong> Two or more substances physically combined (e.g., air, saltwater). Can be homogeneous (uniform) or heterogeneous (non-uniform).</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Changes of state that absorb heat are endothermic (melting, vaporisation, sublimation). Those that release heat are exothermic (freezing, condensation, deposition). Sublimation examples: iodine, dry ice (solid CO₂), naphthalene (mothballs).\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Matter exists as solid, liquid, or gas with distinct properties. Elements contain one atom type; compounds are chemically combined; mixtures are physically combined. Homogeneous mixtures are uniform; heterogeneous are non-uniform.\n</div>\n  ",
    "questions": [
      {
        "q": "Which state of matter has both a fixed shape and a fixed volume?",
        "o": ["Gas", "Liquid", "Solid", "Plasma"],
        "a": 2,
        "e": "Solids have a fixed shape and a fixed volume because their particles are closely packed in regular arrangements and can only vibrate in fixed positions.",
        "h": "Solids keep their shape and size.",
        "yr": "SS1"
      },
      {
        "q": "The change of state from solid directly to gas without passing through the liquid state is called:",
        "o": ["Condensation", "Sublimation", "Vaporisation", "Deposition"],
        "a": 1,
        "e": "Sublimation is the direct change of state from solid to gas. Examples include iodine, dry ice (solid CO₂), and naphthalene (mothballs).",
        "h": "Sublimation skips the liquid stage completely.",
        "yr": "SS1"
      },
      {
        "q": "When water freezes to form ice, the process is called:",
        "o": ["Melting", "Condensation", "Freezing", "Sublimation"],
        "a": 2,
        "e": "Freezing is the change of state from liquid to solid. Water loses heat below 0°C to freeze. Freezing releases heat (exothermic process).",
        "h": "Water → ice is freezing; it happens at 0°C.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following substances undergoes sublimation?",
        "o": ["Water", "Ethanol", "Iodine", "Mercury"],
        "a": 2,
        "e": "Iodine is a classic subliming substance. When heated gently, solid iodine converts directly to purple iodine vapour without passing through a liquid phase.",
        "h": "Iodine turns directly from solid to purple vapour.",
        "yr": "SS1"
      },
      {
        "q": "A pure substance is different from a mixture because:",
        "o": ["It can be separated by physical methods", "It has a fixed composition and definite physical properties", "It always contains only one element", "It is always in the solid state"],
        "a": 1,
        "e": "A pure substance has a fixed, definite composition and specific reproducible physical properties (melting point, boiling point). A mixture has variable composition.",
        "h": "Pure substances have fixed, reproducible properties.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a compound?",
        "o": ["Iron (Fe)", "Oxygen (O₂)", "Water (H₂O)", "Air"],
        "a": 2,
        "e": "Water (H₂O) is a compound — it consists of two elements (hydrogen and oxygen) chemically combined in a fixed ratio of 2:1. Its properties differ from both hydrogen and oxygen.",
        "h": "Compounds are formed by chemical combination of elements.",
        "yr": "SS1"
      },
      {
        "q": "Gases are highly compressible because:",
        "o": ["Their particles move very slowly", "Their particles are very heavy", "Their particles are far apart with large empty spaces between them", "Their particles are arranged in a regular pattern"],
        "a": 2,
        "e": "Gas particles are far apart with large empty spaces between them. Applying pressure pushes the particles closer, making gases highly compressible.",
        "h": "Large spaces between gas particles make compression possible.",
        "yr": "SS1"
      },
      {
        "q": "The process by which liquid water turns into water vapour is called:",
        "o": ["Condensation", "Freezing", "Melting", "Vaporisation"],
        "a": 3,
        "e": "Vaporisation is the change from liquid to gas. When it occurs throughout the liquid at boiling point, it is called boiling. When below boiling point at the surface, it is evaporation.",
        "h": "Liquid → gas = vaporisation.",
        "yr": "SS1"
      },
      {
        "q": "Air is classified as a mixture because:",
        "o": ["It contains only nitrogen and oxygen", "Its components are chemically combined in a fixed ratio", "Its composition varies and it can be separated physically", "It exists only in the gaseous state"],
        "a": 2,
        "e": "Air is a mixture of nitrogen (~78%), oxygen (~21%), argon, CO₂, and other gases. Its composition can vary slightly and its components can be separated by fractional distillation.",
        "h": "Mixtures have variable composition and are separable physically.",
        "yr": "SS1"
      },
      {
        "q": "Which describes the particle arrangement in a liquid?",
        "o": ["Closely packed in a rigid, regular arrangement", "Close together but able to slide past each other", "Far apart and moving randomly at high speed", "Fixed layers that vibrate in place"],
        "a": 1,
        "e": "In a liquid, particles are close together but not in a fixed arrangement. They can slide and flow past each other, giving liquids their fluid properties.",
        "h": "Liquid particles are close but can move past each other.",
        "yr": "SS1"
      },
      {
        "q": "An element is defined as:",
        "o": ["A pure substance made of only one type of atom", "A mixture of two or more substances", "A compound formed by chemical combination", "A substance that cannot be heated"],
        "a": 0,
        "e": "An element is a pure substance consisting of only one type of atom. It cannot be broken down into simpler substances by ordinary chemical means. Examples: Fe, O₂, Au.",
        "h": "Elements contain one type of atom only.",
        "yr": "SS1"
      },
      {
        "q": "When steam condenses to form water droplets, the process releases heat. This makes condensation:",
        "o": ["An endothermic process", "A physical change that absorbs heat", "An exothermic process", "A chemical change"],
        "a": 2,
        "e": "Condensation is exothermic — heat is released as gas particles slow down and come together to form a liquid.",
        "h": "Exothermic = heat released. Condensation releases heat.",
        "yr": "SS1"
      },
      {
        "q": "A homogeneous mixture has:",
        "o": ["Visibly different components throughout", "A uniform composition throughout", "Components that can be easily seen", "Only one pure substance dissolved in it"],
        "a": 1,
        "e": "A homogeneous mixture has a uniform, consistent composition throughout — you cannot distinguish the components visually. Examples: saltwater, sugar solution, alloys.",
        "h": "Homogeneous = uniform appearance throughout.",
        "yr": "SS1"
      },
      {
        "q": "At what temperature does pure water boil at standard pressure?",
        "o": ["0°C", "50°C", "100°C", "212°C"],
        "a": 2,
        "e": "Pure water boils at exactly 100°C at standard atmospheric pressure (1 atm). This fixed physical property distinguishes pure water from impure water or solutions.",
        "h": "100°C is the boiling point of pure water at sea level.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following represents a physical change?",
        "o": ["Burning of wood", "Rusting of iron", "Melting of ice", "Digestion of food"],
        "a": 2,
        "e": "Melting of ice is a physical change — only the state of matter changes from solid to liquid; no new substance is formed. Burning and rusting are chemical changes.",
        "h": "Physical changes do not produce new substances.",
        "yr": "SS1"
      },
      {
        "q": "The melting point of a substance is:",
        "o": ["The temperature at which it boils", "The temperature at which it changes from solid to liquid", "The temperature at which it dissolves in water", "The temperature at which it decomposes"],
        "a": 1,
        "e": "The melting point is the specific temperature at which a solid changes to a liquid. It is a characteristic physical property used to identify pure substances. For ice, it is 0°C.",
        "h": "Melting point = temperature of solid → liquid transition.",
        "yr": "SS1"
      },
      {
        "q": "Salt dissolved in water forms a solution best classified as:",
        "o": ["A compound", "A heterogeneous mixture", "A homogeneous mixture", "A pure substance"],
        "a": 2,
        "e": "Salt dissolved in water forms a homogeneous mixture (solution) — uniformly mixed throughout. It is not a compound because no chemical reaction occurred.",
        "h": "Solutions are homogeneous mixtures.",
        "yr": "SS1"
      },
      {
        "q": "Gas particles move more quickly than liquid particles because:",
        "o": ["Gas particles are heavier", "Gas particles have more kinetic energy", "Gas particles are more densely packed", "Gas particles attract each other strongly"],
        "a": 1,
        "e": "Gas particles have higher kinetic energy than liquid or solid particles. Kinetic energy increases with temperature, and gases exist at temperatures above the liquid's boiling point.",
        "h": "Higher temperature = higher kinetic energy = faster movement.",
        "yr": "SS1"
      },
      {
        "q": "Which statement about a compound is correct?",
        "o": ["It can be separated into its elements by physical methods", "Its properties are the same as the elements it contains", "It has a fixed composition and is formed by chemical combination", "It is always a liquid at room temperature"],
        "a": 2,
        "e": "A compound has a definite, fixed composition — elements are chemically combined in fixed ratios. Its properties are entirely different from its component elements.",
        "h": "Compounds = fixed composition, chemical combination.",
        "yr": "SS1"
      },
      {
        "q": "Which change of state involves the absorption of heat?",
        "o": ["Condensation", "Freezing", "Deposition", "Melting"],
        "a": 3,
        "e": "Melting absorbs heat (endothermic) — the absorbed heat breaks bonds holding solid particles in fixed positions. Vaporisation and sublimation also absorb heat. Condensation, freezing, and deposition release heat.",
        "h": "Melting requires heat input to break solid bonds.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Separation Techniques",
    "topicCode": "SS1-CHEM-04",
    "module": "Foundations of Chemistry",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Separation techniques</span> exploit differences in the physical properties of mixture components — such as particle size, boiling point, solubility, or density — to separate them without causing any chemical change.\n</div>\n\n<h3 class=\"learn-subheading\">1. Filtration</h3>\n<p class=\"learn-p\">Filtration separates an insoluble solid from a liquid. The mixture is poured through filter paper in a funnel. The liquid (filtrate) passes through; the solid (residue) remains.</p>\n<p class=\"learn-p\"><strong>Example:</strong> Separating sand from water; chalk from water.</p>\n\n<h3 class=\"learn-subheading\">2. Distillation</h3>\n<p class=\"learn-p\"><strong>Simple distillation</strong> separates a soluble solid from a liquid, or two liquids with very different boiling points. The liquid is boiled, vapour condenses in the condenser, and pure liquid (distillate) is collected.</p>\n<p class=\"learn-p\"><strong>Fractional distillation</strong> separates liquids with similar boiling points using a fractionating column. Examples: separating ethanol from water; refining crude oil.</p>\n\n<h3 class=\"learn-subheading\">3. Crystallisation and Evaporation</h3>\n<p class=\"learn-p\"><strong>Evaporation to dryness:</strong> Heats a solution until all solvent evaporates, leaving the solid. Suitable for thermally stable salts (e.g., NaCl).</p>\n<p class=\"learn-p\"><strong>Crystallisation:</strong> Solution heated then cooled slowly, allowing dissolved substance to form crystals. Example: obtaining copper sulphate crystals.</p>\n\n<h3 class=\"learn-subheading\">4. Sublimation</h3>\n<p class=\"learn-p\">Separates a subliming solid from a non-subliming solid. The subliming substance vaporises on heating and re-solidifies on a cold surface. Example: separating iodine from sand; ammonium chloride from salt.</p>\n\n<h3 class=\"learn-subheading\">5. Chromatography</h3>\n<p class=\"learn-p\">Separates mixtures based on different rates of movement through a stationary phase carried by a mobile phase (solvent). Types: paper chromatography, thin-layer chromatography (TLC).</p>\n<p class=\"learn-p\"><strong>Rf value:</strong> Rf = distance moved by substance ÷ distance moved by solvent front.</p>\n\n<h3 class=\"learn-subheading\">6. Other Techniques</h3>\n<ul class=\"learn-list\">\n  <li><strong>Decantation:</strong> Carefully pouring off liquid from a settled solid.</li>\n  <li><strong>Centrifugation:</strong> High-speed spinning to separate fine particles from liquid.</li>\n  <li><strong>Magnetic separation:</strong> Using a magnet (e.g., iron filings from sand).</li>\n  <li><strong>Solvent extraction:</strong> Using a separating funnel for immiscible liquids.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Match the technique to the property: filtration = particle size; distillation = boiling point; crystallisation = solubility; chromatography = adsorption; sublimation = direct vaporisation. Know Rf = substance distance ÷ solvent front distance.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Separation techniques exploit physical property differences. Filtration (particle size), distillation (boiling point), crystallisation (solubility), sublimation (direct vaporisation), chromatography (differential movement), magnetic separation (magnetism), solvent extraction (immiscibility).\n</div>\n  ",
    "questions": [
      {
        "q": "Filtration is used to separate:",
        "o": ["Two liquids with different boiling points", "An insoluble solid from a liquid", "A dissolved solid from a solution", "Two solids with different magnetic properties"],
        "a": 1,
        "e": "Filtration separates an insoluble solid from a liquid. The solid (residue) is trapped on filter paper while the liquid (filtrate) passes through.",
        "h": "Filtration works for insoluble solids in liquids.",
        "yr": "SS1"
      },
      {
        "q": "The liquid that passes through the filter paper during filtration is called the:",
        "o": ["Residue", "Precipitate", "Filtrate", "Solute"],
        "a": 2,
        "e": "The filtrate is the liquid that passes through the filter paper. The solid that remains on the filter paper is called the residue.",
        "h": "Filtrate = the liquid that passes through the filter.",
        "yr": "SS1"
      },
      {
        "q": "Which technique is best for separating ethanol (boiling point 78°C) from water (boiling point 100°C)?",
        "o": ["Filtration", "Crystallisation", "Fractional distillation", "Sublimation"],
        "a": 2,
        "e": "Fractional distillation separates liquids with different but close boiling points. The fractionating column allows ethanol (78°C) to distil off first.",
        "h": "Two liquids with different boiling points → fractional distillation.",
        "yr": "SS1"
      },
      {
        "q": "The Rf value in chromatography is calculated as:",
        "o": ["Distance moved by solvent ÷ distance moved by substance", "Distance moved by substance ÷ distance moved by solvent front", "Distance moved by substance × distance moved by solvent front", "Distance moved by solvent − distance moved by substance"],
        "a": 1,
        "e": "Rf = distance moved by substance ÷ distance moved by solvent front. It is always between 0 and 1.",
        "h": "Rf = substance distance ÷ solvent front distance.",
        "yr": "SS1"
      },
      {
        "q": "Which separation technique obtains pure copper sulphate crystals from a saturated solution?",
        "o": ["Distillation", "Filtration", "Crystallisation", "Sublimation"],
        "a": 2,
        "e": "Crystallisation involves heating the solution to concentrate it, then cooling slowly. As it cools, copper sulphate becomes less soluble and crystallises out.",
        "h": "Crystallisation grows solid crystals from a cooling concentrated solution.",
        "yr": "SS1"
      },
      {
        "q": "Crude oil is refined into petrol, diesel, and kerosene using:",
        "o": ["Simple distillation", "Fractional distillation", "Chromatography", "Crystallisation"],
        "a": 1,
        "e": "Crude oil is a mixture of hydrocarbons with different boiling points. Fractional distillation at an industrial scale separates them — different fractions condense at different levels.",
        "h": "Crude oil refining uses fractional distillation.",
        "yr": "SS1"
      },
      {
        "q": "Iodine can be separated from sand by sublimation because:",
        "o": ["Iodine dissolves in sand but sand does not dissolve", "Iodine converts directly from solid to vapour when heated, while sand does not", "Iodine has a higher density than sand", "Iodine is magnetic but sand is not"],
        "a": 1,
        "e": "Iodine sublimes — changes directly from solid to vapour when gently heated. Sand does not sublime. The iodine vapour re-solidifies on a cold surface, leaving sand behind.",
        "h": "Iodine sublimes; sand does not.",
        "yr": "SS1"
      },
      {
        "q": "Which technique is used to separate iron filings from sand?",
        "o": ["Filtration", "Sublimation", "Magnetic separation", "Chromatography"],
        "a": 2,
        "e": "Magnetic separation is used because iron is magnetic and sand is not. A magnet is passed over the mixture and the iron filings are attracted to it, separating from the sand.",
        "h": "Iron is magnetic — use a magnet to pull it out.",
        "yr": "SS1"
      },
      {
        "q": "In simple distillation, the condenser is used to:",
        "o": ["Heat the liquid to its boiling point", "Cool vapour back to liquid", "Separate solid from liquid", "Measure the boiling point accurately"],
        "a": 1,
        "e": "In distillation, hot vapour travels through the condenser where it is cooled by cold water flowing in a jacket, causing condensation back into liquid (distillate).",
        "h": "Condenser cools vapour into liquid during distillation.",
        "yr": "SS1"
      },
      {
        "q": "A separating funnel is used to separate:",
        "o": ["A dissolved solid from a solvent by heating", "Two immiscible liquids that form distinct layers", "Two solids with different melting points", "Components by chromatography"],
        "a": 1,
        "e": "A separating funnel separates two immiscible (non-mixing) liquids such as oil and water. The two layers form and the denser liquid is drained from the bottom tap first.",
        "h": "Separating funnel = two layers of immiscible liquids.",
        "yr": "SS1"
      },
      {
        "q": "Paper chromatography separates mixtures based on:",
        "o": ["Differences in particle size", "Differences in boiling point", "Differences in the rate at which components move through the stationary phase", "Differences in magnetic properties"],
        "a": 2,
        "e": "Chromatography separates components based on their different affinities for the stationary phase (paper) and mobile phase (solvent). Components with more affinity for the stationary phase move slower.",
        "h": "Different movement rates through the paper separate components.",
        "yr": "SS1"
      },
      {
        "q": "In crystallisation, the solution is heated first to:",
        "o": ["Destroy impurities chemically", "Evaporate excess solvent and concentrate the solution", "Raise the Rf value of the solute", "Increase the boiling point of the solvent"],
        "a": 1,
        "e": "Heating during crystallisation evaporates excess solvent, concentrating the solution. When cooled slowly, the supersaturated solution deposits the solute as crystals while impurities remain dissolved.",
        "h": "Concentrate first by evaporation, then cool to form crystals.",
        "yr": "SS1"
      },
      {
        "q": "Which technique separates a soluble solid from a liquid by boiling — collecting the pure liquid?",
        "o": ["Filtration", "Simple distillation", "Crystallisation", "Sublimation"],
        "a": 1,
        "e": "Simple distillation boils the solution, collects and condenses the vapour to give pure distilled water (distillate) — the solid remains in the flask.",
        "h": "Simple distillation collects the pure liquid (distillate), not the solid.",
        "yr": "SS1"
      },
      {
        "q": "If a substance moves 4.5 cm and the solvent front moves 9 cm in chromatography, its Rf value is:",
        "o": ["2.0", "0.2", "0.5", "4.5"],
        "a": 2,
        "e": "Rf = distance moved by substance ÷ distance moved by solvent front = 4.5 ÷ 9.0 = 0.5. Rf values are always between 0 and 1.",
        "h": "Rf = 4.5 ÷ 9.0 = 0.5.",
        "yr": "SS1"
      },
      {
        "q": "Decantation is the process of:",
        "o": ["Boiling a solution until solvent evaporates", "Carefully pouring liquid away from a settled solid", "Spinning a mixture at high speed", "Using a magnet to remove magnetic particles"],
        "a": 1,
        "e": "Decantation involves carefully tilting and pouring the liquid away after the denser solid has settled to the bottom. It is simpler but less thorough than filtration.",
        "h": "Carefully pour off the liquid after the solid settles.",
        "yr": "SS1"
      },
      {
        "q": "Centrifugation is used when:",
        "o": ["The solids are too large to settle on their own", "Small, fine particles need to be separated from liquids quickly", "Two liquids of different densities need separation", "Subliming substances need removal"],
        "a": 1,
        "e": "Centrifugation spins mixtures at high speed, creating centrifugal force greater than gravity. This forces very small particles (like blood cells) to settle quickly — useful in medicine.",
        "h": "Centrifugation separates fine particles that don't settle easily by gravity.",
        "yr": "SS1"
      },
      {
        "q": "The mobile phase in paper chromatography is:",
        "o": ["The paper itself", "The solvent that moves up the paper", "The coloured spot placed on the paper", "The pencil baseline"],
        "a": 1,
        "e": "The mobile phase is the solvent that moves through the stationary phase (paper). It carries the dissolved components with it. The paper is the stationary phase.",
        "h": "Mobile phase moves — it is the solvent.",
        "yr": "SS1"
      },
      {
        "q": "Which technique is most appropriate for separating salt from water to get dry salt crystals quickly?",
        "o": ["Fractional distillation", "Chromatography", "Evaporation to dryness", "Sublimation"],
        "a": 2,
        "e": "Evaporation to dryness heats the salt solution in an evaporating dish until all water evaporates, leaving dry salt crystals. It is quick but only suitable for thermally stable salts.",
        "h": "Evaporation to dryness removes all the water by heating.",
        "yr": "SS1"
      },
      {
        "q": "Ammonium chloride can be separated from sodium chloride by sublimation because:",
        "o": ["Ammonium chloride is soluble but sodium chloride is not", "Ammonium chloride sublimes when heated, while sodium chloride does not", "Sodium chloride has a higher Rf value", "The two salts have different magnetic properties"],
        "a": 1,
        "e": "Ammonium chloride sublimes when heated, converting directly from solid to vapour and re-solidifying on a cooler surface. Sodium chloride does not sublime.",
        "h": "NH₄Cl sublimes; NaCl does not.",
        "yr": "SS1"
      },
      {
        "q": "In fractional distillation of crude oil, which fraction is collected first?",
        "o": ["Bitumen (high boiling point)", "Diesel", "Kerosene", "Petroleum gases (low boiling point)"],
        "a": 3,
        "e": "Temperature decreases from bottom to top of the fractionating column. Fractions with the lowest boiling points (petroleum gases — methane, ethane, propane) are collected as gases at the top first.",
        "h": "Low boiling point = rises to the top of the column first.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Atomic Structure",
    "topicCode": "SS1-CHEM-05",
    "module": "Atomic Theory",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The <span class=\"learn-keyword\">atom</span> is the smallest particle of an element that retains the chemical properties of that element. Modern atomic theory describes a dense nucleus surrounded by electrons in shells.\n</div>\n\n<h3 class=\"learn-subheading\">1. Subatomic Particles</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Particle</th><th>Symbol</th><th>Charge</th><th>Relative Mass</th><th>Location</th></tr></thead>\n    <tbody>\n      <tr><td>Proton</td><td>p⁺</td><td>+1</td><td>1</td><td>Nucleus</td></tr>\n      <tr><td>Neutron</td><td>n⁰</td><td>0</td><td>1</td><td>Nucleus</td></tr>\n      <tr><td>Electron</td><td>e⁻</td><td>−1</td><td>~1/1836 (negligible)</td><td>Electron shells</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Atomic Number and Mass Number</h3>\n<ul class=\"learn-list\">\n  <li><strong>Atomic number (Z):</strong> Number of protons. Defines the element. In a neutral atom, Z = number of electrons.</li>\n  <li><strong>Mass number (A):</strong> Protons + neutrons. A = Z + N.</li>\n  <li><strong>Number of neutrons:</strong> N = A − Z.</li>\n</ul>\n<p class=\"learn-p\">Example: Carbon-12: Z = 6, A = 12, neutrons = 12 − 6 = 6.</p>\n\n<h3 class=\"learn-subheading\">3. Isotopes</h3>\n<p class=\"learn-p\">Isotopes are atoms of the same element with the same atomic number but different mass numbers (different neutron numbers).</p>\n<p class=\"learn-p\">Examples: ¹²C and ¹⁴C; ³⁵Cl and ³⁷Cl. Isotopes have identical chemical properties but different physical properties.</p>\n\n<h3 class=\"learn-subheading\">4. Electronic Configuration</h3>\n<p class=\"learn-p\">Electrons fill shells around the nucleus: Shell 1 = max 2; Shell 2 = max 8; Shell 3 = max 8 (for first 20 elements); Shell 4 = 2.</p>\n<p class=\"learn-p\">Example — Sodium (Z=11): 2, 8, 1. Sodium has 1 valence electron.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Atomic number = protons = electrons (neutral atom). Mass number = protons + neutrons. Neutrons = A − Z. Isotopes: same Z, different A. Valence electrons determine chemical reactivity.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Atoms have protons (+1) and neutrons (0) in the nucleus, electrons (−1) in shells. Atomic number = protons; mass number = protons + neutrons. Isotopes share the same Z but differ in A. Electrons fill shells: 2, 8, 8, 2 for the first 20 elements.\n</div>\n  ",
    "questions": [
      {
        "q": "The atomic number of an element equals the number of:",
        "o": ["Neutrons in the nucleus", "Protons in the nucleus", "Electrons and neutrons combined", "Protons and neutrons combined"],
        "a": 1,
        "e": "The atomic number (Z) is the number of protons in the nucleus. It uniquely identifies an element. In a neutral atom, the number of protons equals the number of electrons.",
        "h": "Atomic number = number of protons.",
        "yr": "SS1"
      },
      {
        "q": "Which subatomic particle has negligible mass and a negative charge?",
        "o": ["Proton", "Neutron", "Electron", "Nucleus"],
        "a": 2,
        "e": "Electrons have a relative mass of approximately 1/1836 (negligible) and carry a charge of −1. They orbit the nucleus in shells.",
        "h": "Electrons: negative charge, negligible mass.",
        "yr": "SS1"
      },
      {
        "q": "For Chlorine-35 (atomic number 17), the number of neutrons is:",
        "o": ["17", "18", "35", "52"],
        "a": 1,
        "e": "Number of neutrons = mass number − atomic number = 35 − 17 = 18.",
        "h": "Neutrons = 35 − 17 = 18.",
        "yr": "SS1"
      },
      {
        "q": "Isotopes of the same element have:",
        "o": ["Different atomic numbers but the same mass number", "The same atomic number but different mass numbers", "Different numbers of protons but the same electrons", "The same number of neutrons but different protons"],
        "a": 1,
        "e": "Isotopes are atoms of the same element (same atomic number) but with different mass numbers — they have different numbers of neutrons. Example: ¹²C and ¹⁴C both have Z = 6.",
        "h": "Isotopes: same Z (protons), different A (neutrons differ).",
        "yr": "SS1"
      },
      {
        "q": "The mass number of an atom is defined as:",
        "o": ["Number of protons only", "Number of neutrons only", "Number of protons plus number of neutrons", "Number of electrons plus number of protons"],
        "a": 2,
        "e": "The mass number (A) = protons + neutrons. Electrons are not included because their mass is negligible.",
        "h": "Mass number = protons + neutrons.",
        "yr": "SS1"
      },
      {
        "q": "The maximum number of electrons in the second electron shell is:",
        "o": ["2", "4", "8", "18"],
        "a": 2,
        "e": "The second electron shell (L shell) can hold a maximum of 8 electrons, given by 2n² where n=2: 2×4 = 8.",
        "h": "Shell 2 maximum = 8 electrons.",
        "yr": "SS1"
      },
      {
        "q": "What is the electronic configuration of sodium (atomic number 11)?",
        "o": ["2, 9", "2, 8, 1", "3, 8", "2, 7, 2"],
        "a": 1,
        "e": "Sodium has 11 electrons filling as: Shell 1 = 2, Shell 2 = 8, Shell 3 = 1. Configuration: 2, 8, 1. Sodium has 1 valence electron.",
        "h": "11 electrons: 2 + 8 + 1 = 11.",
        "yr": "SS1"
      },
      {
        "q": "Where are protons and neutrons found in an atom?",
        "o": ["In the electron shells", "In the nucleus", "In the outermost energy level", "Evenly distributed throughout the atom"],
        "a": 1,
        "e": "Protons and neutrons are both located in the nucleus — the tiny, dense, positively charged centre of the atom.",
        "h": "Protons and neutrons are in the nucleus.",
        "yr": "SS1"
      },
      {
        "q": "The two isotopes Cl-35 and Cl-37 have in common:",
        "o": ["Same number of neutrons", "Same mass number", "Same number of protons (atomic number 17)", "Same physical properties"],
        "a": 2,
        "e": "Both chlorine isotopes have atomic number Z = 17 (17 protons and 17 electrons). They differ in neutron number: 18 for Cl-35 and 20 for Cl-37.",
        "h": "Isotopes share the same atomic number (same protons).",
        "yr": "SS1"
      },
      {
        "q": "A neutral atom of oxygen has atomic number 8 and mass number 16. How many electrons does it have?",
        "o": ["8", "16", "6", "10"],
        "a": 0,
        "e": "In a neutral atom, electrons = protons = atomic number. Oxygen has atomic number 8, so it has 8 electrons.",
        "h": "Neutral atom: electrons = protons = atomic number.",
        "yr": "SS1"
      },
      {
        "q": "The electron configuration of magnesium (atomic number 12) is:",
        "o": ["2, 10", "2, 8, 2", "4, 8", "2, 6, 4"],
        "a": 1,
        "e": "Magnesium has 12 electrons: Shell 1 = 2, Shell 2 = 8, Shell 3 = 2. Configuration: 2, 8, 2. Magnesium has 2 valence electrons.",
        "h": "12 electrons: 2 + 8 + 2 = 12.",
        "yr": "SS1"
      },
      {
        "q": "Why do isotopes of the same element have identical chemical properties?",
        "o": ["They have the same number of neutrons", "They have the same number of protons and electrons", "They have the same mass number", "They were discovered at the same time"],
        "a": 1,
        "e": "Chemical properties depend on the number and arrangement of electrons. Since isotopes have the same atomic number, they have the same number of electrons and identical electronic configuration.",
        "h": "Same electrons = same chemistry.",
        "yr": "SS1"
      },
      {
        "q": "An atom has 15 protons and 16 neutrons. Its mass number is:",
        "o": ["15", "16", "31", "1"],
        "a": 2,
        "e": "Mass number = protons + neutrons = 15 + 16 = 31. This is phosphorus-31 (³¹P).",
        "h": "Mass number = 15 + 16 = 31.",
        "yr": "SS1"
      },
      {
        "q": "The first shell of an atom can hold a maximum of:",
        "o": ["2 electrons", "4 electrons", "8 electrons", "18 electrons"],
        "a": 0,
        "e": "The first electron shell (K shell) holds a maximum of 2 electrons. This follows 2n² where n=1: 2×1 = 2. Helium (Z=2) has a full first shell.",
        "h": "Shell 1 maximum = 2 electrons.",
        "yr": "SS1"
      },
      {
        "q": "The subatomic particle that has no electrical charge is the:",
        "o": ["Proton", "Electron", "Neutron", "Ion"],
        "a": 2,
        "e": "The neutron has no electrical charge (it is neutral). Protons carry +1 and electrons carry −1. Neutrons add mass to the nucleus and provide nuclear stability.",
        "h": "Neutron = neutral particle in the nucleus.",
        "yr": "SS1"
      },
      {
        "q": "Calcium (atomic number 20) has the electron configuration:",
        "o": ["2, 8, 10", "2, 8, 8, 2", "2, 18", "2, 8, 4, 6"],
        "a": 1,
        "e": "Calcium has 20 electrons: Shell 1=2, Shell 2=8, Shell 3=8, Shell 4=2. Configuration: 2, 8, 8, 2. Calcium has 2 valence electrons.",
        "h": "20 electrons: 2 + 8 + 8 + 2 = 20.",
        "yr": "SS1"
      },
      {
        "q": "Carbon-14 has more neutrons than Carbon-12 because:",
        "o": ["It has more protons", "It has more electrons", "Its mass number is higher with the same atomic number", "It has more electron shells"],
        "a": 2,
        "e": "Both have Z=6. C-12 has 6 neutrons (12−6=6); C-14 has 8 neutrons (14−6=8). The higher mass number means more neutrons.",
        "h": "C-14 mass 14 vs C-12 mass 12 — extra neutrons account for the difference.",
        "yr": "SS1"
      },
      {
        "q": "Chemical properties of an atom are mainly determined by:",
        "o": ["The number of neutrons in the nucleus", "The mass number", "The number of valence electrons", "The total mass of the atom"],
        "a": 2,
        "e": "Valence electrons — those in the outermost shell — determine how an atom bonds and reacts. Atoms with the same number of valence electrons have similar chemical properties.",
        "h": "Valence electrons control chemical reactivity.",
        "yr": "SS1"
      },
      {
        "q": "The atomic number of an element is unique because:",
        "o": ["No two elements have the same number of neutrons", "No two elements have the same number of protons", "Each element has a unique mass number", "Each element has a unique number of electron shells"],
        "a": 1,
        "e": "Each element is defined by its number of protons. No two elements share the same atomic number. Changing the proton number creates a different element.",
        "h": "Proton number defines the element — it is unique.",
        "yr": "SS1"
      },
      {
        "q": "An ion is formed when an atom:",
        "o": ["Changes its number of protons", "Gains or loses neutrons", "Gains or loses electrons", "Splits into two smaller atoms"],
        "a": 2,
        "e": "An ion forms when a neutral atom gains electrons (negative anion) or loses electrons (positive cation). The atomic number stays the same — only the electron count changes.",
        "h": "Ions form by gaining or losing electrons.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Periodic Table",
    "topicCode": "SS1-CHEM-06",
    "module": "Atomic Theory",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The <span class=\"learn-keyword\">Periodic Table</span> arranges all known elements in order of increasing <span class=\"learn-keyword\">atomic number</span> into <span class=\"learn-keyword\">periods (rows)</span> and <span class=\"learn-keyword\">groups (columns)</span>. Elements in the same group share similar chemical properties due to having the same number of valence electrons.\n</div>\n\n<h3 class=\"learn-subheading\">1. Structure</h3>\n<ul class=\"learn-list\">\n  <li><strong>Periods:</strong> Horizontal rows (1–7). Period number = number of electron shells.</li>\n  <li><strong>Groups:</strong> Vertical columns (1–18). Group number (main group) = number of valence electrons.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Key Groups</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Group</th><th>Name</th><th>Valence e⁻</th><th>Key Properties</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>I</td><td>Alkali Metals</td><td>1</td><td>Very reactive; react vigorously with water</td><td>Li, Na, K</td></tr>\n      <tr><td>II</td><td>Alkaline Earth Metals</td><td>2</td><td>Reactive metals; less so than Group I</td><td>Mg, Ca, Ba</td></tr>\n      <tr><td>VII</td><td>Halogens</td><td>7</td><td>Very reactive non-metals; form salts</td><td>F, Cl, Br, I</td></tr>\n      <tr><td>0 (VIII)</td><td>Noble Gases</td><td>8 (2 for He)</td><td>Very unreactive; full outer shells</td><td>He, Ne, Ar</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Trends Across a Period (left to right)</h3>\n<ul class=\"learn-list\">\n  <li>Atomic radius <strong>decreases</strong> (more protons pull electrons closer)</li>\n  <li>Ionisation energy <strong>increases</strong></li>\n  <li>Electronegativity <strong>increases</strong></li>\n  <li>Metallic character <strong>decreases</strong></li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Trends Down a Group</h3>\n<ul class=\"learn-list\">\n  <li>Atomic radius <strong>increases</strong> (more electron shells)</li>\n  <li>Ionisation energy <strong>decreases</strong></li>\n  <li>Reactivity of metals <strong>increases</strong> (e.g., K more reactive than Na)</li>\n  <li>Reactivity of non-metals <strong>decreases</strong> (e.g., F more reactive than Cl)</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Group number = valence electrons (main group). Period number = number of electron shells. Noble gases are unreactive because their outer shells are full. Halogens need just 1 more electron — hence very reactive.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Periods = rows (period number = shells). Groups = columns (group number = valence electrons). Group I = alkali metals; Group VII = halogens; Group 0 = noble gases. Atomic radius decreases across a period; increases down a group.\n</div>\n  ",
    "questions": [
      {
        "q": "Elements in the Periodic Table are arranged in order of increasing:",
        "o": ["Mass number", "Atomic radius", "Atomic number", "Number of neutrons"],
        "a": 2,
        "e": "The modern Periodic Table arranges elements in order of increasing atomic number (protons). This replaced Mendeleev's original arrangement by atomic mass.",
        "h": "Modern arrangement: increasing atomic number (protons).",
        "yr": "SS1"
      },
      {
        "q": "The horizontal rows in the Periodic Table are called:",
        "o": ["Groups", "Periods", "Families", "Blocks"],
        "a": 1,
        "e": "The horizontal rows are called periods. There are 7 periods. The period number indicates how many electron shells the atoms of that period have.",
        "h": "Rows = periods (horizontal).",
        "yr": "SS1"
      },
      {
        "q": "Group I alkali metals are very reactive because they have:",
        "o": ["8 valence electrons", "A full outermost shell", "Only 1 valence electron to donate", "A very large atomic radius"],
        "a": 2,
        "e": "Group I alkali metals have 1 valence electron. They readily lose it to form a stable +1 ion with a full outer shell — making them very reactive.",
        "h": "1 valence electron = very willing to react by losing it.",
        "yr": "SS1"
      },
      {
        "q": "Noble gases are unreactive because:",
        "o": ["They have very few electrons", "Their outermost electron shells are completely full", "They are at the bottom of the Periodic Table", "They have the highest atomic masses"],
        "a": 1,
        "e": "Noble gases (Group 0) have full outermost shells — 8 electrons (2 for helium). This stable configuration means they have no tendency to gain or lose electrons.",
        "h": "Full outer shells = no need to react.",
        "yr": "SS1"
      },
      {
        "q": "The period number of an element indicates:",
        "o": ["The number of valence electrons", "The group it belongs to", "The number of electron shells", "The number of neutrons"],
        "a": 2,
        "e": "The period number equals the number of electron shells in atoms of that period. Sodium is in Period 3 and has 3 electron shells (2, 8, 1).",
        "h": "Period number = number of electron shells.",
        "yr": "SS1"
      },
      {
        "q": "Which group contains the halogens?",
        "o": ["Group I", "Group II", "Group VII", "Group 0"],
        "a": 2,
        "e": "Group VII contains the halogens: fluorine, chlorine, bromine, iodine, and astatine. They have 7 valence electrons and need only 1 more for a full outer shell.",
        "h": "Halogens are in Group VII.",
        "yr": "SS1"
      },
      {
        "q": "Across a period from left to right, atomic radius:",
        "o": ["Increases steadily", "Decreases steadily", "Stays the same", "First increases then decreases"],
        "a": 1,
        "e": "Atomic radius decreases across a period. As more protons are added without adding new shells, the increased nuclear charge pulls the electron cloud inward.",
        "h": "More protons across a period pull electrons closer → smaller radius.",
        "yr": "SS1"
      },
      {
        "q": "Which element is in the same group as sodium (Na)?",
        "o": ["Magnesium (Mg)", "Potassium (K)", "Chlorine (Cl)", "Neon (Ne)"],
        "a": 1,
        "e": "Sodium is in Group I (alkali metals) with 1 valence electron. Potassium (K) is also in Group I — same valence electrons, similar chemical properties.",
        "h": "Group I alkali metals: Li, Na, K, Rb, Cs.",
        "yr": "SS1"
      },
      {
        "q": "Going down Group I, reactivity of the elements:",
        "o": ["Decreases", "Stays the same", "Increases", "First increases then decreases"],
        "a": 2,
        "e": "Going down Group I, reactivity increases. The valence electron is further from the nucleus and more shielded, so it is easier to remove — making the metal more reactive. K is more reactive than Na.",
        "h": "Further from nucleus = easier to lose electron = more reactive.",
        "yr": "SS1"
      },
      {
        "q": "Chlorine and bromine are in the same group. This means they have:",
        "o": ["The same atomic number", "The same number of electron shells", "The same number of valence electrons", "The same mass number"],
        "a": 2,
        "e": "Elements in the same group have the same number of valence electrons and similar chemical properties. Both Cl and Br have 7 valence electrons — both are halogens in Group VII.",
        "h": "Same group = same number of valence electrons.",
        "yr": "SS1"
      },
      {
        "q": "Which element has atomic number 2 and belongs to Group 0?",
        "o": ["Hydrogen", "Lithium", "Neon", "Helium"],
        "a": 3,
        "e": "Helium has atomic number 2 and belongs to Group 0 (noble gases). Its 2 electrons fill its first and only shell completely.",
        "h": "Atomic number 2 = Helium, a noble gas.",
        "yr": "SS1"
      },
      {
        "q": "An element in Period 3 has:",
        "o": ["3 valence electrons", "3 electron shells", "3 neutrons", "3 protons"],
        "a": 1,
        "e": "Elements in Period 3 have 3 electron shells. The period number always equals the number of occupied electron shells. Sodium (Period 3) has configuration 2, 8, 1 — three shells.",
        "h": "Period 3 = 3 electron shells.",
        "yr": "SS1"
      },
      {
        "q": "Who developed the first widely accepted Periodic Table?",
        "o": ["John Dalton", "Robert Boyle", "Dmitri Mendeleev", "Henry Moseley"],
        "a": 2,
        "e": "Dmitri Mendeleev developed the Periodic Table in 1869, arranging elements by increasing atomic mass and predicting properties of undiscovered elements.",
        "h": "Mendeleev created the Periodic Table in 1869.",
        "yr": "SS1"
      },
      {
        "q": "Down a group, atomic radius increases because:",
        "o": ["More protons are added to the nucleus", "More electron shells are added with each new period", "The atoms gain more neutrons", "Atomic mass decreases going down"],
        "a": 1,
        "e": "Going down a group, each successive element has one more electron shell. More shells means electrons are farther from the nucleus — the atom is physically larger.",
        "h": "More shells down the group = larger atom.",
        "yr": "SS1"
      },
      {
        "q": "Fluorine is more reactive than iodine as a halogen because:",
        "o": ["Fluorine has more valence electrons", "Fluorine has a smaller atomic radius and attracts an extra electron more strongly", "Fluorine is a gas while iodine is a solid", "Fluorine has a higher atomic mass"],
        "a": 1,
        "e": "Fluorine is at the top of Group VII with the smallest atomic radius. Its nucleus exerts a strong attraction on an incoming electron — making it the most reactive halogen.",
        "h": "Smaller atom = nucleus closer to outer shell = stronger pull for electrons.",
        "yr": "SS1"
      },
      {
        "q": "The d-block (transition metals) in the Periodic Table contains metals that are typically:",
        "o": ["Very soft and highly reactive with water", "Hard with high melting points and form coloured compounds", "All gaseous at room temperature", "Only found as gases in nature"],
        "a": 1,
        "e": "Transition metals (d-block) such as Fe, Cu, Zn, and Ni are typically hard, have high melting points, and form coloured compounds. They are located in the middle of the Periodic Table.",
        "h": "Transition (d-block) metals are hard and form coloured compounds.",
        "yr": "SS1"
      },
      {
        "q": "An element in Group V of the Periodic Table has how many valence electrons?",
        "o": ["3", "5", "7", "2"],
        "a": 1,
        "e": "For main group elements, the group number equals the number of valence electrons. Group V elements have 5 valence electrons. Examples: nitrogen (N) and phosphorus (P).",
        "h": "Group V = 5 valence electrons.",
        "yr": "SS1"
      },
      {
        "q": "Ionisation energy increases across a period because:",
        "o": ["Atoms get larger across a period", "The number of electron shells increases", "Nuclear charge increases while shell number stays the same, holding electrons more tightly", "Electrons are added to inner shells"],
        "a": 2,
        "e": "Across a period, proton number increases (increasing nuclear charge) but electrons are added to the same shell. The stronger nuclear pull makes it harder to remove electrons.",
        "h": "More protons across = stronger hold on electrons = higher ionisation energy.",
        "yr": "SS1"
      },
      {
        "q": "Neon (Ne, atomic number 10) is in Period 2 because it has:",
        "o": ["10 protons, making it the 10th element", "2 electron shells", "8 valence electrons", "The highest atomic mass in Period 2"],
        "a": 1,
        "e": "Neon is in Period 2 because it has 2 electron shells (configuration 2, 8). The period number always reflects the number of occupied electron shells.",
        "h": "Period 2 = 2 electron shells. Neon: 2, 8 = 2 shells.",
        "yr": "SS1"
      },
      {
        "q": "All elements in Group II share the property of having:",
        "o": ["1 valence electron", "All being non-metals", "2 valence electrons", "Being gaseous at room temperature"],
        "a": 2,
        "e": "All Group II elements (Be, Mg, Ca, Sr, Ba, Ra) have 2 valence electrons. They readily lose these 2 electrons to form 2+ ions and behave similarly chemically.",
        "h": "Group II = 2 valence electrons in all members.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Chemical Combination",
    "topicCode": "SS1-CHEM-07",
    "module": "Chemical Laws",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Chemical combination</span> refers to the rules governing how elements combine to form compounds. The <span class=\"learn-keyword\">laws of chemical combination</span> were established through careful experimentation and form the foundation of stoichiometry.\n</div>\n\n<h3 class=\"learn-subheading\">1. Law of Conservation of Mass (Lavoisier, 1789)</h3>\n<p class=\"learn-p\"><em>\"Matter can neither be created nor destroyed in a chemical reaction — the total mass of reactants equals the total mass of products.\"</em></p>\n<p class=\"learn-p\"><strong>Example:</strong> 2H₂ + O₂ → 2H₂O. Mass of H₂ + mass of O₂ = mass of H₂O produced.</p>\n\n<h3 class=\"learn-subheading\">2. Law of Definite Proportions (Proust, 1799)</h3>\n<p class=\"learn-p\"><em>\"A compound always contains the same elements in the same fixed mass ratio regardless of source.\"</em></p>\n<p class=\"learn-p\"><strong>Example:</strong> Water (H₂O) always contains H and O in the mass ratio 1:8.</p>\n\n<h3 class=\"learn-subheading\">3. Law of Multiple Proportions (Dalton, 1803)</h3>\n<p class=\"learn-p\"><em>\"When two elements A and B form more than one compound, the masses of B combining with a fixed mass of A are in simple whole-number ratios.\"</em></p>\n<p class=\"learn-p\"><strong>Example:</strong> CO and CO₂: In CO, 12g C + 16g O. In CO₂, 12g C + 32g O. Ratio of O = 16:32 = 1:2.</p>\n\n<h3 class=\"learn-subheading\">4. Law of Combining Volumes (Gay-Lussac, 1808)</h3>\n<p class=\"learn-p\"><em>\"Gases react in volumes that are in simple whole-number ratios at the same temperature and pressure.\"</em></p>\n<p class=\"learn-p\"><strong>Example:</strong> H₂ + Cl₂ → 2HCl. Volume ratio = 1:1:2.</p>\n\n<h3 class=\"learn-subheading\">5. Avogadro's Law (Avogadro, 1811)</h3>\n<p class=\"learn-p\"><em>\"Equal volumes of all gases at the same temperature and pressure contain the same number of molecules.\"</em></p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Conservation of mass = Lavoisier. Definite proportions = Proust. Multiple proportions = Dalton. Combining volumes = Gay-Lussac. Avogadro's law = Avogadro. Multiple proportions always gives simple whole-number ratios.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Five laws: (1) Conservation of mass — total mass conserved. (2) Definite proportions — fixed mass ratios. (3) Multiple proportions — simple whole-number ratios. (4) Combining volumes — gases react in simple volume ratios. (5) Avogadro — equal volumes contain equal numbers of molecules.\n</div>\n  ",
    "questions": [
      {
        "q": "The Law of Conservation of Mass states that in a chemical reaction:",
        "o": ["Mass is created from energy", "Total mass of reactants equals total mass of products", "Mass is lost as heat during exothermic reactions", "Products always have less mass than reactants"],
        "a": 1,
        "e": "The Law of Conservation of Mass (Lavoisier, 1789) states that mass cannot be created or destroyed in a chemical reaction. Total reactant mass = total product mass.",
        "h": "Mass is neither created nor destroyed in reactions.",
        "yr": "SS1"
      },
      {
        "q": "Who proposed the Law of Conservation of Mass?",
        "o": ["John Dalton", "Joseph Proust", "Antoine Lavoisier", "Gay-Lussac"],
        "a": 2,
        "e": "Antoine Lavoisier (1789) proposed the Law of Conservation of Mass through experiments in sealed containers showing mass of products always equalled mass of reactants.",
        "h": "Lavoisier = Conservation of Mass.",
        "yr": "SS1"
      },
      {
        "q": "The Law of Definite Proportions states that a compound:",
        "o": ["Can vary in composition depending on how it is made", "Always contains the same elements in the same fixed mass ratio", "Is formed only when gases combine", "Changes its ratio with temperature"],
        "a": 1,
        "e": "The Law of Definite Proportions (Proust, 1799) states that a pure compound always contains the same elements in the same definite proportion by mass, regardless of source or preparation.",
        "h": "Same compound = same mass ratio always.",
        "yr": "SS1"
      },
      {
        "q": "Water always contains hydrogen and oxygen in the mass ratio 1:8. This illustrates:",
        "o": ["Law of Multiple Proportions", "Law of Combining Volumes", "Law of Definite Proportions", "Law of Conservation of Mass"],
        "a": 2,
        "e": "This is the Law of Definite Proportions — water always has the same composition by mass (1g H:8g O) regardless of its source.",
        "h": "Fixed mass ratio in a compound = Law of Definite Proportions.",
        "yr": "SS1"
      },
      {
        "q": "In the Law of Multiple Proportions, the masses that combine are in:",
        "o": ["A logarithmic ratio", "Simple whole-number ratios", "Complex decimal ratios", "The same ratio always"],
        "a": 1,
        "e": "The Law of Multiple Proportions (Dalton, 1803) states that when two elements form more than one compound, combining masses are in simple whole-number ratios (e.g., 1:2, 1:3).",
        "h": "Multiple proportions = simple whole-number ratios.",
        "yr": "SS1"
      },
      {
        "q": "Carbon forms CO and CO₂. In CO, 12g of carbon combines with 16g of oxygen. In CO₂, 12g of carbon combines with 32g of oxygen. The ratio of oxygen masses is:",
        "o": ["1:1", "1:2", "2:3", "1:3"],
        "a": 1,
        "e": "Ratio of oxygen masses = 16:32 = 1:2. This simple whole-number ratio illustrates the Law of Multiple Proportions.",
        "h": "16:32 simplifies to 1:2.",
        "yr": "SS1"
      },
      {
        "q": "Gay-Lussac's Law of Combining Volumes applies to:",
        "o": ["Solids reacting with liquids", "Only ionic compounds", "Gases reacting at the same temperature and pressure", "Aqueous solutions only"],
        "a": 2,
        "e": "Gay-Lussac's Law (1808) states that gases react in volumes in simple whole-number ratios at the same temperature and pressure. It only applies to gases.",
        "h": "Combining volumes law applies only to gases.",
        "yr": "SS1"
      },
      {
        "q": "In the reaction H₂ + Cl₂ → 2HCl, the volume ratio H₂:Cl₂:HCl is:",
        "o": ["1:1:1", "1:2:2", "1:1:2", "2:1:2"],
        "a": 2,
        "e": "From the equation, the mole ratio is 1:1:2. By Gay-Lussac's Law, gas volumes at the same T and P are proportional to mole ratios — volume ratio is 1:1:2.",
        "h": "1 vol H₂ + 1 vol Cl₂ → 2 vol HCl.",
        "yr": "SS1"
      },
      {
        "q": "Avogadro's Law states that equal volumes of gases at the same temperature and pressure contain:",
        "o": ["The same number of atoms", "The same mass", "The same number of molecules", "The same density"],
        "a": 2,
        "e": "Avogadro's Law (1811) states that equal volumes of any gas at the same temperature and pressure contain the same number of molecules, regardless of gas type.",
        "h": "Equal volumes (same T, P) = equal number of molecules.",
        "yr": "SS1"
      },
      {
        "q": "Who proposed the Law of Multiple Proportions?",
        "o": ["Gay-Lussac", "Joseph Proust", "Avogadro", "John Dalton"],
        "a": 3,
        "e": "John Dalton proposed the Law of Multiple Proportions in 1803 as part of his atomic theory, observing that combining mass ratios are always simple whole numbers.",
        "h": "Dalton proposed the Law of Multiple Proportions (1803).",
        "yr": "SS1"
      },
      {
        "q": "If 16g of sulfur combines with 16g of oxygen in SO, and with 48g of oxygen in SO₃, the ratio of oxygen masses is:",
        "o": ["1:1", "1:2", "1:3", "1:4"],
        "a": 2,
        "e": "Ratio = 16:48 = 1:3. This illustrates the Law of Multiple Proportions — a simple whole-number ratio.",
        "h": "16:48 = 1:3.",
        "yr": "SS1"
      },
      {
        "q": "When 2g hydrogen reacts with 16g oxygen to form 18g water, which law is demonstrated?",
        "o": ["Law of Definite Proportions", "Law of Multiple Proportions", "Law of Conservation of Mass", "Gay-Lussac's Law"],
        "a": 2,
        "e": "2g H₂ + 16g O₂ → 18g H₂O. Total reactant mass = 18g = total product mass. This demonstrates Conservation of Mass.",
        "h": "Reactant mass = product mass → Conservation of Mass.",
        "yr": "SS1"
      },
      {
        "q": "The Law of Combining Volumes was proposed by:",
        "o": ["Avogadro", "John Dalton", "Gay-Lussac", "Joseph Proust"],
        "a": 2,
        "e": "Gay-Lussac proposed the Law of Combining Volumes in 1808, observing that gases always react in simple whole-number volume ratios at constant T and P.",
        "h": "Gay-Lussac proposed the combining volumes law.",
        "yr": "SS1"
      },
      {
        "q": "100g of copper carbonate always contains the same percentage of copper regardless of where it was made. This supports:",
        "o": ["Law of Multiple Proportions", "Law of Conservation of Mass", "Law of Definite Proportions", "Gay-Lussac's Law"],
        "a": 2,
        "e": "The Law of Definite Proportions states that a pure compound always has a fixed composition by mass. Copper carbonate always contains the same mass percentages of its elements.",
        "h": "Fixed composition regardless of source = Definite Proportions.",
        "yr": "SS1"
      },
      {
        "q": "The molar volume of any gas at STP is approximately:",
        "o": ["11.2 dm³/mol", "22.4 dm³/mol", "44.8 dm³/mol", "0.0224 dm³/mol"],
        "a": 1,
        "e": "At STP (0°C and 1 atm), the molar volume of any ideal gas is 22.4 dm³/mol. This follows from Avogadro's Law — equal volumes contain equal numbers of molecules.",
        "h": "Molar volume at STP = 22.4 dm³/mol.",
        "yr": "SS1"
      },
      {
        "q": "In the reaction N₂(g) + 3H₂(g) → 2NH₃(g), the volume ratio N₂:H₂:NH₃ is:",
        "o": ["1:2:1", "1:3:2", "2:3:1", "1:1:2"],
        "a": 1,
        "e": "From the balanced equation, the mole ratio is 1:3:2. By Gay-Lussac's Law, gas volumes at same T and P equal the mole ratio — so 1:3:2.",
        "h": "Mole ratio from equation = volume ratio for gases.",
        "yr": "SS1"
      },
      {
        "q": "Joseph Proust's work directly supported Dalton's atomic theory because:",
        "o": ["He discovered new elements", "His fixed mass ratios in compounds supported atomic composition", "He measured gas volumes precisely", "He invented the Periodic Table"],
        "a": 1,
        "e": "Joseph Proust's Law of Definite Proportions provided evidence for Dalton's atomic theory by showing compounds have fixed compositions — exactly what atomic theory predicted.",
        "h": "Proust's fixed ratios supported Dalton's atomic theory.",
        "yr": "SS1"
      },
      {
        "q": "A compound has the same properties regardless of its source. This supports:",
        "o": ["Conservation of mass", "Multiple proportions", "Combining volumes", "Definite proportions"],
        "a": 3,
        "e": "The Law of Definite (Constant) Proportions states that a pure compound has a fixed composition and fixed properties regardless of preparation method — a distinguishing feature of compounds vs mixtures.",
        "h": "Fixed composition regardless of source = Definite Proportions.",
        "yr": "SS1"
      },
      {
        "q": "The Law of Conservation of Mass implies that in a balanced chemical equation:",
        "o": ["The number of molecules is the same on both sides", "The number of atoms of each element is the same on both sides", "The number of moles is conserved", "The volume of gas is conserved"],
        "a": 1,
        "e": "Conservation of mass means atoms are neither created nor destroyed. A balanced equation reflects this — the number of atoms of each element is the same on both sides.",
        "h": "Balanced equation: same atoms on both sides = conservation of mass.",
        "yr": "SS1"
      },
      {
        "q": "N₂H₄ and N₂H₂ are two nitrogen-hydrogen compounds. In N₂H₄, 28g of N combines with 8g of H; in N₂H₂, 28g of N combines with 4g of H. The ratio of hydrogen is:",
        "o": ["1:1", "2:1", "1:4", "4:1"],
        "a": 1,
        "e": "Ratio of hydrogen = 8:4 = 2:1. This simple whole-number ratio demonstrates the Law of Multiple Proportions.",
        "h": "8g : 4g = 2:1 ratio of hydrogen.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Acids, Bases, and Salts",
    "topicCode": "SS1-CHEM-08",
    "module": "Inorganic Chemistry",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Acids</span>, <span class=\"learn-keyword\">bases</span>, and <span class=\"learn-keyword\">salts</span> are fundamental classes of chemical compounds encountered in everyday life — from citric acid in oranges, hydrochloric acid in the stomach, to sodium hydroxide in soap and calcium carbonate in chalk.\n</div>\n\n<h3 class=\"learn-subheading\">1. Acids</h3>\n<p class=\"learn-p\">An <strong>acid</strong> donates hydrogen ions (H⁺) in solution (Arrhenius) or is a proton donor (Brønsted-Lowry).</p>\n<p class=\"learn-p\"><strong>Properties:</strong> Sour taste; turn blue litmus red; pH &lt; 7; react with metals to produce H₂ gas; react with carbonates to produce CO₂; neutralise bases.</p>\n<p class=\"learn-p\"><strong>Common acids:</strong> HCl, H₂SO₄, HNO₃, CH₃COOH (ethanoic acid/vinegar), citric acid.</p>\n\n<h3 class=\"learn-subheading\">2. Bases and Alkalis</h3>\n<p class=\"learn-p\">A <strong>base</strong> accepts H⁺ ions. An <strong>alkali</strong> is a base soluble in water.</p>\n<p class=\"learn-p\"><strong>Properties:</strong> Bitter taste; soapy feel; turn red litmus blue; pH &gt; 7; neutralise acids; react with ammonium salts releasing NH₃.</p>\n<p class=\"learn-p\"><strong>Common examples:</strong> NaOH, KOH, Ca(OH)₂ (limewater), NH₃(aq).</p>\n\n<h3 class=\"learn-subheading\">3. pH Scale</h3>\n<ul class=\"learn-list\">\n  <li>pH 0–6: Acidic (lower = more acidic)</li>\n  <li>pH 7: Neutral (pure water)</li>\n  <li>pH 8–14: Alkaline (higher = more alkaline)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Neutralisation</h3>\n<p class=\"learn-p\"><strong>Acid + Base → Salt + Water</strong></p>\n<p class=\"learn-p\">Example: HCl + NaOH → NaCl + H₂O</p>\n<p class=\"learn-p\">Example: H₂SO₄ + 2KOH → K₂SO₄ + 2H₂O</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Acids turn blue litmus red; alkalis turn red litmus blue. pH &lt; 7 = acid; pH 7 = neutral; pH &gt; 7 = alkali. Neutralisation always produces salt + water. Strong acids (HCl, H₂SO₄, HNO₃) fully ionise; weak acids (CH₃COOH) partially ionise.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Acids: donate H⁺, sour taste, blue litmus → red, pH &lt; 7. Bases: accept H⁺, bitter taste, soapy feel, pH &gt; 7. Alkalis = soluble bases. Neutralisation = acid + base → salt + water.\n</div>\n  ",
    "questions": [
      {
        "q": "An acid is best defined as a substance that:",
        "o": ["Turns red litmus blue", "Has a bitter taste and feels soapy", "Donates hydrogen ions (H⁺) in solution", "Has a pH greater than 7"],
        "a": 2,
        "e": "According to the Arrhenius definition, an acid is a substance that produces hydrogen ions (H⁺) when dissolved in water.",
        "h": "Acids donate H⁺ ions in solution.",
        "yr": "SS1"
      },
      {
        "q": "What is the pH of a neutral solution such as pure water?",
        "o": ["0", "7", "14", "5"],
        "a": 1,
        "e": "Pure water is neutral with a pH of 7. At pH 7, the concentration of H⁺ equals OH⁻. Below 7 is acidic; above 7 is alkaline.",
        "h": "pH 7 = neutral.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a strong acid?",
        "o": ["Citric acid", "Ethanoic acid (vinegar)", "Carbonic acid", "Hydrochloric acid (HCl)"],
        "a": 3,
        "e": "HCl is a strong acid — it completely ionises in water. Citric, ethanoic, and carbonic acids are weak acids that only partially ionise.",
        "h": "Strong acids fully ionise. HCl, H₂SO₄, HNO₃ are the key three.",
        "yr": "SS1"
      },
      {
        "q": "What does an acid do to blue litmus paper?",
        "o": ["Turns it colourless", "Turns it blue", "Turns it red", "Has no effect"],
        "a": 2,
        "e": "Acids turn blue litmus paper red. Alkalis turn red litmus blue. Neutral solutions do not change litmus.",
        "h": "Acids turn blue litmus red.",
        "yr": "SS1"
      },
      {
        "q": "An alkali differs from a base in that an alkali is:",
        "o": ["Any substance with pH > 7", "A base that is soluble in water", "A substance that only reacts with acids", "An insoluble substance that turns litmus blue"],
        "a": 1,
        "e": "All alkalis are bases, but not all bases are alkalis. An alkali is a base that dissolves in water producing OH⁻ ions. Insoluble bases like copper oxide are not alkalis.",
        "h": "Alkali = base soluble in water.",
        "yr": "SS1"
      },
      {
        "q": "The reaction between an acid and a base is called:",
        "o": ["Oxidation", "Reduction", "Neutralisation", "Sublimation"],
        "a": 2,
        "e": "Neutralisation is the reaction between an acid and a base to produce a salt and water. Example: HCl + NaOH → NaCl + H₂O.",
        "h": "Acid + base → salt + water = neutralisation.",
        "yr": "SS1"
      },
      {
        "q": "Products of the neutralisation of HCl with NaOH are:",
        "o": ["Sodium chloride and hydrogen gas", "Sodium chloride and water", "Sodium oxide and water", "Sodium chloride and carbon dioxide"],
        "a": 1,
        "e": "HCl + NaOH → NaCl + H₂O. The acid and alkali neutralise to form sodium chloride (table salt) and water. No gas is produced.",
        "h": "HCl + NaOH → NaCl + H₂O.",
        "yr": "SS1"
      },
      {
        "q": "Which is a common property of alkalis?",
        "o": ["Sour taste", "Turn blue litmus red", "React with acids to produce hydrogen gas", "Feel soapy and taste bitter"],
        "a": 3,
        "e": "Alkalis feel soapy to the touch and taste bitter. They turn red litmus blue, have pH > 7, and react with acids to form salts and water.",
        "h": "Alkalis: soapy feel, bitter taste, red litmus → blue.",
        "yr": "SS1"
      },
      {
        "q": "What gas is produced when an acid reacts with a carbonate?",
        "o": ["Hydrogen gas", "Oxygen gas", "Carbon dioxide gas", "Sulfur dioxide gas"],
        "a": 2,
        "e": "Acid + carbonate → salt + water + CO₂. Example: 2HCl + CaCO₃ → CaCl₂ + H₂O + CO₂. The CO₂ causes effervescence (bubbling).",
        "h": "Acid + carbonate → salt + water + CO₂.",
        "yr": "SS1"
      },
      {
        "q": "A solution has pH 2. This solution is:",
        "o": ["Weakly alkaline", "Strongly alkaline", "Neutral", "Strongly acidic"],
        "a": 3,
        "e": "pH 2 indicates a strongly acidic solution. The lower the pH below 7, the more acidic. pH 2 is more acidic than pH 5.",
        "h": "Low pH (1–3) = strongly acidic.",
        "yr": "SS1"
      },
      {
        "q": "Name the salt formed when sulfuric acid reacts with sodium hydroxide:",
        "o": ["Sodium chloride", "Sodium sulfate", "Sodium sulfite", "Sodium nitrate"],
        "a": 1,
        "e": "H₂SO₄ + 2NaOH → Na₂SO₄ + 2H₂O. The salt formed is sodium sulfate (Na₂SO₄).",
        "h": "H₂SO₄ forms sulfates; NaOH provides Na⁺ → sodium sulfate.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a weak acid found in fruits?",
        "o": ["Hydrochloric acid", "Sulfuric acid", "Citric acid", "Nitric acid"],
        "a": 2,
        "e": "Citric acid is a weak organic acid found naturally in citrus fruits. It only partially ionises in water, unlike strong acids HCl, H₂SO₄, and HNO₃.",
        "h": "Citric acid gives citrus fruits their sour taste.",
        "yr": "SS1"
      },
      {
        "q": "Limewater turns milky when CO₂ is bubbled through it because:",
        "o": ["HCl is formed", "An insoluble white precipitate of calcium carbonate forms", "Calcium dissolves releasing hydrogen", "Water evaporates leaving calcium behind"],
        "a": 1,
        "e": "CO₂ + Ca(OH)₂ → CaCO₃ + H₂O. Calcium carbonate (CaCO₃) is an insoluble white precipitate that makes limewater turn milky — the standard test for CO₂.",
        "h": "CO₂ + limewater → insoluble CaCO₃ (milky white precipitate).",
        "yr": "SS1"
      },
      {
        "q": "The formula of sulfuric acid is:",
        "o": ["HCl", "HNO₃", "H₂SO₄", "H₂CO₃"],
        "a": 2,
        "e": "Sulfuric acid has the formula H₂SO₄. It is a diprotic strong acid widely used in industry and as a laboratory reagent.",
        "h": "H₂SO₄ = sulfuric acid.",
        "yr": "SS1"
      },
      {
        "q": "When zinc reacts with hydrochloric acid, the gas produced is:",
        "o": ["Oxygen", "Carbon dioxide", "Chlorine", "Hydrogen"],
        "a": 3,
        "e": "Zn + 2HCl → ZnCl₂ + H₂. When a reactive metal reacts with dilute acid, hydrogen gas is produced. Tested with a burning splint — it burns with a squeaky pop.",
        "h": "Metal + acid → salt + hydrogen gas.",
        "yr": "SS1"
      },
      {
        "q": "Ammonia solution (NH₃(aq)) is a weak alkali. Its pH is likely:",
        "o": ["4", "7", "10", "1"],
        "a": 2,
        "e": "Ammonia solution is a weak alkali with pH around 10–11. It is alkaline (pH > 7) but does not fully ionise, making it weak compared to NaOH (strong alkali).",
        "h": "Weak alkali → pH slightly above 7, around 10.",
        "yr": "SS1"
      },
      {
        "q": "A salt is formed when the hydrogen ions in an acid are replaced by:",
        "o": ["Oxygen ions", "Metal ions or ammonium ions", "Hydroxide ions", "Carbonate ions"],
        "a": 1,
        "e": "A salt forms when H⁺ ions in an acid are replaced by a metal ion (or NH₄⁺). Example: In HCl → NaCl, H⁺ is replaced by Na⁺.",
        "h": "Salt = acid with H⁺ replaced by a metal or ammonium ion.",
        "yr": "SS1"
      },
      {
        "q": "Which acid is produced in the stomach to help digestion?",
        "o": ["Sulfuric acid", "Nitric acid", "Hydrochloric acid", "Phosphoric acid"],
        "a": 2,
        "e": "The stomach produces hydrochloric acid (HCl) as gastric juice. It digests proteins, kills bacteria, and activates digestive enzymes.",
        "h": "Stomach acid = HCl (hydrochloric acid).",
        "yr": "SS1"
      },
      {
        "q": "The colour of universal indicator in a solution of pH 10 is:",
        "o": ["Red", "Yellow", "Green", "Blue or purple"],
        "a": 3,
        "e": "Universal indicator turns blue or purple in alkaline conditions. At pH 10 (strongly alkaline), it shows blue-purple. Green = neutral (pH 7); red = strong acid.",
        "h": "pH 10 = alkaline → universal indicator turns blue/purple.",
        "yr": "SS1"
      },
      {
        "q": "The reaction of an acid with a metal oxide produces:",
        "o": ["Salt, water, and carbon dioxide", "Salt and water only", "Salt and hydrogen gas", "Salt, water, and hydrogen gas"],
        "a": 1,
        "e": "Acid + metal oxide (base) → salt + water. Example: H₂SO₄ + CuO → CuSO₄ + H₂O. No gas is produced because a metal oxide is not a carbonate or reactive metal.",
        "h": "Acid + metal oxide → salt + water (no gas).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Chemical Symbols, Formulae, and Equations",
    "topicCode": "SS1-CHEM-09",
    "module": "Foundations of Chemistry",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Chemical symbols</span> are shorthand representations of elements. <span class=\"learn-keyword\">Chemical formulae</span> show the composition of compounds. <span class=\"learn-keyword\">Chemical equations</span> describe reactions — these are the language of chemistry.\n</div>\n\n<h3 class=\"learn-subheading\">1. Chemical Symbols</h3>\n<p class=\"learn-p\">Each element uses a 1–2 letter symbol (first letter always capitalised). Many symbols derive from Latin names.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Symbol</th><th>Latin Origin</th></tr></thead>\n    <tbody>\n      <tr><td>Sodium</td><td>Na</td><td>Natrium</td></tr>\n      <tr><td>Potassium</td><td>K</td><td>Kalium</td></tr>\n      <tr><td>Iron</td><td>Fe</td><td>Ferrum</td></tr>\n      <tr><td>Copper</td><td>Cu</td><td>Cuprum</td></tr>\n      <tr><td>Silver</td><td>Ag</td><td>Argentum</td></tr>\n      <tr><td>Gold</td><td>Au</td><td>Aurum</td></tr>\n      <tr><td>Lead</td><td>Pb</td><td>Plumbum</td></tr>\n      <tr><td>Mercury</td><td>Hg</td><td>Hydrargyrum</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Chemical Formulae</h3>\n<p class=\"learn-p\">Subscript numbers show how many atoms of each element are present. Examples: H₂O (2H, 1O); H₂SO₄ (2H, 1S, 4O); Ca(OH)₂ (1Ca, 2O, 2H); Al₂(SO₄)₃ (2Al, 3S, 12O).</p>\n\n<h3 class=\"learn-subheading\">3. Balancing Chemical Equations</h3>\n<ol class=\"learn-list\">\n  <li>Write the unbalanced formula equation.</li>\n  <li>Count atoms on each side.</li>\n  <li>Add coefficients (never change subscripts) to balance.</li>\n  <li>Verify by counting atoms again.</li>\n</ol>\n<p class=\"learn-p\"><strong>Example:</strong> 2H₂ + O₂ → 2H₂O. Left: 4H, 2O. Right: 4H, 2O ✓</p>\n\n<h3 class=\"learn-subheading\">4. State Symbols</h3>\n<p class=\"learn-p\">(s) = solid; (l) = liquid; (g) = gas; (aq) = aqueous solution</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Never change subscripts when balancing — only change coefficients. Know Latin-origin symbols: Na (Natrium), K (Kalium), Fe (Ferrum), Cu (Cuprum), Ag (Argentum), Au (Aurum), Pb (Plumbum), Hg (Hydrargyrum).\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Symbols use 1–2 letters with first letter capitalised. Formulae show atom types and numbers. Balanced equations have equal atoms on both sides. State symbols: (s), (l), (g), (aq).\n</div>\n  ",
    "questions": [
      {
        "q": "The chemical symbol for potassium is K because it comes from the Latin word:",
        "o": ["Kuprum", "Kalium", "Krypton", "Komplex"],
        "a": 1,
        "e": "K for potassium comes from 'Kalium', the Latin name. Potassium was named after potash (potassium carbonate) extracted from wood ash.",
        "h": "K = Kalium (Latin for potassium).",
        "yr": "SS1"
      },
      {
        "q": "What is the chemical symbol for iron?",
        "o": ["Ir", "Io", "Fe", "Fn"],
        "a": 2,
        "e": "The symbol for iron is Fe, from the Latin 'Ferrum'. Iron is a transition metal with atomic number 26.",
        "h": "Fe = Ferrum (Latin for iron).",
        "yr": "SS1"
      },
      {
        "q": "How many atoms of oxygen are in one molecule of sulfuric acid (H₂SO₄)?",
        "o": ["1", "2", "4", "6"],
        "a": 2,
        "e": "H₂SO₄ contains 2 hydrogen atoms, 1 sulfur atom, and 4 oxygen atoms. The subscript 4 after O indicates 4 oxygen atoms.",
        "h": "The subscript after O in H₂SO₄ is 4.",
        "yr": "SS1"
      },
      {
        "q": "In Ca(OH)₂, how many hydrogen atoms are present?",
        "o": ["1", "2", "3", "4"],
        "a": 1,
        "e": "Ca(OH)₂ contains 2 (OH) groups. Each OH has 1 O and 1 H, so there are 2 O atoms and 2 H atoms. The subscript 2 outside the bracket multiplies everything inside.",
        "h": "The subscript 2 outside bracket gives 2 O and 2 H.",
        "yr": "SS1"
      },
      {
        "q": "When balancing a chemical equation, you should NEVER change:",
        "o": ["The coefficients (numbers in front of formulae)", "The state symbols", "The subscripts within formulae", "The arrow direction"],
        "a": 2,
        "e": "Only coefficients should be changed when balancing. Changing subscripts would change the identity of the substance to a different compound.",
        "h": "Never change subscripts — only change coefficients.",
        "yr": "SS1"
      },
      {
        "q": "The chemical symbol for silver is:",
        "o": ["Si", "Sv", "Ag", "Sr"],
        "a": 2,
        "e": "The symbol for silver is Ag, from the Latin 'Argentum'. Silver has atomic number 47.",
        "h": "Ag = Argentum (Latin for silver).",
        "yr": "SS1"
      },
      {
        "q": "Which state symbol represents an aqueous solution?",
        "o": ["(s)", "(l)", "(g)", "(aq)"],
        "a": 3,
        "e": "(aq) stands for 'aqueous', meaning dissolved in water. Other symbols: (s) solid, (l) liquid, (g) gas.",
        "h": "(aq) = dissolved in water (aqueous).",
        "yr": "SS1"
      },
      {
        "q": "The correctly balanced equation for hydrogen burning in oxygen is:",
        "o": ["H₂ + O₂ → H₂O₂", "H₂ + O₂ → 2H₂O", "2H₂ + O₂ → 2H₂O", "H₂ + 2O₂ → H₂O"],
        "a": 2,
        "e": "2H₂ + O₂ → 2H₂O. Left: 4H, 2O. Right: 4H, 2O ✓.",
        "h": "2H₂ + O₂ → 2H₂O: 4H and 2O on each side.",
        "yr": "SS1"
      },
      {
        "q": "CO₂ contains:",
        "o": ["2 carbon atoms and 1 oxygen atom", "1 carbon atom and 2 oxygen atoms", "2 carbon atoms and 2 oxygen atoms", "1 carbon atom and 1 oxygen atom"],
        "a": 1,
        "e": "CO₂ contains 1 carbon atom (no subscript = 1) and 2 oxygen atoms (subscript 2 after O). It is the gas produced in combustion and exhaled during respiration.",
        "h": "CO₂: C = 1 (no subscript); O = 2 (subscript).",
        "yr": "SS1"
      },
      {
        "q": "The Latin origin of the symbol Na (sodium) is:",
        "o": ["Natrium", "Nakrium", "Noricum", "Nephrium"],
        "a": 0,
        "e": "Na comes from 'Natrium', the Latin name for sodium. Sodium carbonate was obtained from the mineral natron found in Egyptian deposits.",
        "h": "Na = Natrium (Latin for sodium).",
        "yr": "SS1"
      },
      {
        "q": "How many total atoms are in one formula unit of Al₂(SO₄)₃?",
        "o": ["7", "9", "13", "17"],
        "a": 3,
        "e": "Al₂(SO₄)₃ = 2 Al + 3 S + 3×4 O = 2 + 3 + 12 = 17 atoms total.",
        "h": "2 Al + 3 S + 12 O = 17 total atoms.",
        "yr": "SS1"
      },
      {
        "q": "What does the coefficient '3' in front of H₂O mean?",
        "o": ["There are 3 hydrogen atoms", "There are 3 molecules of water", "There are 3 oxygen atoms in each water molecule", "Water has 3 bonds"],
        "a": 1,
        "e": "A coefficient in front of a formula tells how many molecules or formula units are involved. 3H₂O means 3 molecules of water.",
        "h": "Coefficient = number of molecules or formula units.",
        "yr": "SS1"
      },
      {
        "q": "The symbol Cu for copper comes from the Latin word:",
        "o": ["Copperium", "Cuprum", "Curium", "Calium"],
        "a": 1,
        "e": "Cu comes from 'Cuprum', the Latin name for copper, named after the island of Cyprus — a major ancient copper mining site.",
        "h": "Cu = Cuprum (Latin for copper, from Cyprus).",
        "yr": "SS1"
      },
      {
        "q": "The balanced equation for formation of iron(III) oxide (rust) is:",
        "o": ["Fe + O₂ → Fe₂O₃", "2Fe + O₂ → Fe₂O₃", "4Fe + 3O₂ → 2Fe₂O₃", "Fe + 3O₂ → Fe₂O₃"],
        "a": 2,
        "e": "4Fe + 3O₂ → 2Fe₂O₃. Left: 4Fe, 6O. Right: 4Fe, 6O ✓.",
        "h": "4Fe + 3O₂ → 2Fe₂O₃: 4Fe and 6O on each side.",
        "yr": "SS1"
      },
      {
        "q": "Total number of atoms in H₂SO₄:",
        "o": ["4", "6", "7", "8"],
        "a": 2,
        "e": "H₂SO₄ contains: 2H + 1S + 4O = 7 atoms in total.",
        "h": "2 + 1 + 4 = 7 atoms.",
        "yr": "SS1"
      },
      {
        "q": "The balanced equation for sodium reacting with water is:",
        "o": ["Na + H₂O → NaOH + H", "2Na + 2H₂O → 2NaOH + H₂", "Na + 2H₂O → NaOH + H₂", "2Na + H₂O → NaOH + H₂"],
        "a": 1,
        "e": "2Na + 2H₂O → 2NaOH + H₂. Left: 2Na, 4H, 2O. Right: 2Na (in NaOH), 2O (in NaOH), 4H (2 from NaOH + 2 from H₂) ✓.",
        "h": "2Na + 2H₂O → 2NaOH + H₂ is the balanced equation.",
        "yr": "SS1"
      },
      {
        "q": "The chemical symbol for mercury is:",
        "o": ["Me", "My", "Hg", "Mr"],
        "a": 2,
        "e": "Mercury's symbol is Hg, from the Latin 'Hydrargyrum' meaning 'liquid silver'. Mercury is the only metal liquid at room temperature.",
        "h": "Hg = Hydrargyrum (Latin for mercury).",
        "yr": "SS1"
      },
      {
        "q": "CaCO₃ contains:",
        "o": ["1Ca, 3C, 3O", "1Ca, 1C, 3O", "2Ca, 1C, 3O", "1Ca, 1C, 6O"],
        "a": 1,
        "e": "CaCO₃: 1 Ca, 1 C, 3 O. The subscript 3 applies only to oxygen. Calcium carbonate is the formula for limestone, chalk, and marble.",
        "h": "CaCO₃: 1 Ca + 1 C + 3 O.",
        "yr": "SS1"
      },
      {
        "q": "What does (g) mean in a chemical equation?",
        "o": ["Grams of the substance", "The substance is a gas", "The substance is dissolved in water", "The substance is at a fixed temperature"],
        "a": 1,
        "e": "(g) is the state symbol for gas. When written after a formula, it indicates the substance is in the gaseous state.",
        "h": "(g) = gas state symbol.",
        "yr": "SS1"
      },
      {
        "q": "The balanced equation for complete combustion of methane is:",
        "o": ["CH₄ + O₂ → CO₂ + 2H₂O", "CH₄ + 2O₂ → CO₂ + 2H₂O", "2CH₄ + O₂ → CO₂ + H₂O", "CH₄ + O₂ → CO₂ + H₂O"],
        "a": 1,
        "e": "CH₄ + 2O₂ → CO₂ + 2H₂O. Left: 1C, 4H, 4O. Right: 1C, 4H, 4O ✓. This is the complete combustion of methane.",
        "h": "CH₄ + 2O₂ → CO₂ + 2H₂O: 1C, 4H, 4O each side.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Quantitative and Qualitative Analysis",
    "topicCode": "SS1-CHEM-10",
    "module": "Analytical Chemistry",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Qualitative analysis</span> identifies <em>what</em> substances are present in a sample. <span class=\"learn-keyword\">Quantitative analysis</span> determines <em>how much</em> of a substance is present. Both are fundamental skills in chemistry, medicine, environmental science, and industry.\n</div>\n\n<h3 class=\"learn-subheading\">1. Qualitative Analysis — Basic Tests</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Test</th><th>Procedure</th><th>Positive Result</th></tr></thead>\n    <tbody>\n      <tr><td>Test for CO₂</td><td>Bubble gas through limewater</td><td>Limewater turns milky (CaCO₃ precipitate)</td></tr>\n      <tr><td>Test for H₂</td><td>Apply burning splint to gas</td><td>Squeaky pop</td></tr>\n      <tr><td>Test for O₂</td><td>Apply glowing splint to gas</td><td>Glowing splint relights</td></tr>\n      <tr><td>Test for NH₃</td><td>Moist red litmus paper</td><td>Red litmus turns blue; pungent smell</td></tr>\n      <tr><td>Test for Cl₂</td><td>Moist blue litmus paper</td><td>Turns red then bleaches white</td></tr>\n      <tr><td>Test for water</td><td>Anhydrous cobalt chloride paper</td><td>Blue → pink</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Quantitative Analysis — Titration</h3>\n<p class=\"learn-p\">Titration is the main quantitative technique. A solution of known concentration (standard solution) is used to find the concentration of an unknown solution.</p>\n<p class=\"learn-p\"><strong>Key terms:</strong></p>\n<ul class=\"learn-list\">\n  <li><strong>Titre:</strong> The volume of solution delivered from the burette to reach the endpoint.</li>\n  <li><strong>Endpoint:</strong> The point at which the indicator changes colour permanently.</li>\n  <li><strong>Equivalence point:</strong> The theoretical point at which moles of acid = moles of base (adjusted for stoichiometry).</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Indicators</h3>\n<ul class=\"learn-list\">\n  <li><strong>Methyl orange:</strong> Red in acid, yellow in alkali. Best for strong acid–strong base or strong acid–weak base.</li>\n  <li><strong>Phenolphthalein:</strong> Colourless in acid, pink/purple in alkali. Best for weak acid–strong base.</li>\n  <li><strong>Litmus:</strong> Red in acid, blue in alkali.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Qualitative = identity (WHAT). Quantitative = amount (HOW MUCH). Know all gas tests. For titration: Methyl orange is best for HCl/NaOH; Phenolphthalein best for weak acid/strong base. Anhydrous CoCl₂ = blue → pink for water detection.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Qualitative analysis identifies substances using tests (e.g., limewater for CO₂, burning splint for H₂). Quantitative analysis measures amounts — titration being the main method. Indicators show the endpoint: methyl orange (red→yellow) and phenolphthalein (colourless→pink).\n</div>\n  ",
    "questions": [
      {
        "q": "Qualitative analysis in chemistry is concerned with:",
        "o": ["Measuring how much of a substance is present", "Identifying what substances are present in a sample", "Calculating the molar mass of substances", "Determining the boiling points of liquids"],
        "a": 1,
        "e": "Qualitative analysis determines the identity of substances — WHAT is present in a sample. It involves tests like litmus, flame tests, and gas tests.",
        "h": "Qualitative = identity (WHAT is present).",
        "yr": "SS1"
      },
      {
        "q": "Quantitative analysis determines:",
        "o": ["What elements are present in a compound", "How much of a substance is present in a sample", "The colour of a substance in solution", "The physical state of a substance"],
        "a": 1,
        "e": "Quantitative analysis measures the amount or concentration of a substance — HOW MUCH is present. Titration is the main quantitative technique at SS1 level.",
        "h": "Quantitative = amount (HOW MUCH is present).",
        "yr": "SS1"
      },
      {
        "q": "A gas turns limewater milky. The gas is:",
        "o": ["Hydrogen", "Oxygen", "Carbon dioxide", "Nitrogen"],
        "a": 2,
        "e": "Carbon dioxide (CO₂) turns limewater milky due to the formation of insoluble calcium carbonate: CO₂ + Ca(OH)₂ → CaCO₃ + H₂O.",
        "h": "CO₂ turns limewater milky — the standard CO₂ test.",
        "yr": "SS1"
      },
      {
        "q": "A gas produces a squeaky pop when a burning splint is applied. The gas is:",
        "o": ["Oxygen", "Carbon dioxide", "Hydrogen", "Chlorine"],
        "a": 2,
        "e": "Hydrogen gas produces a characteristic squeaky pop when ignited with a burning splint. This is because H₂ burns rapidly: 2H₂ + O₂ → 2H₂O.",
        "h": "Hydrogen = squeaky pop with burning splint.",
        "yr": "SS1"
      },
      {
        "q": "Anhydrous cobalt(II) chloride paper turns from blue to pink in the presence of:",
        "o": ["Oxygen gas", "Hydrogen gas", "Carbon dioxide", "Water (moisture)"],
        "a": 3,
        "e": "Anhydrous cobalt(II) chloride is blue. When it absorbs water, it becomes hydrated (CoCl₂·6H₂O), turning pink. This is the standard test for water.",
        "h": "Blue → pink with anhydrous CoCl₂ = water present.",
        "yr": "SS1"
      },
      {
        "q": "A glowing splint relights when inserted into a gas. The gas is:",
        "o": ["Hydrogen", "Carbon dioxide", "Nitrogen", "Oxygen"],
        "a": 3,
        "e": "Oxygen relights a glowing splint. This is the standard test for oxygen — the excess oxygen supports combustion and causes the glowing splint to burst back into flame.",
        "h": "Oxygen relights a glowing splint.",
        "yr": "SS1"
      },
      {
        "q": "In titration, the titre is defined as:",
        "o": ["The initial volume in the burette", "The volume of solution in the conical flask", "The volume of solution delivered from the burette to reach the endpoint", "The concentration of the standard solution"],
        "a": 2,
        "e": "The titre is the volume of solution delivered from the burette during titration to reach the endpoint (indicator colour change). It is calculated as: titre = initial reading − final reading.",
        "h": "Titre = volume used from the burette.",
        "yr": "SS1"
      },
      {
        "q": "Which indicator turns red in acid and yellow in alkali?",
        "o": ["Phenolphthalein", "Methyl orange", "Litmus", "Universal indicator"],
        "a": 1,
        "e": "Methyl orange is red in acidic solutions and yellow in alkaline solutions. It is best used for strong acid–strong base or strong acid–weak base titrations.",
        "h": "Methyl orange: red (acid) → yellow (alkali).",
        "yr": "SS1"
      },
      {
        "q": "Phenolphthalein is colourless in acid and turns pink/purple in alkali. It is best used in:",
        "o": ["Strong acid–strong base titrations", "Weak acid–strong base titrations", "Strong acid–weak base titrations", "All titrations equally"],
        "a": 1,
        "e": "Phenolphthalein is best used for weak acid–strong base titrations (e.g., ethanoic acid + NaOH). Its colour change is sharp in this range.",
        "h": "Phenolphthalein: best for weak acid + strong base titrations.",
        "yr": "SS1"
      },
      {
        "q": "A gas turns moist red litmus paper blue and has a pungent smell. The gas is:",
        "o": ["Chlorine", "Oxygen", "Ammonia", "Hydrogen sulfide"],
        "a": 2,
        "e": "Ammonia (NH₃) is an alkaline gas that turns moist red litmus paper blue and has a characteristic pungent, choking smell. It is the only common alkaline gas.",
        "h": "Pungent gas + turns red litmus blue = ammonia.",
        "yr": "SS1"
      },
      {
        "q": "The endpoint of a titration is:",
        "o": ["The point where the burette is empty", "The theoretical point where moles of acid = moles of base", "The point at which the indicator changes colour permanently", "The starting point of the titration"],
        "a": 2,
        "e": "The endpoint is the point in a titration where the indicator permanently changes colour, signalling that the reaction is complete. It is the practical equivalent of the equivalence point.",
        "h": "Endpoint = permanent colour change of indicator.",
        "yr": "SS1"
      },
      {
        "q": "Chlorine gas applied to moist blue litmus paper:",
        "o": ["Turns it red permanently", "Turns it red then bleaches it white", "Has no effect", "Turns it blue immediately"],
        "a": 1,
        "e": "Chlorine is an acidic gas that first turns moist blue litmus red (due to acidity), then bleaches it white (due to its oxidising/bleaching properties).",
        "h": "Cl₂ + moist litmus: red first, then bleached white.",
        "yr": "SS1"
      },
      {
        "q": "In a titration, the standard solution is:",
        "o": ["A solution of unknown concentration used in the burette", "A solution of precisely known concentration", "A solution that changes colour at the endpoint", "A saturated solution used in the flask"],
        "a": 1,
        "e": "A standard solution has a precisely known concentration. It is used in titrations to determine the concentration of an unknown solution by reaction stoichiometry.",
        "h": "Standard solution = precisely known concentration.",
        "yr": "SS1"
      },
      {
        "q": "The test for oxygen uses:",
        "o": ["A burning splint (squeaky pop)", "A glowing splint that relights", "Limewater turning milky", "Moist red litmus turning blue"],
        "a": 1,
        "e": "Oxygen is tested by inserting a glowing splint into the gas. If oxygen is present, the splint relights (bursts back into flame) because oxygen supports combustion.",
        "h": "Oxygen test = glowing splint relights.",
        "yr": "SS1"
      },
      {
        "q": "Why are concordant titres (within 0.1 cm³) required in titration?",
        "o": ["To make the calculations easier", "To ensure reproducibility and accuracy of results", "To save on the volume of chemicals used", "To satisfy the law of multiple proportions"],
        "a": 1,
        "e": "Concordant titres (very similar results, within 0.1 cm³) are required to ensure the titration is reproducible and accurate. The mean of concordant titres is used in calculations.",
        "h": "Concordant results = reproducible, reliable, accurate.",
        "yr": "SS1"
      },
      {
        "q": "Which apparatus is filled with the standard solution in an acid-base titration?",
        "o": ["Conical flask", "Volumetric flask", "Burette", "Beaker"],
        "a": 2,
        "e": "The burette is filled with the standard solution (usually the acid or base of known concentration). The unknown solution is placed in the conical flask.",
        "h": "Burette contains the standard solution.",
        "yr": "SS1"
      },
      {
        "q": "A solution of hydrogen sulfide (H₂S) gas has a smell of:",
        "o": ["Freshly cut grass", "Rotten eggs", "Chlorine bleach", "Ammonia"],
        "a": 1,
        "e": "Hydrogen sulfide (H₂S) has a very distinctive rotten egg smell. It is toxic and is produced by the decay of organic matter containing sulfur.",
        "h": "H₂S smells like rotten eggs.",
        "yr": "SS1"
      },
      {
        "q": "Distilled water turns anhydrous cobalt chloride paper from:",
        "o": ["Pink to blue", "Blue to pink", "Red to yellow", "Yellow to red"],
        "a": 1,
        "e": "Anhydrous cobalt chloride (blue) absorbs water to become hydrated, turning pink. The change is blue → pink, indicating the presence of water.",
        "h": "Blue → pink = water detected by anhydrous CoCl₂.",
        "yr": "SS1"
      },
      {
        "q": "In a flame test, sodium compounds produce a:",
        "o": ["Brick-red flame", "Lilac flame", "Green flame", "Bright yellow flame"],
        "a": 3,
        "e": "Sodium compounds produce a bright, persistent yellow flame in a flame test. This is one of the most distinctive and easily recognised flame test colours.",
        "h": "Sodium = bright yellow flame.",
        "yr": "SS1"
      },
      {
        "q": "Adding dilute HCl to a white solid produces effervescence and turns limewater milky. The solid likely contains:",
        "o": ["A sulfate", "A chloride", "A carbonate", "A nitrate"],
        "a": 2,
        "e": "Carbonates react with dilute HCl to produce CO₂ gas, which causes effervescence and turns limewater milky. This is the standard test for carbonates.",
        "h": "Carbonate + acid → CO₂ (effervescence, milky limewater).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Mole Concept and Stoichiometry",
    "topicCode": "SS1-CHEM-11",
    "module": "Quantitative Chemistry",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The <span class=\"learn-keyword\">mole</span> is the SI unit for amount of substance. One mole contains exactly <span class=\"learn-keyword\">6.02 × 10²³ particles</span> (Avogadro's number). The mole concept links the macroscopic (grams) to the microscopic (atoms/molecules) world, making quantitative calculations possible.\n</div>\n\n<h3 class=\"learn-subheading\">1. Key Definitions</h3>\n<ul class=\"learn-list\">\n  <li><strong>Mole (mol):</strong> The amount of substance containing 6.02 × 10²³ particles (atoms, molecules, or ions).</li>\n  <li><strong>Avogadro's number (Nₐ):</strong> 6.02 × 10²³ mol⁻¹.</li>\n  <li><strong>Molar mass (M):</strong> The mass of one mole of a substance in g/mol. Numerically equal to the relative atomic/molecular mass.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Mole Calculations</h3>\n<p class=\"learn-p\"><strong>Number of moles = mass ÷ molar mass</strong><br>n = m ÷ M</p>\n<p class=\"learn-p\"><strong>Number of particles = moles × Avogadro's number</strong><br>N = n × Nₐ</p>\n<p class=\"learn-p\"><strong>Example:</strong> How many moles in 9g of water (M = 18 g/mol)?<br>n = 9 ÷ 18 = 0.5 mol</p>\n\n<h3 class=\"learn-subheading\">3. Molar Mass of Compounds</h3>\n<p class=\"learn-p\">Add the relative atomic masses (RAM) of all atoms in the formula.</p>\n<p class=\"learn-p\"><strong>Example:</strong> Molar mass of CaCO₃: Ca(40) + C(12) + 3×O(16) = 40 + 12 + 48 = 100 g/mol.</p>\n\n<h3 class=\"learn-subheading\">4. Stoichiometry</h3>\n<p class=\"learn-p\">Stoichiometry uses the balanced equation to find the mole ratios of reactants and products.</p>\n<p class=\"learn-p\"><strong>Example:</strong> In 2H₂ + O₂ → 2H₂O, 2 mol H₂ reacts with 1 mol O₂ to form 2 mol H₂O (2:1:2 ratio).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Key formula: n = m/M. Always use molar mass (g/mol) in calculations. Avogadro's number = 6.02 × 10²³. To find number of particles: N = n × Nₐ. Molar mass of H₂O = 18; NaCl = 58.5; CaCO₃ = 100; H₂SO₄ = 98.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> 1 mole = 6.02 × 10²³ particles. n = m/M. Stoichiometry uses mole ratios from balanced equations to link reactants and products. Molar mass = sum of all RAM in the formula.\n</div>\n  ",
    "questions": [
      {
        "q": "One mole of any substance contains:",
        "o": ["1 × 10²³ particles", "6.02 × 10²³ particles", "6.02 × 10²⁴ particles", "1 × 10²⁴ particles"],
        "a": 1,
        "e": "One mole contains exactly 6.02 × 10²³ particles (Avogadro's number). This applies to atoms, molecules, ions, or any other specified particle.",
        "h": "1 mole = 6.02 × 10²³ particles (Avogadro's number).",
        "yr": "SS1"
      },
      {
        "q": "The formula for calculating the number of moles is:",
        "o": ["n = M × m", "n = m ÷ M", "n = M ÷ m", "n = m + M"],
        "a": 1,
        "e": "Number of moles (n) = mass (m) ÷ molar mass (M). For example, 18g of water ÷ 18 g/mol = 1 mol of water.",
        "h": "n = m ÷ M (moles = mass ÷ molar mass).",
        "yr": "SS1"
      },
      {
        "q": "What is the molar mass of water (H₂O)? (H = 1, O = 16)",
        "o": ["16 g/mol", "17 g/mol", "18 g/mol", "20 g/mol"],
        "a": 2,
        "e": "Molar mass of H₂O = 2×1 (H) + 16 (O) = 2 + 16 = 18 g/mol.",
        "h": "H₂O: 2×1 + 16 = 18 g/mol.",
        "yr": "SS1"
      },
      {
        "q": "How many moles are in 9g of water (M = 18 g/mol)?",
        "o": ["0.25 mol", "0.5 mol", "1 mol", "2 mol"],
        "a": 1,
        "e": "n = m ÷ M = 9 ÷ 18 = 0.5 mol.",
        "h": "n = 9 ÷ 18 = 0.5 mol.",
        "yr": "SS1"
      },
      {
        "q": "Avogadro's number (Nₐ) has the value:",
        "o": ["6.02 × 10²⁰", "6.02 × 10²³", "6.02 × 10²⁶", "3.01 × 10²³"],
        "a": 1,
        "e": "Avogadro's number Nₐ = 6.02 × 10²³ mol⁻¹. It defines the number of particles in one mole of any substance.",
        "h": "Nₐ = 6.02 × 10²³.",
        "yr": "SS1"
      },
      {
        "q": "What is the molar mass of CaCO₃? (Ca=40, C=12, O=16)",
        "o": ["68 g/mol", "84 g/mol", "100 g/mol", "116 g/mol"],
        "a": 2,
        "e": "Molar mass of CaCO₃ = 40 (Ca) + 12 (C) + 3×16 (O) = 40 + 12 + 48 = 100 g/mol.",
        "h": "CaCO₃: 40 + 12 + 48 = 100 g/mol.",
        "yr": "SS1"
      },
      {
        "q": "How many molecules are in 0.5 mol of CO₂?",
        "o": ["6.02 × 10²³", "3.01 × 10²³", "1.20 × 10²⁴", "6.02 × 10²²"],
        "a": 1,
        "e": "Number of molecules = n × Nₐ = 0.5 × 6.02×10²³ = 3.01×10²³ molecules.",
        "h": "N = 0.5 × 6.02×10²³ = 3.01×10²³.",
        "yr": "SS1"
      },
      {
        "q": "In the reaction 2H₂ + O₂ → 2H₂O, how many moles of O₂ are needed to produce 4 mol of H₂O?",
        "o": ["1 mol", "2 mol", "4 mol", "8 mol"],
        "a": 1,
        "e": "From the equation, mole ratio H₂O:O₂ = 2:1. To produce 4 mol H₂O, we need 4÷2 = 2 mol O₂.",
        "h": "Ratio H₂O:O₂ = 2:1 → 4 mol H₂O needs 2 mol O₂.",
        "yr": "SS1"
      },
      {
        "q": "The molar mass of NaCl is: (Na=23, Cl=35.5)",
        "o": ["23 g/mol", "35.5 g/mol", "58.5 g/mol", "46.5 g/mol"],
        "a": 2,
        "e": "Molar mass of NaCl = 23 (Na) + 35.5 (Cl) = 58.5 g/mol.",
        "h": "NaCl: 23 + 35.5 = 58.5 g/mol.",
        "yr": "SS1"
      },
      {
        "q": "What mass of NaOH (M = 40 g/mol) is needed to make 2 mol?",
        "o": ["20 g", "40 g", "80 g", "160 g"],
        "a": 2,
        "e": "m = n × M = 2 × 40 = 80 g.",
        "h": "m = n × M = 2 × 40 = 80 g.",
        "yr": "SS1"
      },
      {
        "q": "Stoichiometry allows chemists to:",
        "o": ["Determine the colour of a substance", "Calculate the mole ratios of reactants and products using balanced equations", "Identify unknown substances by colour reactions", "Measure the boiling points of compounds"],
        "a": 1,
        "e": "Stoichiometry uses balanced chemical equations to determine the exact mole ratios of reactants and products, allowing calculation of masses, volumes, and concentrations.",
        "h": "Stoichiometry = using mole ratios from balanced equations.",
        "yr": "SS1"
      },
      {
        "q": "How many moles are in 49g of H₂SO₄? (M = 98 g/mol)",
        "o": ["0.25 mol", "0.5 mol", "1 mol", "2 mol"],
        "a": 1,
        "e": "n = m ÷ M = 49 ÷ 98 = 0.5 mol.",
        "h": "n = 49 ÷ 98 = 0.5 mol.",
        "yr": "SS1"
      },
      {
        "q": "What mass does 3 mol of CO₂ (M = 44 g/mol) have?",
        "o": ["44 g", "88 g", "132 g", "176 g"],
        "a": 2,
        "e": "m = n × M = 3 × 44 = 132 g.",
        "h": "m = 3 × 44 = 132 g.",
        "yr": "SS1"
      },
      {
        "q": "In CaCO₃ → CaO + CO₂, how many mol of CO₂ are produced from 2 mol of CaCO₃?",
        "o": ["0.5 mol", "1 mol", "2 mol", "4 mol"],
        "a": 2,
        "e": "From the equation, mole ratio CaCO₃:CO₂ = 1:1. So 2 mol CaCO₃ produces 2 mol CO₂.",
        "h": "1:1 ratio → 2 mol CaCO₃ gives 2 mol CO₂.",
        "yr": "SS1"
      },
      {
        "q": "What is the molar mass of H₂SO₄? (H=1, S=32, O=16)",
        "o": ["66 g/mol", "82 g/mol", "98 g/mol", "114 g/mol"],
        "a": 2,
        "e": "Molar mass of H₂SO₄ = 2×1 (H) + 32 (S) + 4×16 (O) = 2 + 32 + 64 = 98 g/mol.",
        "h": "H₂SO₄: 2 + 32 + 64 = 98 g/mol.",
        "yr": "SS1"
      },
      {
        "q": "The mole is the SI unit for:",
        "o": ["Mass", "Amount of substance", "Volume of a gas", "Concentration"],
        "a": 1,
        "e": "The mole is the SI unit for amount of substance. It is defined as the amount containing exactly 6.02 × 10²³ elementary entities.",
        "h": "Mole = SI unit for amount of substance.",
        "yr": "SS1"
      },
      {
        "q": "1 mol of atoms of any element has a mass numerically equal to its:",
        "o": ["Atomic number", "Mass number of its most common isotope", "Relative atomic mass in grams", "Number of neutrons"],
        "a": 2,
        "e": "The molar mass of an element in g/mol is numerically equal to its relative atomic mass (RAM). For example, iron (RAM = 56) has molar mass 56 g/mol.",
        "h": "Molar mass (g/mol) = relative atomic mass numerically.",
        "yr": "SS1"
      },
      {
        "q": "What is the concentration (mol/dm³) of a solution containing 4 mol in 2 dm³?",
        "o": ["0.5 mol/dm³", "1 mol/dm³", "2 mol/dm³", "8 mol/dm³"],
        "a": 2,
        "e": "Concentration = moles ÷ volume = 4 ÷ 2 = 2 mol/dm³.",
        "h": "Concentration = moles ÷ volume = 4 ÷ 2 = 2 mol/dm³.",
        "yr": "SS1"
      },
      {
        "q": "In the reaction N₂ + 3H₂ → 2NH₃, how many moles of NH₃ are produced from 3 mol of H₂?",
        "o": ["1 mol", "2 mol", "3 mol", "6 mol"],
        "a": 1,
        "e": "Mole ratio H₂:NH₃ = 3:2. From 3 mol H₂: NH₃ = 3 × (2/3) = 2 mol.",
        "h": "H₂:NH₃ = 3:2, so 3 mol H₂ → 2 mol NH₃.",
        "yr": "SS1"
      },
      {
        "q": "The number of atoms in 1 mol of iron (Fe) is:",
        "o": ["56", "6.02 × 10²⁰", "6.02 × 10²³", "56 × 6.02 × 10²³"],
        "a": 2,
        "e": "By definition, 1 mole of any element contains 6.02 × 10²³ atoms (Avogadro's number), regardless of the element or its molar mass.",
        "h": "1 mol of any element = 6.02 × 10²³ atoms.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Percentage Composition and Empirical Formula",
    "topicCode": "SS1-CHEM-12",
    "module": "Quantitative Chemistry",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Percentage composition</span> tells us the mass percentage of each element in a compound. The <span class=\"learn-keyword\">empirical formula</span> gives the simplest whole-number ratio of atoms in a compound, while the <span class=\"learn-keyword\">molecular formula</span> gives the actual number of each atom.\n</div>\n\n<h3 class=\"learn-subheading\">1. Percentage Composition</h3>\n<p class=\"learn-p\"><strong>% of element = (mass of element in formula ÷ molar mass of compound) × 100</strong></p>\n<p class=\"learn-p\"><strong>Example:</strong> % of H in H₂O (M=18): = (2÷18) × 100 = 11.1%</p>\n<p class=\"learn-p\">% of O in H₂O: = (16÷18) × 100 = 88.9%</p>\n\n<h3 class=\"learn-subheading\">2. Empirical Formula</h3>\n<p class=\"learn-p\">Steps to find the empirical formula from percentage composition:</p>\n<ol class=\"learn-list\">\n  <li>Assume 100g of the compound — percentages become grams.</li>\n  <li>Divide each mass by the RAM of the element to get moles.</li>\n  <li>Divide all mole values by the smallest mole value.</li>\n  <li>Round to the nearest whole number to get the ratio.</li>\n</ol>\n<p class=\"learn-p\"><strong>Example:</strong> A compound contains 40% C, 6.7% H, 53.3% O.<br>C: 40/12 = 3.33; H: 6.7/1 = 6.7; O: 53.3/16 = 3.33<br>Divide by smallest (3.33): C=1, H=2, O=1 → Empirical formula: <strong>CH₂O</strong></p>\n\n<h3 class=\"learn-subheading\">3. Molecular Formula from Empirical Formula</h3>\n<p class=\"learn-p\"><strong>n = Molar mass of compound ÷ Molar mass of empirical formula</strong><br>Molecular formula = n × empirical formula</p>\n<p class=\"learn-p\"><strong>Example:</strong> If empirical formula CH₂O has molar mass 30, and actual molar mass is 180, then n = 180/30 = 6. Molecular formula = C₆H₁₂O₆ (glucose).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> For empirical formula: % → grams → moles (÷ RAM) → simplest ratio (÷ smallest). The empirical formula may equal the molecular formula (e.g., NaCl, H₂O). Molecular formula = n × empirical formula, where n = molar mass ÷ empirical formula mass.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> % composition = (element mass/molar mass) × 100. Empirical formula = simplest atom ratio (from % data: divide by RAM, then by smallest value). Molecular formula = n × empirical formula.\n</div>\n  ",
    "questions": [
      {
        "q": "The percentage composition of an element in a compound is calculated as:",
        "o": ["RAM of element ÷ molar mass × 100", "Moles of element ÷ molar mass × 100", "Mass of element in formula ÷ molar mass of compound × 100", "Number of atoms × molar mass × 100"],
        "a": 2,
        "e": "% composition = (mass of element in formula ÷ molar mass of compound) × 100. For H in H₂O: (2÷18) × 100 = 11.1%.",
        "h": "% = (element mass in formula ÷ molar mass) × 100.",
        "yr": "SS1"
      },
      {
        "q": "What is the percentage of oxygen in water (H₂O)? (H=1, O=16)",
        "o": ["11.1%", "22.2%", "88.9%", "77.8%"],
        "a": 2,
        "e": "Molar mass H₂O = 18. % O = (16÷18) × 100 = 88.9%.",
        "h": "% O = (16÷18) × 100 = 88.9%.",
        "yr": "SS1"
      },
      {
        "q": "The empirical formula of a compound gives:",
        "o": ["The actual number of each type of atom in the molecule", "The simplest whole-number ratio of atoms in the compound", "The three-dimensional arrangement of atoms", "The molecular mass of the compound"],
        "a": 1,
        "e": "The empirical formula gives the simplest whole-number ratio of atoms of each element in the compound. It may or may not be the same as the molecular formula.",
        "h": "Empirical formula = simplest atom ratio.",
        "yr": "SS1"
      },
      {
        "q": "A compound contains 40% C, 6.7% H, and 53.3% O. Its empirical formula is:",
        "o": ["C₂H₄O₂", "CHO", "CH₂O", "CH₃O"],
        "a": 2,
        "e": "C: 40/12=3.33; H: 6.7/1=6.7; O: 53.3/16=3.33. Divide by 3.33: C=1, H=2, O=1 → CH₂O.",
        "h": "Divide moles by smallest: 3.33:6.7:3.33 → 1:2:1 → CH₂O.",
        "yr": "SS1"
      },
      {
        "q": "What is the percentage of calcium in CaCO₃? (Ca=40, C=12, O=16)",
        "o": ["12%", "40%", "48%", "64%"],
        "a": 1,
        "e": "Molar mass of CaCO₃ = 100 g/mol. % Ca = (40÷100) × 100 = 40%.",
        "h": "% Ca = (40÷100) × 100 = 40%.",
        "yr": "SS1"
      },
      {
        "q": "The first step in finding an empirical formula from percentage data is to:",
        "o": ["Divide percentages by the molar mass of the compound", "Assume 100g of compound so percentages become grams", "Multiply percentages by Avogadro's number", "Convert all percentages to fractions"],
        "a": 1,
        "e": "By assuming 100g of compound, percentages are directly converted to grams (e.g., 40% C becomes 40g C). This simplifies the calculation.",
        "h": "Assume 100g so % directly becomes g.",
        "yr": "SS1"
      },
      {
        "q": "If the empirical formula CH₂O has mass 30 g/mol and the actual molar mass is 180 g/mol, the molecular formula is:",
        "o": ["C₂H₄O₂", "C₃H₆O₃", "C₆H₁₂O₆", "C₄H₈O₄"],
        "a": 2,
        "e": "n = 180 ÷ 30 = 6. Molecular formula = 6 × CH₂O = C₆H₁₂O₆ (glucose).",
        "h": "n = 180÷30 = 6; C₆H₁₂O₆.",
        "yr": "SS1"
      },
      {
        "q": "In calculating the empirical formula, after dividing masses by RAM to get moles, the next step is to:",
        "o": ["Multiply all values by the molar mass", "Divide all values by the largest mole number", "Divide all values by the smallest mole number", "Add all mole values together"],
        "a": 2,
        "e": "After getting moles of each element, divide all values by the smallest mole value to get the simplest whole-number ratio.",
        "h": "Divide by smallest mole value to get the simplest ratio.",
        "yr": "SS1"
      },
      {
        "q": "What is the percentage by mass of nitrogen in NH₃? (N=14, H=1)",
        "o": ["17.6%", "58.8%", "82.4%", "76.5%"],
        "a": 2,
        "e": "Molar mass of NH₃ = 14 + 3×1 = 17 g/mol. % N = (14÷17) × 100 = 82.4%.",
        "h": "% N = (14÷17) × 100 = 82.4%.",
        "yr": "SS1"
      },
      {
        "q": "A compound is 75% carbon and 25% hydrogen by mass. Its empirical formula is:",
        "o": ["CH", "CH₂", "CH₃", "CH₄"],
        "a": 2,
        "e": "C: 75/12 = 6.25; H: 25/1 = 25. Divide by 6.25: C=1, H=4. Empirical formula = CH₄.",
        "h": "6.25:25 → 1:4 → CH₄.",
        "yr": "SS1"
      },
      {
        "q": "The molecular formula shows:",
        "o": ["The simplest ratio of atoms in a compound", "The actual number of each type of atom in a molecule", "The three-dimensional shape of a molecule", "The percentage composition of the compound"],
        "a": 1,
        "e": "The molecular formula shows the actual numbers of each type of atom in one molecule. For example, glucose (C₆H₁₂O₆) has 6 carbons, 12 hydrogens, and 6 oxygens per molecule.",
        "h": "Molecular formula = actual atom count per molecule.",
        "yr": "SS1"
      },
      {
        "q": "What is the percentage by mass of sulfur in H₂SO₄? (H=1, S=32, O=16)",
        "o": ["16.3%", "32.7%", "49.0%", "64.0%"],
        "a": 1,
        "e": "Molar mass of H₂SO₄ = 98 g/mol. % S = (32÷98) × 100 = 32.7%.",
        "h": "% S = (32÷98) × 100 = 32.7%.",
        "yr": "SS1"
      },
      {
        "q": "A compound with empirical formula NO₂ has molar mass 92 g/mol. Its molecular formula is:",
        "o": ["NO₂", "N₂O₄", "N₃O₆", "N₄O₈"],
        "a": 1,
        "e": "Empirical formula mass of NO₂ = 14 + 32 = 46 g/mol. n = 92÷46 = 2. Molecular formula = 2×NO₂ = N₂O₄.",
        "h": "n = 92÷46 = 2; N₂O₄.",
        "yr": "SS1"
      },
      {
        "q": "If the percentages of all elements in a compound do not add up to 100%, the remaining percentage likely represents:",
        "o": ["Impurities in the sample", "An element not yet identified in the formula", "Experimental error that should be ignored", "The percentage of empty space in the compound"],
        "a": 1,
        "e": "If given percentages don't add to 100%, the remainder is an unidentified element. For example, if 60% C + 8% H = 68%, the remaining 32% is likely oxygen (a common missing element).",
        "h": "Missing percentage = unidentified element, often oxygen.",
        "yr": "SS1"
      },
      {
        "q": "What is the percentage of hydrogen in H₂SO₄? (H=1, S=32, O=16; M=98)",
        "o": ["1.0%", "2.04%", "4.0%", "16.3%"],
        "a": 1,
        "e": "% H = (2÷98) × 100 = 2.04%.",
        "h": "% H = (2÷98) × 100 = 2.04%.",
        "yr": "SS1"
      },
      {
        "q": "The empirical and molecular formula are the same for:",
        "o": ["Glucose (C₆H₁₂O₆)", "Ethene (C₂H₄)", "Water (H₂O)", "Benzene (C₆H₆)"],
        "a": 2,
        "e": "Water (H₂O) has the same empirical and molecular formula. Its empirical formula is H₂O (ratio H:O = 2:1) and this is also its molecular formula since no simpler ratio exists.",
        "h": "H₂O: empirical = molecular = H₂O.",
        "yr": "SS1"
      },
      {
        "q": "A compound contains 52.2% C, 13.0% H, and 34.8% O. What is its empirical formula? (C=12, H=1, O=16)",
        "o": ["CHO", "CH₂O", "C₂H₆O", "CH₃O"],
        "a": 2,
        "e": "C: 52.2/12=4.35; H: 13.0/1=13.0; O: 34.8/16=2.175. Divide by 2.175: C=2, H=6, O=1 → C₂H₆O (ethanol).",
        "h": "4.35:13.0:2.175 ÷ 2.175 → 2:6:1 → C₂H₆O.",
        "yr": "SS1"
      },
      {
        "q": "What is the percentage of iron in Fe₂O₃? (Fe=56, O=16)",
        "o": ["30%", "50%", "70%", "80%"],
        "a": 2,
        "e": "Molar mass Fe₂O₃ = 2×56 + 3×16 = 112 + 48 = 160 g/mol. % Fe = (112÷160) × 100 = 70%.",
        "h": "% Fe = (112÷160) × 100 = 70%.",
        "yr": "SS1"
      },
      {
        "q": "When calculating empirical formula and the ratio comes out as 1:1.5, you should:",
        "o": ["Accept it as the empirical formula", "Divide by the largest number", "Multiply all values by 2 to get whole numbers", "Add 0.5 to the smaller number"],
        "a": 2,
        "e": "If ratios contain 0.5, multiply all values by 2 to get whole numbers. For example, 1:1.5 becomes 2:3. The empirical formula must contain only whole numbers.",
        "h": "Ratio with 0.5? Multiply all by 2 to get whole numbers.",
        "yr": "SS1"
      },
      {
        "q": "The percentage by mass of Na in NaCl is approximately: (Na=23, Cl=35.5)",
        "o": ["23%", "39.3%", "60.7%", "50%"],
        "a": 1,
        "e": "Molar mass NaCl = 58.5 g/mol. % Na = (23÷58.5) × 100 = 39.3%.",
        "h": "% Na = (23÷58.5) × 100 = 39.3%.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Gas Laws",
    "topicCode": "SS1-CHEM-13",
    "module": "Physical Chemistry",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The <span class=\"learn-keyword\">gas laws</span> describe the relationships between pressure (P), volume (V), and temperature (T) for a fixed amount of an ideal gas. These laws form the basis for understanding gas behaviour and are widely applied in industry, engineering, and chemistry.\n</div>\n\n<h3 class=\"learn-subheading\">1. Boyle's Law (Robert Boyle, 1662)</h3>\n<p class=\"learn-p\"><em>\"At constant temperature, the volume of a fixed mass of gas is inversely proportional to its pressure.\"</em></p>\n<p class=\"learn-p\"><strong>P₁V₁ = P₂V₂</strong> (at constant T and n)</p>\n<p class=\"learn-p\"><strong>Example:</strong> A gas occupies 5 dm³ at 100 kPa. What is its volume at 200 kPa (constant T)?<br>V₂ = P₁V₁ ÷ P₂ = (100 × 5) ÷ 200 = 2.5 dm³</p>\n\n<h3 class=\"learn-subheading\">2. Charles's Law (Jacques Charles, 1787)</h3>\n<p class=\"learn-p\"><em>\"At constant pressure, the volume of a fixed mass of gas is directly proportional to its absolute temperature (Kelvin).\"</em></p>\n<p class=\"learn-p\"><strong>V₁/T₁ = V₂/T₂</strong> (at constant P and n)</p>\n<p class=\"learn-p\">Note: Temperature must be in Kelvin: K = °C + 273</p>\n\n<h3 class=\"learn-subheading\">3. Ideal Gas Equation</h3>\n<p class=\"learn-p\">Combining Boyle's and Charles's Laws gives the <strong>Ideal Gas Equation:</strong></p>\n<p class=\"learn-p\"><strong>PV = nRT</strong></p>\n<p class=\"learn-p\">where P = pressure (Pa), V = volume (m³), n = moles, R = 8.314 J mol⁻¹ K⁻¹, T = temperature (K).</p>\n\n<h3 class=\"learn-subheading\">4. The Combined Gas Law</h3>\n<p class=\"learn-p\"><strong>P₁V₁/T₁ = P₂V₂/T₂</strong></p>\n<p class=\"learn-p\">Used when both pressure and temperature change simultaneously.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Always convert temperature to Kelvin (K = °C + 273) before using gas law equations. Boyle's: P₁V₁ = P₂V₂. Charles's: V₁/T₁ = V₂/T₂. Combined: P₁V₁/T₁ = P₂V₂/T₂. Ideal gas: PV = nRT.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Gas laws: Boyle's (P inversely proportional to V at constant T); Charles's (V directly proportional to T at constant P); Combined (P₁V₁/T₁ = P₂V₂/T₂); Ideal gas equation (PV = nRT).\n</div>\n  ",
    "questions": [
      {
        "q": "Boyle's Law states that at constant temperature, the volume of a fixed mass of gas is:",
        "o": ["Directly proportional to its pressure", "Inversely proportional to its pressure", "Equal to its pressure multiplied by temperature", "Independent of pressure"],
        "a": 1,
        "e": "Boyle's Law: at constant temperature, volume is inversely proportional to pressure. When pressure doubles, volume halves. P₁V₁ = P₂V₂.",
        "h": "Boyle's Law: V inversely proportional to P at constant T.",
        "yr": "SS1"
      },
      {
        "q": "The mathematical expression for Boyle's Law is:",
        "o": ["V₁/T₁ = V₂/T₂", "P₁V₁ = P₂V₂", "P₁/T₁ = P₂/T₂", "PV = nRT"],
        "a": 1,
        "e": "Boyle's Law is expressed as P₁V₁ = P₂V₂ at constant temperature and amount of gas.",
        "h": "Boyle's: P₁V₁ = P₂V₂.",
        "yr": "SS1"
      },
      {
        "q": "A gas occupies 5 dm³ at 100 kPa. At 200 kPa (constant T), its volume is:",
        "o": ["10 dm³", "5 dm³", "2.5 dm³", "1.25 dm³"],
        "a": 2,
        "e": "P₁V₁ = P₂V₂ → V₂ = P₁V₁ ÷ P₂ = (100 × 5) ÷ 200 = 2.5 dm³.",
        "h": "V₂ = (100 × 5) ÷ 200 = 2.5 dm³.",
        "yr": "SS1"
      },
      {
        "q": "Charles's Law states that at constant pressure, the volume of a fixed mass of gas is:",
        "o": ["Inversely proportional to temperature", "Directly proportional to absolute temperature", "Independent of temperature", "Equal to the square of temperature"],
        "a": 1,
        "e": "Charles's Law: at constant pressure, volume is directly proportional to absolute temperature (Kelvin). When T doubles, V doubles.",
        "h": "Charles's Law: V directly proportional to T (Kelvin) at constant P.",
        "yr": "SS1"
      },
      {
        "q": "In gas law calculations, temperature must be expressed in:",
        "o": ["Celsius (°C)", "Fahrenheit (°F)", "Kelvin (K)", "Any unit consistently"],
        "a": 2,
        "e": "Temperature must be in Kelvin for gas law calculations. Convert using K = °C + 273. Celsius cannot be used directly because gas laws require an absolute temperature scale.",
        "h": "Always use Kelvin: K = °C + 273.",
        "yr": "SS1"
      },
      {
        "q": "Convert 27°C to Kelvin:",
        "o": ["246 K", "273 K", "300 K", "327 K"],
        "a": 2,
        "e": "K = °C + 273 = 27 + 273 = 300 K.",
        "h": "K = 27 + 273 = 300 K.",
        "yr": "SS1"
      },
      {
        "q": "The expression for Charles's Law is:",
        "o": ["P₁V₁ = P₂V₂", "V₁/T₁ = V₂/T₂", "P₁/T₁ = P₂/T₂", "PV = nRT"],
        "a": 1,
        "e": "Charles's Law is V₁/T₁ = V₂/T₂ at constant pressure, where T must be in Kelvin.",
        "h": "Charles's: V₁/T₁ = V₂/T₂.",
        "yr": "SS1"
      },
      {
        "q": "The Ideal Gas Equation is:",
        "o": ["PV = nRT", "P₁V₁ = P₂V₂", "V₁/T₁ = V₂/T₂", "P₁V₁/T₁ = P₂V₂/T₂"],
        "a": 0,
        "e": "The Ideal Gas Equation is PV = nRT, where P = pressure, V = volume, n = moles, R = 8.314 J mol⁻¹ K⁻¹, and T = temperature in Kelvin.",
        "h": "PV = nRT — the ideal gas equation.",
        "yr": "SS1"
      },
      {
        "q": "The combined gas law is:",
        "o": ["P₁V₁ = P₂V₂", "V₁/T₁ = V₂/T₂", "P₁V₁/T₁ = P₂V₂/T₂", "PV = nRT"],
        "a": 2,
        "e": "The combined gas law P₁V₁/T₁ = P₂V₂/T₂ applies when both pressure and temperature change simultaneously.",
        "h": "Combined: P₁V₁/T₁ = P₂V₂/T₂.",
        "yr": "SS1"
      },
      {
        "q": "A gas occupies 4 dm³ at 300 K. At 600 K (constant pressure), its volume is:",
        "o": ["2 dm³", "4 dm³", "6 dm³", "8 dm³"],
        "a": 3,
        "e": "V₁/T₁ = V₂/T₂ → V₂ = V₁T₂/T₁ = (4 × 600) ÷ 300 = 8 dm³.",
        "h": "V₂ = 4 × 600 ÷ 300 = 8 dm³.",
        "yr": "SS1"
      },
      {
        "q": "According to Boyle's Law, if the pressure on a gas is tripled at constant temperature, the volume:",
        "o": ["Triples", "Doubles", "Becomes one-third", "Stays the same"],
        "a": 2,
        "e": "Boyle's Law: P and V are inversely proportional. If P triples, V becomes 1/3 of the original volume.",
        "h": "P triples → V becomes 1/3 (inversely proportional).",
        "yr": "SS1"
      },
      {
        "q": "The value of R in the ideal gas equation PV = nRT is:",
        "o": ["6.02 × 10²³ J mol⁻¹ K⁻¹", "8.314 J mol⁻¹ K⁻¹", "22.4 J mol⁻¹ K⁻¹", "0.0821 J mol⁻¹ K⁻¹"],
        "a": 1,
        "e": "R is the universal gas constant = 8.314 J mol⁻¹ K⁻¹ (or 8.314 Pa m³ mol⁻¹ K⁻¹). It appears in the ideal gas equation PV = nRT.",
        "h": "R = 8.314 J mol⁻¹ K⁻¹.",
        "yr": "SS1"
      },
      {
        "q": "Which scientist formulated the law describing the relationship between gas volume and pressure at constant temperature?",
        "o": ["Charles", "Avogadro", "Gay-Lussac", "Boyle"],
        "a": 3,
        "e": "Robert Boyle (1662) formulated the law relating gas volume and pressure at constant temperature. Hence the name Boyle's Law.",
        "h": "Boyle formulated the pressure-volume law.",
        "yr": "SS1"
      },
      {
        "q": "An ideal gas follows all gas laws perfectly. Real gases deviate from ideal behaviour under conditions of:",
        "o": ["Low pressure and high temperature", "High pressure and low temperature", "Standard temperature and pressure", "Any constant temperature"],
        "a": 1,
        "e": "Real gases deviate from ideal behaviour at high pressure (gas particles are close together) and low temperature (intermolecular attractions become significant). At low P and high T, real gases behave more ideally.",
        "h": "High P + low T = real gas deviates from ideal behaviour.",
        "yr": "SS1"
      },
      {
        "q": "A gas at 2 atm and 10 dm³ is compressed to 5 dm³ at constant temperature. The new pressure is:",
        "o": ["1 atm", "2 atm", "4 atm", "8 atm"],
        "a": 2,
        "e": "P₁V₁ = P₂V₂ → P₂ = P₁V₁ ÷ V₂ = (2 × 10) ÷ 5 = 4 atm.",
        "h": "P₂ = (2 × 10) ÷ 5 = 4 atm.",
        "yr": "SS1"
      },
      {
        "q": "Charles's Law was formulated by:",
        "o": ["Robert Boyle", "Jacques Charles", "Avogadro", "Gay-Lussac"],
        "a": 1,
        "e": "Jacques Charles (1787) proposed the law relating volume and absolute temperature at constant pressure.",
        "h": "Charles formulated the volume-temperature law.",
        "yr": "SS1"
      },
      {
        "q": "A gas occupies 3 dm³ at 27°C and 1 atm. At 127°C and 1 atm, its volume is:",
        "o": ["3 dm³", "4 dm³", "6 dm³", "1.5 dm³"],
        "a": 1,
        "e": "Convert: T₁ = 300K, T₂ = 400K. V₂ = V₁T₂/T₁ = 3 × 400/300 = 4 dm³.",
        "h": "V₂ = 3 × 400/300 = 4 dm³.",
        "yr": "SS1"
      },
      {
        "q": "The molar volume of an ideal gas at STP (0°C, 1 atm) is:",
        "o": ["11.2 dm³", "22.4 dm³", "44.8 dm³", "2.24 dm³"],
        "a": 1,
        "e": "At STP (0°C = 273 K, 1 atm), 1 mole of any ideal gas occupies 22.4 dm³. This follows from PV = nRT with the given values.",
        "h": "Molar volume at STP = 22.4 dm³/mol.",
        "yr": "SS1"
      },
      {
        "q": "In PV = nRT, the variable n represents:",
        "o": ["Number of atoms", "Number of molecules", "Number of moles of gas", "Number of collisions per second"],
        "a": 2,
        "e": "In the ideal gas equation PV = nRT, n represents the number of moles of the gas. It is calculated as n = PV ÷ RT.",
        "h": "n in PV = nRT = number of moles.",
        "yr": "SS1"
      },
      {
        "q": "At constant temperature and pressure, doubling the amount of gas doubles its volume. This is:",
        "o": ["Boyle's Law", "Charles's Law", "Gay-Lussac's Law", "Avogadro's Law"],
        "a": 3,
        "e": "Avogadro's Law states that at constant T and P, equal volumes of gases contain equal numbers of molecules. Equivalently, volume is proportional to the number of moles.",
        "h": "Volume ∝ moles at constant T and P = Avogadro's Law.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Structure of the Atom and Electronic Configuration",
    "topicCode": "SS1-CHEM-14",
    "module": "Atomic Theory",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Building on basic atomic structure, this topic explores <span class=\"learn-keyword\">atomic models</span>, <span class=\"learn-keyword\">electron shells and sub-shells</span>, and how electronic configuration determines an element's position in the Periodic Table and its chemical behaviour.\n</div>\n\n<h3 class=\"learn-subheading\">1. Development of Atomic Models</h3>\n<ul class=\"learn-list\">\n  <li><strong>Dalton (1803):</strong> Solid sphere model — atoms are indivisible solid spheres.</li>\n  <li><strong>Thomson (1904):</strong> Plum pudding model — electrons embedded in a positive sphere.</li>\n  <li><strong>Rutherford (1911):</strong> Nuclear model — small dense positive nucleus with electrons orbiting it.</li>\n  <li><strong>Bohr (1913):</strong> Planetary model — electrons orbit in fixed energy shells (energy levels).</li>\n  <li><strong>Modern quantum model:</strong> Electrons exist in orbitals (probability clouds) within sub-shells.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Rutherford's Gold Foil Experiment</h3>\n<p class=\"learn-p\">Alpha particles were fired at thin gold foil. Observations:</p>\n<ul class=\"learn-list\">\n  <li>Most particles passed straight through (atom is mostly empty space).</li>\n  <li>A few were deflected at large angles (positive nucleus exists).</li>\n  <li>Very few bounced straight back (nucleus is small and dense).</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Electronic Configuration Rules</h3>\n<ul class=\"learn-list\">\n  <li>Shell 1 (K): max 2 electrons</li>\n  <li>Shell 2 (L): max 8 electrons</li>\n  <li>Shell 3 (M): max 8 electrons (for first 20 elements)</li>\n  <li>Shell 4 (N): max 2 electrons (for first 20 elements)</li>\n</ul>\n<p class=\"learn-p\">Electrons fill from the innermost shell outwards.</p>\n\n<h3 class=\"learn-subheading\">4. Electronic Configuration and the Periodic Table</h3>\n<p class=\"learn-p\">The number of valence electrons equals the group number (main group). The number of occupied shells equals the period number.</p>\n\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Z</th><th>Config</th><th>Period</th><th>Group</th></tr></thead>\n    <tbody>\n      <tr><td>Na</td><td>11</td><td>2,8,1</td><td>3</td><td>I</td></tr>\n      <tr><td>Mg</td><td>12</td><td>2,8,2</td><td>3</td><td>II</td></tr>\n      <tr><td>Cl</td><td>17</td><td>2,8,7</td><td>3</td><td>VII</td></tr>\n      <tr><td>Ca</td><td>20</td><td>2,8,8,2</td><td>4</td><td>II</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Rutherford's experiment proved atoms have a small, dense, positive nucleus. Bohr added fixed energy shells. Configuration: 2,8,8,2 for Ca (Z=20). Group = valence electrons; Period = number of shells.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Atomic models evolved from Dalton → Thomson → Rutherford → Bohr → quantum model. Rutherford's gold foil experiment revealed a small dense nucleus. Electronic configuration fills shells 2,8,8,2 for the first 20 elements.\n</div>\n  ",
    "questions": [
      {
        "q": "Which scientist proposed the 'plum pudding' model of the atom?",
        "o": ["Dalton", "Thomson", "Rutherford", "Bohr"],
        "a": 1,
        "e": "J.J. Thomson proposed the plum pudding model (1904) in which electrons are embedded in a diffuse positive sphere, like plums in a pudding.",
        "h": "Thomson = plum pudding model.",
        "yr": "SS1"
      },
      {
        "q": "Rutherford's gold foil experiment showed that most alpha particles passed straight through because:",
        "o": ["Atoms are mostly empty space", "Gold has a very low density", "Alpha particles are too small to interact", "The electrons stopped all the particles"],
        "a": 0,
        "e": "Most alpha particles passed straight through the gold foil because atoms are mostly empty space. Only the tiny nucleus could deflect particles.",
        "h": "Mostly empty space → most particles pass through.",
        "yr": "SS1"
      },
      {
        "q": "What conclusion was drawn from the few alpha particles that bounced back in Rutherford's experiment?",
        "o": ["Atoms are hollow", "The nucleus is large and spread out", "The nucleus is small, dense, and positively charged", "Electrons are found in the nucleus"],
        "a": 2,
        "e": "The very few particles that bounced back indicated that they had hit a small, dense, positively charged nucleus. This disproved Thomson's model.",
        "h": "Back-scatter = small, dense, positive nucleus.",
        "yr": "SS1"
      },
      {
        "q": "In Bohr's atomic model, electrons:",
        "o": ["Are randomly distributed around the nucleus", "Orbit in fixed energy levels (shells)", "Are embedded in a positive sphere", "Move in random cloud orbitals"],
        "a": 1,
        "e": "Bohr (1913) proposed that electrons orbit the nucleus in fixed, discrete energy levels (shells). Electrons can jump between shells by absorbing or emitting energy.",
        "h": "Bohr = electrons in fixed energy shells.",
        "yr": "SS1"
      },
      {
        "q": "The electronic configuration of chlorine (Z=17) is:",
        "o": ["2, 8, 7", "2, 7, 8", "2, 8, 8, 1", "2, 15"],
        "a": 0,
        "e": "Chlorine has 17 electrons: Shell 1=2, Shell 2=8, Shell 3=7. Configuration: 2, 8, 7. Chlorine has 7 valence electrons.",
        "h": "17 electrons: 2+8+7 = 17.",
        "yr": "SS1"
      },
      {
        "q": "The number of valence electrons in an atom determines its:",
        "o": ["Mass number", "Number of neutrons", "Group number and chemical reactivity", "Period number"],
        "a": 2,
        "e": "The number of valence electrons determines both the group number in the Periodic Table and the chemical reactivity of the element.",
        "h": "Valence electrons → group number and reactivity.",
        "yr": "SS1"
      },
      {
        "q": "Dalton's model described atoms as:",
        "o": ["Electrons embedded in a positive sphere", "Indivisible solid spheres", "A dense nucleus surrounded by orbiting electrons", "Probability clouds"],
        "a": 1,
        "e": "Dalton (1803) described atoms as indivisible solid spheres — like tiny billiard balls. He could not yet account for subatomic particles.",
        "h": "Dalton = solid sphere model.",
        "yr": "SS1"
      },
      {
        "q": "The electronic configuration 2, 8, 8, 2 represents:",
        "o": ["Sodium (Z=11)", "Chlorine (Z=17)", "Potassium (Z=19)", "Calcium (Z=20)"],
        "a": 3,
        "e": "Configuration 2+8+8+2 = 20 electrons → Calcium (Z=20). Calcium is in Period 4, Group II.",
        "h": "2+8+8+2 = 20 → Calcium.",
        "yr": "SS1"
      },
      {
        "q": "Which atomic model was disproved by Rutherford's gold foil experiment?",
        "o": ["Dalton's solid sphere model", "Thomson's plum pudding model", "Bohr's planetary model", "The quantum model"],
        "a": 1,
        "e": "Rutherford's experiment disproved Thomson's plum pudding model, which predicted that positive charge was spread throughout the atom. The discovery of a concentrated nucleus proved otherwise.",
        "h": "Rutherford disproved Thomson's plum pudding model.",
        "yr": "SS1"
      },
      {
        "q": "An element has configuration 2, 8, 3. It belongs to:",
        "o": ["Period 2, Group III", "Period 3, Group III", "Period 3, Group V", "Period 8, Group 3"],
        "a": 1,
        "e": "Configuration 2, 8, 3: 3 shells = Period 3; 3 valence electrons = Group III. This is aluminium (Z=13).",
        "h": "3 shells = Period 3; 3 valence electrons = Group III.",
        "yr": "SS1"
      },
      {
        "q": "The maximum number of electrons in the third shell (M) for the first 20 elements is:",
        "o": ["2", "8", "18", "32"],
        "a": 1,
        "e": "For the first 20 elements, the third shell fills to a maximum of 8 electrons before the fourth shell begins to fill. (The full theoretical maximum of 18 is not reached for elements 1–20.)",
        "h": "Shell 3 max for first 20 elements = 8.",
        "yr": "SS1"
      },
      {
        "q": "The element with configuration 2, 8, 6 has how many valence electrons?",
        "o": ["2", "6", "8", "16"],
        "a": 1,
        "e": "The outermost shell has 6 electrons → 6 valence electrons. This element is sulfur (Z=16), in Group VI.",
        "h": "The last number in the config = valence electrons = 6.",
        "yr": "SS1"
      },
      {
        "q": "Rutherford proposed the nuclear model based on the observation that:",
        "o": ["All alpha particles bounced back from the foil", "Most alpha particles passed through but a few were deflected", "All alpha particles were absorbed by the foil", "Electrons were emitted from the gold foil"],
        "a": 1,
        "e": "Rutherford observed that most particles passed through (empty space) but a few were deflected at large angles and very few bounced back — indicating a small, dense, positive nucleus.",
        "h": "Most pass through + few deflected = small dense nucleus.",
        "yr": "SS1"
      },
      {
        "q": "An element has atomic number 19 (potassium). Its electronic configuration is:",
        "o": ["2, 8, 9", "2, 8, 8, 1", "2, 17", "2, 8, 7, 2"],
        "a": 1,
        "e": "Potassium (Z=19): Shell 1=2, Shell 2=8, Shell 3=8, Shell 4=1. Configuration: 2, 8, 8, 1.",
        "h": "19 electrons: 2+8+8+1 = 19.",
        "yr": "SS1"
      },
      {
        "q": "In the modern quantum model, electrons exist in:",
        "o": ["Fixed circular orbits", "The nucleus alongside protons", "Orbitals — regions of probability around the nucleus", "Random positions throughout the atom"],
        "a": 2,
        "e": "The modern quantum model describes electrons as existing in orbitals — three-dimensional regions of space where there is a high probability of finding an electron.",
        "h": "Modern model: electrons in probability orbitals.",
        "yr": "SS1"
      },
      {
        "q": "Which period of the Periodic Table do elements with 2 electron shells belong to?",
        "o": ["Period 1", "Period 2", "Period 3", "Period 4"],
        "a": 1,
        "e": "Elements with 2 electron shells belong to Period 2 (e.g., Li to Ne, Z=3 to 10). Period number = number of occupied electron shells.",
        "h": "2 shells = Period 2.",
        "yr": "SS1"
      },
      {
        "q": "The element with configuration 2, 8, 5 is in which group?",
        "o": ["Group III", "Group V", "Group VII", "Group II"],
        "a": 1,
        "e": "Configuration 2, 8, 5 has 5 valence electrons → Group V. This is phosphorus (Z=15).",
        "h": "5 valence electrons = Group V.",
        "yr": "SS1"
      },
      {
        "q": "Who conducted the gold foil (alpha particle scattering) experiment?",
        "o": ["Dalton", "Thomson", "Rutherford", "Bohr"],
        "a": 2,
        "e": "Ernest Rutherford (1911) conducted the famous gold foil experiment, firing alpha particles at thin gold foil to investigate atomic structure.",
        "h": "Rutherford conducted the gold foil experiment.",
        "yr": "SS1"
      },
      {
        "q": "An element has 3 occupied electron shells and 2 valence electrons. Its position in the Periodic Table is:",
        "o": ["Period 2, Group III", "Period 3, Group II", "Period 2, Group II", "Period 3, Group III"],
        "a": 1,
        "e": "3 shells = Period 3; 2 valence electrons = Group II. This is magnesium (Z=12).",
        "h": "3 shells = Period 3; 2 valence e⁻ = Group II → Mg.",
        "yr": "SS1"
      },
      {
        "q": "Bohr's contribution over Rutherford's model was to explain:",
        "o": ["The existence of the nucleus", "Why atoms are mostly empty space", "Why electrons in fixed shells don't spiral into the nucleus", "The mass of the neutron"],
        "a": 2,
        "e": "Bohr proposed that electrons occupy fixed energy levels. In these levels they do not radiate energy and spiral in — explaining the stability of the atom that Rutherford's model could not account for.",
        "h": "Bohr: fixed energy shells explain why electrons don't spiral inward.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Ionic and Covalent Bonding",
    "topicCode": "SS1-CHEM-15",
    "module": "Chemical Bonding",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Chemical bonds form when atoms interact to achieve a more stable electron configuration — typically a full outer shell of 8 electrons (<span class=\"learn-keyword\">octet rule</span>). The two main types of bonding are <span class=\"learn-keyword\">ionic bonding</span> (electron transfer) and <span class=\"learn-keyword\">covalent bonding</span> (electron sharing).\n</div>\n\n<h3 class=\"learn-subheading\">1. Ionic Bonding</h3>\n<p class=\"learn-p\">Ionic bonds form by the <strong>transfer of electrons</strong> from a metal to a non-metal. The metal loses electrons to form a positive ion (cation); the non-metal gains electrons to form a negative ion (anion). The oppositely charged ions attract each other electrostatically.</p>\n<p class=\"learn-p\"><strong>Example:</strong> NaCl — Na loses 1e⁻ → Na⁺; Cl gains 1e⁻ → Cl⁻. The Na⁺ and Cl⁻ ions attract to form the ionic lattice.</p>\n\n<h3 class=\"learn-subheading\">2. Properties of Ionic Compounds</h3>\n<ul class=\"learn-list\">\n  <li>High melting and boiling points (strong electrostatic forces)</li>\n  <li>Conduct electricity when molten or dissolved in water (ions are free to move)</li>\n  <li>Do not conduct electricity when solid (ions fixed in lattice)</li>\n  <li>Usually soluble in water; insoluble in organic solvents</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Covalent Bonding</h3>\n<p class=\"learn-p\">Covalent bonds form by the <strong>sharing of electrons</strong> between non-metals. Each shared pair of electrons forms one covalent bond.</p>\n<ul class=\"learn-list\">\n  <li><strong>Single bond:</strong> 1 shared pair (e.g., H–H, H–Cl)</li>\n  <li><strong>Double bond:</strong> 2 shared pairs (e.g., O=O, C=O)</li>\n  <li><strong>Triple bond:</strong> 3 shared pairs (e.g., N≡N)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Properties of Covalent Compounds</h3>\n<ul class=\"learn-list\">\n  <li>Generally low melting and boiling points (weak intermolecular forces)</li>\n  <li>Do not conduct electricity (no free ions or electrons)</li>\n  <li>Usually soluble in organic solvents; insoluble in water (non-polar)</li>\n  <li>Can be gases, liquids, or solids at room temperature</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Ionic = metal + non-metal = electron transfer. Covalent = non-metal + non-metal = electron sharing. Ionic compounds conduct when molten/dissolved; covalent compounds generally do not. High mp/bp = ionic; low mp/bp = simple covalent.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Ionic bonds: electron transfer; metal→cation, non-metal→anion; high mp/bp; conduct when molten. Covalent bonds: electron sharing between non-metals; single/double/triple bonds; low mp/bp; do not conduct electricity.\n</div>\n  ",
    "questions": [
      {
        "q": "Ionic bonds are formed by:",
        "o": ["Sharing of electrons between two non-metals", "Transfer of electrons from a metal to a non-metal", "Sharing of protons between atoms", "Transfer of neutrons between atoms"],
        "a": 1,
        "e": "Ionic bonds form when electrons are transferred from a metal atom to a non-metal atom. The metal becomes a positive cation; the non-metal becomes a negative anion.",
        "h": "Ionic bond = electron transfer from metal to non-metal.",
        "yr": "SS1"
      },
      {
        "q": "In the formation of NaCl, sodium:",
        "o": ["Gains one electron to become Na⁻", "Loses one electron to become Na⁺", "Shares one electron with chlorine", "Gains one proton to become Na⁺"],
        "a": 1,
        "e": "Sodium (2,8,1) loses its one valence electron to chlorine, becoming Na⁺ (2,8) — a stable configuration. Chlorine gains it to become Cl⁻ (2,8,8).",
        "h": "Na loses 1e⁻ → Na⁺.",
        "yr": "SS1"
      },
      {
        "q": "Covalent bonds are formed by:",
        "o": ["Transfer of electrons from metal to non-metal", "Sharing of electrons between non-metal atoms", "Attraction between oppositely charged ions", "Transfer of protons between atoms"],
        "a": 1,
        "e": "Covalent bonds form when two non-metal atoms share one or more pairs of electrons, each atom contributing one electron to each shared pair.",
        "h": "Covalent bond = electron sharing between non-metals.",
        "yr": "SS1"
      },
      {
        "q": "Why do ionic compounds conduct electricity when dissolved in water?",
        "o": ["Water provides electrons for conduction", "The dissolved ions are free to move and carry charge", "The compound decomposes to release electrons", "Ionic compounds always conduct electricity in all states"],
        "a": 1,
        "e": "When ionic compounds dissolve in water, the lattice breaks down and ions become free to move through the solution. These mobile ions carry electrical charge, enabling conduction.",
        "h": "Dissolved ions are free to move → conduct electricity.",
        "yr": "SS1"
      },
      {
        "q": "A double covalent bond consists of:",
        "o": ["1 shared pair of electrons", "2 shared pairs of electrons", "3 shared pairs of electrons", "4 shared electrons from one atom only"],
        "a": 1,
        "e": "A double covalent bond consists of 2 shared pairs of electrons (4 electrons total). Examples: O₂ (O=O) and CO₂ (O=C=O).",
        "h": "Double bond = 2 shared pairs of electrons.",
        "yr": "SS1"
      },
      {
        "q": "Why do ionic compounds have high melting and boiling points?",
        "o": ["They contain covalent bonds that are very strong", "Strong electrostatic forces between oppositely charged ions require much energy to overcome", "They have very large molecular masses", "They are always found as gases"],
        "a": 1,
        "e": "Ionic compounds form giant lattice structures with strong electrostatic attractions between oppositely charged ions throughout. Large amounts of energy are needed to separate the ions.",
        "h": "Strong electrostatic forces in ionic lattice = high mp/bp.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a covalent compound?",
        "o": ["NaCl", "MgO", "CaCl₂", "H₂O"],
        "a": 3,
        "e": "Water (H₂O) is a covalent compound — oxygen and hydrogen are both non-metals and share electrons. NaCl, MgO, and CaCl₂ are all ionic compounds.",
        "h": "H₂O = two non-metals sharing electrons = covalent.",
        "yr": "SS1"
      },
      {
        "q": "The nitrogen molecule (N₂) has a triple bond because:",
        "o": ["Each nitrogen has 3 valence electrons", "Each nitrogen shares 3 electrons to complete its octet", "Nitrogen is in Group III", "Triple bonds are the most common in diatomic molecules"],
        "a": 1,
        "e": "Each nitrogen atom has 5 valence electrons and needs 3 more to complete its octet. By sharing 3 pairs of electrons, both atoms achieve a full outer shell. This forms a triple bond (N≡N).",
        "h": "N needs 3 more electrons → shares 3 pairs = triple bond.",
        "yr": "SS1"
      },
      {
        "q": "Solid ionic compounds do not conduct electricity because:",
        "o": ["They contain no charged particles", "The ions are fixed in the lattice and cannot move", "They decompose before melting", "They are too dense for electricity to pass through"],
        "a": 1,
        "e": "In the solid state, ionic compounds have ions locked in a rigid lattice structure. Without mobile charge carriers, electrical conduction is impossible.",
        "h": "Solid lattice = ions fixed = no conduction.",
        "yr": "SS1"
      },
      {
        "q": "The octet rule states that atoms tend to form bonds to achieve:",
        "o": ["2 electrons in their outermost shell", "A full outer shell of 8 electrons", "Equal numbers of protons and electrons", "The same configuration as hydrogen"],
        "a": 1,
        "e": "The octet rule states that atoms tend to bond in ways that give them 8 electrons in their outermost shell — like the noble gases. (Exception: hydrogen follows the duet rule — 2 electrons.)",
        "h": "Octet rule = 8 electrons in outer shell (like noble gases).",
        "yr": "SS1"
      },
      {
        "q": "Which pair of elements is most likely to form an ionic bond?",
        "o": ["C and H", "N and O", "Na and Cl", "Cl and Br"],
        "a": 2,
        "e": "Na (a metal) and Cl (a non-metal) form an ionic bond. Na loses its 1 valence electron to Cl, forming Na⁺ and Cl⁻ ions that attract electrostatically.",
        "h": "Metal + non-metal = ionic bond. Na and Cl.",
        "yr": "SS1"
      },
      {
        "q": "Simple covalent compounds generally have low melting and boiling points because:",
        "o": ["Their covalent bonds are very weak", "The weak intermolecular forces between molecules require little energy to overcome", "They have small molar masses", "They contain no charged particles"],
        "a": 1,
        "e": "In simple covalent compounds, the molecules are held together by weak intermolecular forces (van der Waals). These weak forces require little energy to overcome — hence low mp/bp.",
        "h": "Weak intermolecular forces → low mp/bp.",
        "yr": "SS1"
      },
      {
        "q": "Magnesium oxide (MgO) is an ionic compound. In its formation, magnesium:",
        "o": ["Gains 2 electrons", "Loses 2 electrons to become Mg²⁺", "Shares 2 electrons with oxygen", "Loses 1 electron to become Mg⁺"],
        "a": 1,
        "e": "Magnesium (2,8,2) loses its 2 valence electrons to become Mg²⁺ (2,8). Oxygen gains 2 electrons to become O²⁻ (2,8). The two ions attract to form MgO.",
        "h": "Mg loses 2e⁻ → Mg²⁺.",
        "yr": "SS1"
      },
      {
        "q": "In a water molecule (H₂O), each hydrogen forms:",
        "o": ["An ionic bond with oxygen", "A single covalent bond with oxygen", "A double covalent bond with oxygen", "No bond at all"],
        "a": 1,
        "e": "In H₂O, each hydrogen shares 1 pair of electrons with oxygen, forming a single covalent bond. The molecule has two O–H single bonds.",
        "h": "H₂O: each H forms one single covalent bond with O.",
        "yr": "SS1"
      },
      {
        "q": "An ionic compound is likely to be soluble in:",
        "o": ["Hexane", "Petrol", "Water", "Benzene"],
        "a": 2,
        "e": "Ionic compounds are generally soluble in polar solvents like water, which can surround and stabilise the individual ions. They are insoluble in non-polar organic solvents like hexane or benzene.",
        "h": "Ionic compounds dissolve in polar solvents like water.",
        "yr": "SS1"
      },
      {
        "q": "The bond in a hydrogen molecule (H₂) is:",
        "o": ["Ionic — hydrogen transfers an electron", "Covalent — two hydrogen atoms share one pair of electrons", "Metallic — electrons are delocalised", "Hydrogen bonding"],
        "a": 1,
        "e": "In H₂, two hydrogen atoms each contribute 1 electron to form a single covalent bond (one shared pair). Both atoms then have 2 electrons — completing the duet rule.",
        "h": "H₂: each H shares 1e⁻ = single covalent bond.",
        "yr": "SS1"
      },
      {
        "q": "The ion formed when calcium (Z=20, config 2,8,8,2) bonds ionically has the configuration:",
        "o": ["2,8,8,2", "2,8,8", "2,8,10", "2,8,8,4"],
        "a": 1,
        "e": "Calcium loses its 2 valence electrons to form Ca²⁺, leaving configuration 2,8,8. This is the stable configuration of argon.",
        "h": "Ca²⁺ loses 2 outer electrons → 2,8,8.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following has a triple covalent bond?",
        "o": ["H₂", "O₂", "N₂", "Cl₂"],
        "a": 2,
        "e": "N₂ has a triple covalent bond (N≡N). Each nitrogen shares 3 pairs of electrons (6 electrons total) to complete its octet.",
        "h": "N₂ has a triple bond (N≡N).",
        "yr": "SS1"
      },
      {
        "q": "Covalent compounds generally do not conduct electricity because:",
        "o": ["They have very high melting points", "They contain no free ions or free electrons to carry charge", "They dissolve readily in water", "They are all gases at room temperature"],
        "a": 1,
        "e": "Covalent compounds consist of neutral molecules with no free ions or delocalised electrons. Without mobile charge carriers, they cannot conduct electricity.",
        "h": "No free ions or electrons → no electrical conduction.",
        "yr": "SS1"
      },
      {
        "q": "The force that holds ionic compounds together in their lattice is:",
        "o": ["Sharing of electron pairs", "Covalent bonds between ions", "Electrostatic attraction between oppositely charged ions", "Metallic bonds"],
        "a": 2,
        "e": "In an ionic lattice, the ions are held together by strong electrostatic (Coulombic) attraction between the oppositely charged cations and anions arranged in a regular 3D pattern.",
        "h": "Ionic lattice held by electrostatic attraction between + and − ions.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Oxidation and Reduction",
    "topicCode": "SS1-CHEM-16",
    "module": "Inorganic Chemistry",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Oxidation</span> and <span class=\"learn-keyword\">reduction</span> are complementary processes that always occur together — collectively called <span class=\"learn-keyword\">redox reactions</span>. Understanding these processes is fundamental to electrochemistry, corrosion, respiration, and industrial processes.\n</div>\n\n<h3 class=\"learn-subheading\">1. Definitions of Oxidation and Reduction</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Concept</th><th>Old definition (oxygen)</th><th>Modern definition (electrons)</th></tr></thead>\n    <tbody>\n      <tr><td>Oxidation</td><td>Gain of oxygen / loss of hydrogen</td><td>Loss of electrons (OIL)</td></tr>\n      <tr><td>Reduction</td><td>Loss of oxygen / gain of hydrogen</td><td>Gain of electrons (RIG)</td></tr>\n    </tbody>\n  </table>\n</div>\n<p class=\"learn-p\"><strong>Memory aid: OIL RIG</strong> — Oxidation Is Loss (of electrons); Reduction Is Gain (of electrons).</p>\n\n<h3 class=\"learn-subheading\">2. Oxidation States (Oxidation Numbers)</h3>\n<p class=\"learn-p\">Rules for assigning oxidation states:</p>\n<ul class=\"learn-list\">\n  <li>Free elements: oxidation state = 0 (e.g., Fe, O₂, Cl₂)</li>\n  <li>Simple monoatomic ions: oxidation state = charge (e.g., Na⁺ = +1, Cl⁻ = −1)</li>\n  <li>Oxygen: usually −2 (except in peroxides: −1)</li>\n  <li>Hydrogen: usually +1 (except in metal hydrides: −1)</li>\n  <li>Sum of oxidation states in a neutral compound = 0</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Oxidising and Reducing Agents</h3>\n<ul class=\"learn-list\">\n  <li><strong>Oxidising agent:</strong> Causes another substance to be oxidised; itself is reduced (gains electrons). Examples: Cl₂, O₂, H₂O₂, KMnO₄.</li>\n  <li><strong>Reducing agent:</strong> Causes another substance to be reduced; itself is oxidised (loses electrons). Examples: H₂, CO, C, Zn.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Examples of Redox Reactions</h3>\n<p class=\"learn-p\">Zn + CuSO₄ → ZnSO₄ + Cu</p>\n<p class=\"learn-p\">Zn is oxidised (loses 2e⁻ → Zn²⁺); Cu²⁺ is reduced (gains 2e⁻ → Cu).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> OIL RIG — Oxidation Is Loss of electrons; Reduction Is Gain of electrons. The oxidising agent gets reduced; the reducing agent gets oxidised. Oxygen is almost always −2; hydrogen almost always +1.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Oxidation = loss of electrons (or gain of oxygen). Reduction = gain of electrons (or loss of oxygen). They always occur together (redox). OIL RIG. Oxidising agents gain electrons (are reduced); reducing agents lose electrons (are oxidised).\n</div>\n  ",
    "questions": [
      {
        "q": "The mnemonic OIL RIG stands for:",
        "o": ["Oxidation Is Loss; Reduction Is Gain (of electrons)", "Oxidation Is Loss; Reduction Is Gain (of oxygen)", "Only In Labs; Reactions In Gas", "Oxygen Increases Loss; Reduction Involves Gain"],
        "a": 0,
        "e": "OIL RIG = Oxidation Is Loss of electrons; Reduction Is Gain of electrons. This is the modern electron-based definition of redox.",
        "h": "OIL RIG = Oxidation Is Loss; Reduction Is Gain (of electrons).",
        "yr": "SS1"
      },
      {
        "q": "Oxidation in terms of electrons means:",
        "o": ["Gaining electrons", "Losing electrons", "Gaining oxygen atoms", "Losing hydrogen atoms"],
        "a": 1,
        "e": "In the modern electron-transfer definition, oxidation is the loss of electrons. A species is oxidised when it loses one or more electrons.",
        "h": "Oxidation = loss of electrons (OIL).",
        "yr": "SS1"
      },
      {
        "q": "Reduction in terms of electrons means:",
        "o": ["Losing electrons", "Gaining protons", "Gaining electrons", "Losing oxygen"],
        "a": 2,
        "e": "Reduction is the gain of electrons. A species is reduced when it gains one or more electrons.",
        "h": "Reduction = gain of electrons (RIG).",
        "yr": "SS1"
      },
      {
        "q": "In the reaction Zn + CuSO₄ → ZnSO₄ + Cu, zinc is:",
        "o": ["Reduced (gains electrons)", "Oxidised (loses electrons)", "Neither oxidised nor reduced", "Acting as the oxidising agent"],
        "a": 1,
        "e": "Zinc loses 2 electrons to become Zn²⁺ — it is oxidised. Simultaneously, Cu²⁺ gains 2 electrons to become Cu — it is reduced. Zinc is the reducing agent.",
        "h": "Zn → Zn²⁺ (loses 2e⁻) = oxidised.",
        "yr": "SS1"
      },
      {
        "q": "In the reaction Zn + CuSO₄ → ZnSO₄ + Cu, copper is:",
        "o": ["Oxidised", "Reduced (Cu²⁺ gains electrons to form Cu)", "Neither oxidised nor reduced", "Acting as the reducing agent"],
        "a": 1,
        "e": "Cu²⁺ gains 2 electrons to become neutral Cu metal — it is reduced. Cu²⁺ is the oxidising agent.",
        "h": "Cu²⁺ → Cu (gains 2e⁻) = reduced.",
        "yr": "SS1"
      },
      {
        "q": "An oxidising agent is a substance that:",
        "o": ["Loses electrons and is oxidised", "Gains electrons and is reduced", "Does not change oxidation state", "Only reacts with acids"],
        "a": 1,
        "e": "An oxidising agent causes another substance to be oxidised by accepting electrons from it. In doing so, the oxidising agent itself gains electrons and is reduced.",
        "h": "Oxidising agent gains electrons (is itself reduced).",
        "yr": "SS1"
      },
      {
        "q": "A reducing agent is a substance that:",
        "o": ["Gains electrons and is reduced", "Loses electrons and is oxidised", "Reduces the temperature of a reaction", "Only reacts with oxygen"],
        "a": 1,
        "e": "A reducing agent donates electrons to another substance (reducing it). In doing so, the reducing agent itself loses electrons and is oxidised.",
        "h": "Reducing agent loses electrons (is itself oxidised).",
        "yr": "SS1"
      },
      {
        "q": "The oxidation state of oxygen in most compounds is:",
        "o": ["+2", "+1", "0", "−2"],
        "a": 3,
        "e": "Oxygen almost always has an oxidation state of −2 in compounds. Exceptions include peroxides (e.g., H₂O₂) where it is −1, and OF₂ where it is +2.",
        "h": "Oxygen in compounds = −2 (except peroxides = −1).",
        "yr": "SS1"
      },
      {
        "q": "The oxidation state of hydrogen in most compounds is:",
        "o": ["+1", "−1", "0", "+2"],
        "a": 0,
        "e": "Hydrogen is almost always +1 in compounds. The exception is metal hydrides (e.g., NaH) where hydrogen is −1.",
        "h": "Hydrogen in most compounds = +1 (−1 in metal hydrides).",
        "yr": "SS1"
      },
      {
        "q": "What is the oxidation state of sulfur in H₂SO₄?",
        "o": ["+2", "+4", "+6", "+8"],
        "a": 2,
        "e": "In H₂SO₄: H = +1 (×2 = +2); O = −2 (×4 = −8). Sum = 0. So S + 2 − 8 = 0 → S = +6.",
        "h": "H₂SO₄: 2(+1) + S + 4(−2) = 0 → S = +6.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a common oxidising agent?",
        "o": ["Carbon (C)", "Hydrogen (H₂)", "Zinc (Zn)", "Chlorine (Cl₂)"],
        "a": 3,
        "e": "Chlorine (Cl₂) is a powerful oxidising agent. It readily accepts electrons from other substances, becoming Cl⁻. Other common oxidising agents include O₂, H₂O₂, and KMnO₄.",
        "h": "Cl₂ is a common oxidising agent.",
        "yr": "SS1"
      },
      {
        "q": "Rusting of iron is an example of:",
        "o": ["Reduction of iron", "Oxidation of iron", "Neither oxidation nor reduction", "A reversible physical change"],
        "a": 1,
        "e": "Rusting is the oxidation of iron. Iron (Fe) loses electrons to form Fe²⁺ or Fe³⁺, which combines with oxygen and water to form rust (hydrated iron oxide, Fe₂O₃·nH₂O).",
        "h": "Rusting = iron being oxidised.",
        "yr": "SS1"
      },
      {
        "q": "What is the oxidation state of nitrogen in NO₂?",
        "o": ["+2", "+3", "+4", "+5"],
        "a": 2,
        "e": "In NO₂: O = −2 (×2 = −4). Sum = 0. N + (−4) = 0 → N = +4.",
        "h": "NO₂: N + 2(−2) = 0 → N = +4.",
        "yr": "SS1"
      },
      {
        "q": "In the reaction 2Mg + O₂ → 2MgO, magnesium is:",
        "o": ["Reduced because it gains oxygen", "Oxidised because it gains oxygen", "Oxidised because it loses electrons to oxygen", "Reduced because it loses electrons"],
        "a": 2,
        "e": "Mg (0) → Mg²⁺ (+2) — it loses 2 electrons per atom to oxygen. This is oxidation. Oxygen gains electrons (0 → −2) — reduction.",
        "h": "Mg loses electrons to O → Mg is oxidised.",
        "yr": "SS1"
      },
      {
        "q": "The oxidation state of a free element (e.g., Na, Cl₂, Fe) is always:",
        "o": ["+1", "−1", "0", "+2"],
        "a": 2,
        "e": "Free elements (in their uncombined, elemental state) always have an oxidation state of 0, regardless of the element.",
        "h": "Free elements always have oxidation state = 0.",
        "yr": "SS1"
      },
      {
        "q": "Which statement best describes a redox reaction?",
        "o": ["Only oxidation occurs", "Only reduction occurs", "Oxidation and reduction occur simultaneously", "No change in oxidation state occurs"],
        "a": 2,
        "e": "In a redox reaction, oxidation and reduction always occur simultaneously. When one species loses electrons (oxidised), another species must gain those electrons (reduced).",
        "h": "Redox = oxidation AND reduction occurring together.",
        "yr": "SS1"
      },
      {
        "q": "What is the oxidation state of Cl in HCl?",
        "o": ["+1", "0", "−1", "−2"],
        "a": 2,
        "e": "In HCl: H = +1. For the compound to be neutral: +1 + Cl = 0 → Cl = −1.",
        "h": "HCl: H = +1, so Cl = −1.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a good reducing agent?",
        "o": ["KMnO₄", "Cl₂", "O₂", "Carbon (C)"],
        "a": 3,
        "e": "Carbon is a good reducing agent, especially at high temperatures. It donates electrons (is oxidised to CO or CO₂) while reducing metal oxides to extract metals.",
        "h": "Carbon (C) is a common reducing agent.",
        "yr": "SS1"
      },
      {
        "q": "When iron rusts: Fe → Fe³⁺ + 3e⁻. Iron has been:",
        "o": ["Reduced", "Oxidised", "Neutralised", "Hydrated"],
        "a": 1,
        "e": "Fe → Fe³⁺ + 3e⁻. Iron loses 3 electrons — by the definition OIL, iron is oxidised. Its oxidation state changes from 0 to +3.",
        "h": "Loses electrons = oxidised. Fe → Fe³⁺.",
        "yr": "SS1"
      },
      {
        "q": "What is the oxidation state of Mn in KMnO₄? (K=+1, O=−2)",
        "o": ["+4", "+5", "+6", "+7"],
        "a": 3,
        "e": "In KMnO₄: K=+1, O=−2(×4=−8). Sum = 0: +1 + Mn + (−8) = 0 → Mn = +7.",
        "h": "KMnO₄: +1 + Mn − 8 = 0 → Mn = +7.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Water and Solution",
    "topicCode": "SS1-CHEM-17",
    "module": "Inorganic Chemistry",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Water is one of the most important and abundant substances on Earth. It is called the <span class=\"learn-keyword\">universal solvent</span> due to its remarkable ability to dissolve a wide variety of substances. Understanding the properties of water and solutions is fundamental to chemistry, biology, and environmental science.\n</div>\n\n<h3 class=\"learn-subheading\">1. Properties of Water</h3>\n<ul class=\"learn-list\">\n  <li><strong>Physical:</strong> Colourless, odourless, tasteless liquid at room temperature. Boiling point = 100°C; melting point = 0°C at 1 atm.</li>\n  <li><strong>Polarity:</strong> Water is a polar molecule (bent shape, O is δ⁻, H is δ⁺). This polarity gives it unique properties.</li>\n  <li><strong>High specific heat capacity:</strong> Water can absorb large amounts of heat without a large temperature change.</li>\n  <li><strong>High surface tension:</strong> Due to hydrogen bonding between water molecules.</li>\n  <li><strong>Density anomaly:</strong> Ice (solid) is less dense than liquid water — ice floats.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Water as a Solvent</h3>\n<p class=\"learn-p\">Water dissolves substances by surrounding solute particles with water molecules (hydration). It dissolves ionic compounds and polar covalent compounds but not non-polar substances.</p>\n\n<h3 class=\"learn-subheading\">3. Solutions — Key Terms</h3>\n<ul class=\"learn-list\">\n  <li><strong>Solution:</strong> A homogeneous mixture of solute dissolved in solvent.</li>\n  <li><strong>Solute:</strong> The substance dissolved.</li>\n  <li><strong>Solvent:</strong> The substance doing the dissolving.</li>\n  <li><strong>Concentration:</strong> Amount of solute per unit volume of solution (mol/dm³ or g/dm³).</li>\n  <li><strong>Saturated solution:</strong> A solution containing the maximum amount of dissolved solute at a given temperature.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Hard and Soft Water</h3>\n<p class=\"learn-p\"><strong>Hard water</strong> contains dissolved Ca²⁺ and Mg²⁺ ions (from limestone rocks) that prevent soap from lathering easily. It can be temporary (removed by boiling) or permanent (removed by ion exchange or adding Na₂CO₃).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Water is polar → it dissolves ionic and polar compounds (\"like dissolves like\"). Hard water: Ca²⁺ and Mg²⁺ ions. Temporary hardness caused by Ca(HCO₃)₂ — removed by boiling. Permanent hardness (CaSO₄) — not removed by boiling; use ion exchange or Na₂CO₃.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Water is a polar molecule with high specific heat capacity and hydrogen bonding. It is the universal solvent dissolving ionic and polar substances. Hard water contains Ca²⁺/Mg²⁺ ions causing soap wastage. Concentration = moles ÷ volume.\n</div>\n  ",
    "questions": [
      {
        "q": "Water is called the 'universal solvent' because:",
        "o": ["It dissolves all substances without exception", "It dissolves a wider variety of substances than most other solvents", "It evaporates very slowly", "It has a very high boiling point"],
        "a": 1,
        "e": "Water is called the universal solvent because it dissolves more substances than any other common solvent. This is due to its polarity and ability to form hydrogen bonds with solute molecules.",
        "h": "Universal solvent = dissolves more substances than any other solvent.",
        "yr": "SS1"
      },
      {
        "q": "The boiling point of pure water at standard pressure is:",
        "o": ["0°C", "50°C", "100°C", "373°C"],
        "a": 2,
        "e": "Pure water boils at exactly 100°C (373 K) at standard atmospheric pressure (1 atm). This is a fixed physical property used to verify water purity.",
        "h": "Pure water boils at 100°C at standard pressure.",
        "yr": "SS1"
      },
      {
        "q": "Why is water a polar molecule?",
        "o": ["It has an equal distribution of electrons", "The O−H bonds are non-polar", "Oxygen is more electronegative, creating a partial negative charge at O and partial positive at H", "Hydrogen and oxygen have the same electronegativity"],
        "a": 2,
        "e": "Oxygen is more electronegative than hydrogen, pulling electron density towards itself. This creates a partial negative charge (δ⁻) on O and partial positive charges (δ⁺) on the H atoms, making water a polar molecule.",
        "h": "O is more electronegative → δ⁻ on O, δ⁺ on H → polar molecule.",
        "yr": "SS1"
      },
      {
        "q": "Ice floats on water because:",
        "o": ["Ice has a higher density than liquid water", "Ice has a lower density than liquid water due to its open hydrogen bond structure", "Ice repels liquid water electrostatically", "Ice is lighter due to loss of hydrogen atoms"],
        "a": 1,
        "e": "In ice, water molecules form an open lattice structure held by hydrogen bonds with more space between molecules than in liquid water. This makes ice less dense than liquid water — so it floats.",
        "h": "Ice has an open hydrogen bond lattice → less dense → floats.",
        "yr": "SS1"
      },
      {
        "q": "In a solution, the solute is:",
        "o": ["The substance present in the largest quantity", "The substance doing the dissolving", "The substance that is dissolved", "The mixture itself"],
        "a": 2,
        "e": "The solute is the substance that is dissolved in the solvent to form the solution. For example, in salt water, salt (NaCl) is the solute and water is the solvent.",
        "h": "Solute = the substance dissolved.",
        "yr": "SS1"
      },
      {
        "q": "A saturated solution is one that:",
        "o": ["Contains very little dissolved solute", "Can dissolve more solute at the given temperature", "Contains the maximum amount of dissolved solute at that temperature", "Has been heated above its boiling point"],
        "a": 2,
        "e": "A saturated solution contains the maximum amount of solute that can be dissolved at a given temperature. Any extra solute added will remain undissolved.",
        "h": "Saturated = maximum dissolved solute at that temperature.",
        "yr": "SS1"
      },
      {
        "q": "Hard water is caused by the presence of dissolved:",
        "o": ["Sodium and potassium ions", "Calcium and magnesium ions", "Iron and aluminium ions", "Chloride and sulfate ions"],
        "a": 1,
        "e": "Hard water contains dissolved calcium (Ca²⁺) and magnesium (Mg²⁺) ions, which enter water as it flows through limestone rocks (calcium carbonate) and dolomite (magnesium carbonate).",
        "h": "Hard water: Ca²⁺ and Mg²⁺ ions.",
        "yr": "SS1"
      },
      {
        "q": "Temporary hardness of water is caused by:",
        "o": ["Dissolved calcium sulfate", "Dissolved calcium and magnesium chlorides", "Dissolved calcium hydrogen carbonate (Ca(HCO₃)₂)", "Dissolved sodium chloride"],
        "a": 2,
        "e": "Temporary hardness is caused by dissolved calcium hydrogen carbonate (Ca(HCO₃)₂) and magnesium hydrogen carbonate. It can be removed by boiling: Ca(HCO₃)₂ → CaCO₃↓ + H₂O + CO₂.",
        "h": "Temporary hardness = Ca(HCO₃)₂ — removed by boiling.",
        "yr": "SS1"
      },
      {
        "q": "Permanent hardness of water is caused by dissolved:",
        "o": ["Calcium hydrogen carbonate", "Calcium sulfate (CaSO₄) and magnesium chloride", "Carbon dioxide gas", "Sodium bicarbonate"],
        "a": 1,
        "e": "Permanent hardness is caused by dissolved calcium sulfate and magnesium chloride/sulfate. Unlike temporary hardness, boiling does not remove it — ion exchange or adding Na₂CO₃ is needed.",
        "h": "Permanent hardness = CaSO₄/MgCl₂ — cannot be removed by boiling.",
        "yr": "SS1"
      },
      {
        "q": "Boiling water removes temporary hardness because:",
        "o": ["Heat evaporates the calcium ions", "Ca(HCO₃)₂ decomposes to give insoluble CaCO₃", "Calcium sulfate dissolves on heating", "Hot water is a better solvent for hardness ions"],
        "a": 1,
        "e": "Boiling decomposes calcium hydrogen carbonate: Ca(HCO₃)₂ → CaCO₃↓ + H₂O + CO₂. The insoluble CaCO₃ precipitates out (forms limescale), removing the hardness ions.",
        "h": "Boiling decomposes Ca(HCO₃)₂ → insoluble CaCO₃ precipitates out.",
        "yr": "SS1"
      },
      {
        "q": "The concentration of a solution in mol/dm³ is calculated as:",
        "o": ["Moles × volume", "Moles ÷ volume (in dm³)", "Mass ÷ volume", "Volume ÷ moles"],
        "a": 1,
        "e": "Concentration (mol/dm³) = moles of solute ÷ volume of solution in dm³. For example, 2 mol in 4 dm³ = 0.5 mol/dm³.",
        "h": "Concentration = moles ÷ volume (dm³).",
        "yr": "SS1"
      },
      {
        "q": "What volume of 2 mol/dm³ NaOH solution contains 0.5 mol of NaOH?",
        "o": ["0.1 dm³", "0.25 dm³", "1 dm³", "4 dm³"],
        "a": 1,
        "e": "Volume = moles ÷ concentration = 0.5 ÷ 2 = 0.25 dm³.",
        "h": "V = 0.5 ÷ 2 = 0.25 dm³.",
        "yr": "SS1"
      },
      {
        "q": "Why does water have a relatively high boiling point compared to other molecules of similar size?",
        "o": ["Its covalent O−H bonds are very strong", "Hydrogen bonding between water molecules requires extra energy to break", "It has a very large molecular mass", "It contains oxygen which always raises boiling points"],
        "a": 1,
        "e": "Water molecules form hydrogen bonds with each other due to the polarity of O−H bonds. These intermolecular hydrogen bonds require significant extra energy to break, raising the boiling point beyond what would be expected for a molecule of its size.",
        "h": "Hydrogen bonding between water molecules → high boiling point.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following substances would NOT dissolve readily in water?",
        "o": ["NaCl (table salt)", "HCl gas", "NaOH", "Candle wax"],
        "a": 3,
        "e": "Candle wax is a non-polar substance (long-chain hydrocarbons). Water is polar, and polar solvents dissolve polar/ionic substances. Non-polar wax does not dissolve in polar water.",
        "h": "Non-polar wax does not dissolve in polar water.",
        "yr": "SS1"
      },
      {
        "q": "High specific heat capacity of water means that:",
        "o": ["Water evaporates quickly", "Water can absorb large amounts of heat with little temperature change", "Water has a very high boiling point", "Water dissolves all substances"],
        "a": 1,
        "e": "Water's high specific heat capacity means it can absorb (or release) large amounts of heat energy with only a small change in temperature. This is why oceans moderate coastal climates.",
        "h": "High specific heat capacity = large heat absorption for small temperature change.",
        "yr": "SS1"
      },
      {
        "q": "Ion exchange is used to remove water hardness because:",
        "o": ["It boils the water to remove carbonates", "It replaces Ca²⁺ and Mg²⁺ with Na⁺ or H⁺ which do not cause hardness", "It adds soap to precipitate the hardness ions", "It filters out the calcium and magnesium particles"],
        "a": 1,
        "e": "An ion exchange resin replaces the hardness-causing Ca²⁺ and Mg²⁺ ions with Na⁺ or H⁺ ions, which do not interfere with soap. This removes both temporary and permanent hardness.",
        "h": "Ion exchange replaces Ca²⁺/Mg²⁺ with Na⁺/H⁺ — no hardness.",
        "yr": "SS1"
      },
      {
        "q": "The process by which water molecules surround and separate solute ions is called:",
        "o": ["Precipitation", "Hydration", "Crystallisation", "Sublimation"],
        "a": 1,
        "e": "Hydration is the process by which water molecules surround and stabilise solute ions. The polar water molecules orientate themselves with the appropriate end toward each ion.",
        "h": "Water surrounding solute ions = hydration.",
        "yr": "SS1"
      },
      {
        "q": "Distilled water is different from tap water because:",
        "o": ["Distilled water has more dissolved minerals", "Distilled water is free from dissolved salts and minerals", "Tap water has been treated with chlorine only", "Distilled water has a higher boiling point"],
        "a": 1,
        "e": "Distilled water is produced by evaporating water and condensing the vapour — leaving behind dissolved salts and minerals. It is essentially pure water (no dissolved solutes).",
        "h": "Distilled water = pure, no dissolved salts or minerals.",
        "yr": "SS1"
      },
      {
        "q": "What is the concentration of a solution made by dissolving 10g of NaOH (M=40 g/mol) in 500 cm³ of water?",
        "o": ["0.25 mol/dm³", "0.5 mol/dm³", "1.0 mol/dm³", "2.0 mol/dm³"],
        "a": 1,
        "e": "Moles NaOH = 10÷40 = 0.25 mol. Volume = 500 cm³ = 0.5 dm³. Concentration = 0.25÷0.5 = 0.5 mol/dm³.",
        "h": "n = 10/40 = 0.25 mol; V = 0.5 dm³; c = 0.25/0.5 = 0.5 mol/dm³.",
        "yr": "SS1"
      },
      {
        "q": "Adding sodium carbonate (Na₂CO₃) to hard water removes hardness because:",
        "o": ["It evaporates the hardness ions", "CO₃²⁻ ions precipitate Ca²⁺ and Mg²⁺ as insoluble carbonates", "It acidifies the water and dissolves the ions", "It reacts with soap to improve lathering"],
        "a": 1,
        "e": "Na₂CO₃ provides CO₃²⁻ ions which react with Ca²⁺ and Mg²⁺ to form insoluble CaCO₃ and MgCO₃ precipitates, effectively removing the hardness ions from solution.",
        "h": "CO₃²⁻ + Ca²⁺ → CaCO₃↓ precipitate removes hardness.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Solubility and Crystallisation",
    "topicCode": "SS1-CHEM-18",
    "module": "Physical Chemistry",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Solubility</span> is the maximum mass of solute that dissolves in 100g of solvent at a specified temperature. <span class=\"learn-keyword\">Crystallisation</span> is the process of obtaining pure solid crystals from a solution. Both concepts are central to purification processes in chemistry and industry.\n</div>\n\n<h3 class=\"learn-subheading\">1. Solubility</h3>\n<p class=\"learn-p\">Solubility is usually expressed in grams of solute per 100g of solvent at a given temperature.</p>\n<ul class=\"learn-list\">\n  <li>For most solids: solubility <strong>increases with temperature</strong>.</li>\n  <li>For gases: solubility <strong>decreases with temperature</strong> (e.g., CO₂ in soft drinks).</li>\n  <li>A <strong>saturated solution</strong> contains the maximum dissolved solute at that temperature.</li>\n  <li>A <strong>supersaturated solution</strong> contains more dissolved solute than a saturated solution (unstable; crystals form easily).</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Solubility Curves</h3>\n<p class=\"learn-p\">A solubility curve is a graph showing how solubility changes with temperature. Most solid solutes show increasing solubility with temperature. The point on the curve gives the solubility at that temperature.</p>\n\n<h3 class=\"learn-subheading\">3. Crystallisation Process</h3>\n<ol class=\"learn-list\">\n  <li>Dissolve the solid in hot water to make a concentrated (nearly saturated) solution.</li>\n  <li>Filter to remove insoluble impurities.</li>\n  <li>Evaporate gently (on a water bath) until crystals begin to form on the surface.</li>\n  <li>Allow to cool slowly — crystals grow as the substance becomes less soluble at lower temperatures.</li>\n  <li>Filter to collect the crystals; dry with filter paper or in an oven.</li>\n</ol>\n\n<h3 class=\"learn-subheading\">4. Water of Crystallisation</h3>\n<p class=\"learn-p\">Some ionic compounds crystallise with a fixed number of water molecules incorporated into their crystal structure. These are called <strong>hydrated salts</strong>.</p>\n<p class=\"learn-p\">Examples: CuSO₄·5H₂O (blue copper sulphate); Na₂CO₃·10H₂O (washing soda); CaCl₂·6H₂O.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Solubility of solids increases with temperature; gases decrease with temperature. Crystallisation purifies by growing crystals that exclude impurities. CuSO₄·5H₂O contains 5 molecules of water of crystallisation — the dot (·) separates the salt formula from the water.</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Solubility = max grams dissolving per 100g solvent at given temperature. Solids become more soluble with higher temperature; gases less soluble. Crystallisation: dissolve → filter → evaporate → cool → filter crystals. Hydrated salts contain water of crystallisation.\n</div>\n  ",
    "questions": [
      {
        "q": "Solubility is defined as:",
        "o": ["The mass of solvent in 100g of solution", "The maximum mass of solute that dissolves in 100g of solvent at a given temperature", "The volume of solution formed per mole of solute", "The temperature at which a solute dissolves"],
        "a": 1,
        "e": "Solubility is the maximum mass of solute (in grams) that will dissolve in 100g of solvent at a specific temperature. It varies with temperature.",
        "h": "Solubility = max mass dissolving per 100g solvent at given T.",
        "yr": "SS1"
      },
      {
        "q": "For most solid solutes, solubility in water:",
        "o": ["Decreases as temperature increases", "Increases as temperature increases", "Does not change with temperature", "First increases then decreases with temperature"],
        "a": 1,
        "e": "For most solid solutes, solubility increases with increasing temperature. Higher temperatures provide more energy for solute-solvent interactions.",
        "h": "Most solids: solubility increases with temperature.",
        "yr": "SS1"
      },
      {
        "q": "The solubility of gases in water:",
        "o": ["Increases with temperature", "Decreases with temperature", "Is not affected by temperature", "Only depends on pressure"],
        "a": 1,
        "e": "The solubility of gases decreases with increasing temperature. This is why warm water holds less dissolved oxygen (bad for fish) and why CO₂ escapes faster from a warm fizzy drink.",
        "h": "Gases: solubility decreases as temperature increases.",
        "yr": "SS1"
      },
      {
        "q": "A saturated solution is one in which:",
        "o": ["No solute has been added", "The solute has completely reacted with the solvent", "No more solute can be dissolved at that temperature", "The solute and solvent are in a 1:1 ratio"],
        "a": 2,
        "e": "A saturated solution contains the maximum amount of solute that can be dissolved at a given temperature. Adding more solute results in it remaining undissolved.",
        "h": "Saturated = no more solute can dissolve at that temperature.",
        "yr": "SS1"
      },
      {
        "q": "Crystallisation is used in chemistry to:",
        "o": ["Separate two liquids with different boiling points", "Obtain pure solid crystals from a solution by cooling", "Remove insoluble solids from a liquid", "Identify gases using chemical tests"],
        "a": 1,
        "e": "Crystallisation purifies a solid by dissolving it in hot water, then cooling the solution slowly. As the solution cools, the pure substance crystallises while impurities remain dissolved.",
        "h": "Crystallisation: cooling concentrated solution grows pure crystals.",
        "yr": "SS1"
      },
      {
        "q": "During crystallisation, why is the solution filtered before cooling?",
        "o": ["To remove the pure crystals", "To remove insoluble impurities before crystals form", "To speed up the cooling process", "To concentrate the solution by removing solvent"],
        "a": 1,
        "e": "Filtering the hot concentrated solution removes any insoluble impurities before crystallisation begins, ensuring the crystals that form are pure.",
        "h": "Filter hot solution first to remove insoluble impurities.",
        "yr": "SS1"
      },
      {
        "q": "Blue copper sulphate crystals (CuSO₄·5H₂O) contain:",
        "o": ["5 atoms of hydrogen", "5 molecules of water of crystallisation per formula unit", "5 sulfate ions", "5 copper ions"],
        "a": 1,
        "e": "CuSO₄·5H₂O contains 5 molecules of water of crystallisation incorporated into the crystal structure. When heated, the water is driven off and the crystals become white anhydrous CuSO₄.",
        "h": "CuSO₄·5H₂O: 5 water molecules per formula unit = water of crystallisation.",
        "yr": "SS1"
      },
      {
        "q": "When anhydrous copper sulphate (CuSO₄) is added to water, it turns blue because:",
        "o": ["Copper dissolves and forms a new compound", "It absorbs water to form hydrated CuSO₄·5H₂O (blue)", "The sulfate reacts with hydrogen to produce colour", "Oxygen is released, producing a blue gas"],
        "a": 1,
        "e": "Anhydrous CuSO₄ is white. When it absorbs water, it forms the hydrated salt CuSO₄·5H₂O, which is characteristically blue. This reaction also releases heat.",
        "h": "Anhydrous CuSO₄ (white) + water → CuSO₄·5H₂O (blue).",
        "yr": "SS1"
      },
      {
        "q": "A solubility curve shows:",
        "o": ["How the boiling point of a solution changes with concentration", "How solubility of a substance changes with temperature", "The rate at which a solute dissolves", "The density of a solution at different temperatures"],
        "a": 1,
        "e": "A solubility curve is a graph of solubility (g per 100g water) against temperature (°C). It shows how much solute can dissolve at each temperature.",
        "h": "Solubility curve = graph of solubility vs temperature.",
        "yr": "SS1"
      },
      {
        "q": "Washing soda has the formula Na₂CO₃·10H₂O. How many water molecules are in one formula unit?",
        "o": ["1", "2", "5", "10"],
        "a": 3,
        "e": "Na₂CO₃·10H₂O contains 10 molecules of water of crystallisation per formula unit. This is why it is called 'decahydrate'.",
        "h": "Na₂CO₃·10H₂O: 10 water molecules.",
        "yr": "SS1"
      },
      {
        "q": "A supersaturated solution:",
        "o": ["Contains less solute than a saturated solution", "Is a stable long-term solution", "Contains more dissolved solute than a saturated solution at the same temperature", "Cannot be made in a laboratory"],
        "a": 2,
        "e": "A supersaturated solution contains more dissolved solute than a saturated solution at the same temperature. It is unstable — adding a seed crystal or disturbing it causes rapid crystallisation.",
        "h": "Supersaturated = more dissolved than saturated (unstable).",
        "yr": "SS1"
      },
      {
        "q": "Why do crystals form when a hot saturated solution is cooled?",
        "o": ["Cooling increases the solubility, forcing solute out", "Cooling decreases solubility of solids, so excess solute crystallises", "Cooling evaporates the solvent", "Cooling causes a chemical reaction"],
        "a": 1,
        "e": "As temperature decreases, the solubility of most solid solutes decreases. The solution becomes supersaturated and the excess solute deposits as crystals.",
        "h": "Lower temperature = lower solubility → excess solute crystallises.",
        "yr": "SS1"
      },
      {
        "q": "The dots (·) in the formula CuSO₄·5H₂O indicate:",
        "o": ["A mixture, not a compound", "Water of crystallisation — water molecules incorporated into the crystal lattice", "Five separate CuSO₄ units", "The compound is unstable"],
        "a": 1,
        "e": "The dot (·) separates the main salt formula from the water of crystallisation, indicating that 5 water molecules are incorporated into each formula unit of the crystalline solid.",
        "h": "· separates salt from its water of crystallisation.",
        "yr": "SS1"
      },
      {
        "q": "If 60g of potassium nitrate dissolves in 100g of water at 70°C but only 32g dissolves at 20°C, how many grams crystallise when the solution cools from 70°C to 20°C?",
        "o": ["20g", "28g", "32g", "60g"],
        "a": 1,
        "e": "At 70°C, 60g dissolved. At 20°C, only 32g can remain dissolved. Amount crystallised = 60 − 32 = 28g.",
        "h": "60 − 32 = 28g crystallise out.",
        "yr": "SS1"
      },
      {
        "q": "Evaporation to dryness is used instead of crystallisation when:",
        "o": ["The substance is thermally sensitive and would decompose", "The substance is thermally stable and will not decompose on heating", "The substance is a gas at room temperature", "The substance does not dissolve in water"],
        "a": 1,
        "e": "Evaporation to dryness is suitable for thermally stable salts like NaCl. For thermally sensitive salts (like Na₂CO₃), crystallisation (gentle cooling) is preferred to avoid decomposition.",
        "h": "Evaporation to dryness = only for thermally stable substances.",
        "yr": "SS1"
      },
      {
        "q": "What happens to CuSO₄·5H₂O when heated strongly?",
        "o": ["It melts and forms a blue liquid", "It turns white as water of crystallisation is driven off", "It decomposes into copper and sulfur dioxide", "It absorbs more water and turns darker blue"],
        "a": 1,
        "e": "When CuSO₄·5H₂O is heated, the water of crystallisation is driven off: CuSO₄·5H₂O → CuSO₄ + 5H₂O. The blue crystals turn white (anhydrous CuSO₄).",
        "h": "Heating CuSO₄·5H₂O drives off water → white anhydrous CuSO₄.",
        "yr": "SS1"
      },
      {
        "q": "The solubility of oxygen in river water decreasing on a hot day is significant because:",
        "o": ["More fish will breathe more", "Fish and aquatic organisms may suffocate due to lower dissolved oxygen", "Water will evaporate faster", "The river will become more acidic"],
        "a": 1,
        "e": "Higher water temperatures decrease the solubility of oxygen in water. Less dissolved oxygen means fish and other aquatic organisms may not get enough oxygen to breathe, potentially causing mass death.",
        "h": "Hot water holds less O₂ → aquatic life may suffocate.",
        "yr": "SS1"
      },
      {
        "q": "The term 'anhydrous' means:",
        "o": ["Containing water of crystallisation", "Dissolved in water", "Without water", "Very concentrated in water"],
        "a": 2,
        "e": "Anhydrous means 'without water'. An anhydrous compound contains no water of crystallisation. For example, anhydrous CuSO₄ is white; the hydrated form CuSO₄·5H₂O is blue.",
        "h": "Anhydrous = without water.",
        "yr": "SS1"
      },
      {
        "q": "On a solubility curve, a point below the curve represents:",
        "o": ["A saturated solution", "A supersaturated solution", "An unsaturated solution", "A pure solvent"],
        "a": 2,
        "e": "On a solubility curve, the curve represents saturated solutions. A point below the curve (less solute than maximum) represents an unsaturated solution — more solute can still dissolve.",
        "h": "Below curve = unsaturated (more can dissolve).",
        "yr": "SS1"
      },
      {
        "q": "Slow cooling during crystallisation produces larger crystals than rapid cooling because:",
        "o": ["More solute dissolves at lower temperatures", "Fewer nucleation sites form and each crystal grows larger over time", "Rapid cooling increases solubility", "Slow cooling evaporates the solvent faster"],
        "a": 1,
        "e": "Slow cooling allows few nucleation sites to form and gives each crystal time to grow slowly and regularly into large, well-formed crystals. Rapid cooling produces many small crystals.",
        "h": "Slow cooling → fewer, larger crystals; fast cooling → many small crystals.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Introduction to Organic Chemistry",
    "topicCode": "SS1-CHEM-19",
    "module": "Organic Chemistry",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Organic chemistry</span> is the branch of chemistry that deals with compounds of <span class=\"learn-keyword\">carbon</span>. Carbon's unique ability to form four bonds and chain with other carbon atoms allows it to form millions of different compounds — the basis of all living things and most materials in daily use.\n</div>\n\n<h3 class=\"learn-subheading\">1. Why Carbon is Special</h3>\n<ul class=\"learn-list\">\n  <li>Carbon has 4 valence electrons — it forms <strong>4 covalent bonds</strong>.</li>\n  <li><strong>Catenation:</strong> Carbon atoms bond to each other to form chains, branches, and rings.</li>\n  <li>Forms strong bonds with H, O, N, halogens, and other carbon atoms.</li>\n  <li>Can form single, double, and triple bonds.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Hydrocarbons</h3>\n<p class=\"learn-p\">Hydrocarbons contain only carbon and hydrogen. They are the simplest organic compounds and are found in petroleum.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Series</th><th>Bond type</th><th>General formula</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Alkanes</td><td>Single C−C bonds</td><td>CₙH₂ₙ₊₂</td><td>CH₄, C₂H₆, C₃H₈</td></tr>\n      <tr><td>Alkenes</td><td>At least one C=C double bond</td><td>CₙH₂ₙ</td><td>C₂H₄, C₃H₆</td></tr>\n      <tr><td>Alkynes</td><td>At least one C≡C triple bond</td><td>CₙH₂ₙ₋₂</td><td>C₂H₂ (ethyne)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Functional Groups</h3>\n<p class=\"learn-p\">A functional group is an atom or group of atoms responsible for the characteristic chemical properties of an organic compound.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Functional group</th><th>Name</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>−OH</td><td>Hydroxyl (alcohol)</td><td>Ethanol (C₂H₅OH)</td></tr>\n      <tr><td>−COOH</td><td>Carboxyl (carboxylic acid)</td><td>Ethanoic acid (CH₃COOH)</td></tr>\n      <tr><td>−CHO</td><td>Aldehyde</td><td>Ethanal (CH₃CHO)</td></tr>\n      <tr><td>−NH₂</td><td>Amino (amine)</td><td>Methylamine (CH₃NH₂)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Homologous Series</h3>\n<p class=\"learn-p\">A homologous series is a family of organic compounds with: (1) the same functional group; (2) the same general formula; (3) each member differing by CH₂; (4) similar chemical properties but gradually changing physical properties.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Carbon has 4 bonds (tetravalent). Alkanes = single bonds (CₙH₂ₙ₊₂); Alkenes = one double bond (CₙH₂ₙ); Alkynes = one triple bond (CₙH₂ₙ₋₂). Homologous series: same functional group, each member differs by CH₂.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Organic chemistry = carbon compounds. Carbon is tetravalent and undergoes catenation. Hydrocarbons: alkanes (CₙH₂ₙ₊₂), alkenes (CₙH₂ₙ), alkynes (CₙH₂ₙ₋₂). Functional groups determine chemical properties. Homologous series: same group, differ by CH₂.\n</div>\n  ",
    "questions": [
      {
        "q": "Organic chemistry primarily studies compounds of:",
        "o": ["Oxygen", "Nitrogen", "Carbon", "Sulfur"],
        "a": 2,
        "e": "Organic chemistry is the study of carbon compounds. Carbon is unique in its ability to form millions of compounds due to its tetravalency and catenation.",
        "h": "Organic chemistry = carbon compounds.",
        "yr": "SS1"
      },
      {
        "q": "The ability of carbon atoms to bond to each other forming chains and rings is called:",
        "o": ["Hybridisation", "Catenation", "Polymerisation", "Valency"],
        "a": 1,
        "e": "Catenation is the ability of carbon atoms to bond to one another forming long chains, branches, and rings. This is why carbon forms millions of compounds.",
        "h": "Catenation = carbon bonding to itself in chains/rings.",
        "yr": "SS1"
      },
      {
        "q": "Carbon has 4 valence electrons. This means it can form:",
        "o": ["2 covalent bonds", "3 covalent bonds", "4 covalent bonds", "6 covalent bonds"],
        "a": 2,
        "e": "Carbon has 4 valence electrons and needs 4 more to complete its octet. It therefore forms 4 covalent bonds, making it tetravalent.",
        "h": "4 valence electrons = 4 covalent bonds (tetravalent).",
        "yr": "SS1"
      },
      {
        "q": "Hydrocarbons are organic compounds containing only:",
        "o": ["Carbon and oxygen", "Carbon and nitrogen", "Carbon and hydrogen", "Carbon and sulfur"],
        "a": 2,
        "e": "Hydrocarbons contain only carbon and hydrogen. They are the simplest organic compounds and are found in petroleum and natural gas.",
        "h": "Hydrocarbons = only carbon and hydrogen.",
        "yr": "SS1"
      },
      {
        "q": "The general formula for alkanes is:",
        "o": ["CₙH₂ₙ", "CₙH₂ₙ₊₂", "CₙH₂ₙ₋₂", "CₙHₙ"],
        "a": 1,
        "e": "Alkanes have the general formula CₙH₂ₙ₊₂. For example: methane CH₄ (n=1), ethane C₂H₆ (n=2), propane C₃H₈ (n=3).",
        "h": "Alkanes: CₙH₂ₙ₊₂ (saturated hydrocarbons).",
        "yr": "SS1"
      },
      {
        "q": "Alkenes are hydrocarbons that contain:",
        "o": ["Only single carbon-carbon bonds", "At least one carbon-carbon double bond", "At least one carbon-carbon triple bond", "Only carbon-oxygen bonds"],
        "a": 1,
        "e": "Alkenes contain at least one C=C double bond. They are unsaturated hydrocarbons. The simplest alkene is ethene (C₂H₄).",
        "h": "Alkenes have at least one C=C double bond.",
        "yr": "SS1"
      },
      {
        "q": "The general formula for alkenes is:",
        "o": ["CₙH₂ₙ₊₂", "CₙH₂ₙ", "CₙH₂ₙ₋₂", "CₙH₂ₙ₊₁"],
        "a": 1,
        "e": "Alkenes have the general formula CₙH₂ₙ. For example: ethene C₂H₄ (n=2), propene C₃H₆ (n=3).",
        "h": "Alkenes: CₙH₂ₙ.",
        "yr": "SS1"
      },
      {
        "q": "A homologous series is a family of organic compounds that:",
        "o": ["Have the same molecular formula", "Have the same functional group and each member differs by CH₂", "Have the same boiling point", "Are all found in petroleum"],
        "a": 1,
        "e": "A homologous series is a family of compounds with the same functional group, same general formula, each member differing from the next by CH₂, and similar chemical properties.",
        "h": "Homologous: same functional group, each member +CH₂.",
        "yr": "SS1"
      },
      {
        "q": "The functional group −OH in organic compounds indicates:",
        "o": ["A carboxylic acid", "An amine", "An alcohol", "An aldehyde"],
        "a": 2,
        "e": "The −OH (hydroxyl) functional group is characteristic of alcohols. For example, ethanol (C₂H₅OH) contains the −OH group.",
        "h": "−OH = hydroxyl group = alcohol.",
        "yr": "SS1"
      },
      {
        "q": "The simplest alkane is methane. Its molecular formula is:",
        "o": ["C₂H₄", "CH₄", "C₂H₆", "C₃H₈"],
        "a": 1,
        "e": "Methane (CH₄) is the simplest alkane with n=1 in CₙH₂ₙ₊₂. It is the main component of natural gas.",
        "h": "Methane = CH₄ (simplest alkane).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a characteristic feature of organic compounds?",
        "o": ["They always contain oxygen", "They are usually insoluble in water and soluble in organic solvents", "They always have high melting points", "They always conduct electricity"],
        "a": 1,
        "e": "Organic compounds are generally non-polar or weakly polar, making them insoluble in polar water but soluble in organic solvents like hexane or ethanol.",
        "h": "Organic compounds: insoluble in water, soluble in organic solvents.",
        "yr": "SS1"
      },
      {
        "q": "The −COOH group in an organic molecule indicates the compound is:",
        "o": ["An alcohol", "A carboxylic acid", "An amine", "An aldehyde"],
        "a": 1,
        "e": "The −COOH (carboxyl) group is characteristic of carboxylic acids. For example, ethanoic acid (CH₃COOH) is vinegar and contains −COOH.",
        "h": "−COOH = carboxyl group = carboxylic acid.",
        "yr": "SS1"
      },
      {
        "q": "Alkynes contain:",
        "o": ["Only single bonds", "One double bond", "At least one triple bond", "Benzene rings"],
        "a": 2,
        "e": "Alkynes contain at least one carbon-carbon triple bond (C≡C). The simplest alkyne is ethyne (acetylene, C₂H₂).",
        "h": "Alkynes have at least one C≡C triple bond.",
        "yr": "SS1"
      },
      {
        "q": "The general formula for alkynes is:",
        "o": ["CₙH₂ₙ₊₂", "CₙH₂ₙ", "CₙH₂ₙ₋₂", "CₙH₂ₙ₋₄"],
        "a": 2,
        "e": "Alkynes have the general formula CₙH₂ₙ₋₂. The simplest alkyne, ethyne (C₂H₂), fits: 2×2−2 = 2 H atoms. ✓",
        "h": "Alkynes: CₙH₂ₙ₋₂.",
        "yr": "SS1"
      },
      {
        "q": "Successive members of a homologous series differ by:",
        "o": ["One oxygen atom", "One CH₂ unit", "Two hydrogen atoms only", "One carbon atom only"],
        "a": 1,
        "e": "Each successive member of a homologous series differs by a CH₂ unit (one carbon and two hydrogen atoms). For example: methane CH₄ → ethane C₂H₆ → propane C₃H₈.",
        "h": "Homologous series: successive members differ by CH₂.",
        "yr": "SS1"
      },
      {
        "q": "Petroleum (crude oil) is mainly composed of:",
        "o": ["Alcohols and carboxylic acids", "Alkanes and other hydrocarbons", "Proteins and carbohydrates", "Metals and non-metals"],
        "a": 1,
        "e": "Petroleum (crude oil) is primarily a mixture of alkanes and other hydrocarbons of varying chain lengths. It is separated into useful fractions by fractional distillation.",
        "h": "Petroleum = mainly alkanes and other hydrocarbons.",
        "yr": "SS1"
      },
      {
        "q": "The compound ethanol has the formula C₂H₅OH. Its functional group is:",
        "o": ["−COOH", "−CHO", "−OH", "−NH₂"],
        "a": 2,
        "e": "Ethanol (C₂H₅OH) contains the −OH (hydroxyl) functional group, classifying it as an alcohol.",
        "h": "C₂H₅OH: the −OH group makes it an alcohol.",
        "yr": "SS1"
      },
      {
        "q": "In a homologous series, as the molecular mass increases (going up the series), the boiling point:",
        "o": ["Decreases", "Stays the same", "Increases gradually", "Increases then decreases"],
        "a": 2,
        "e": "As molecules get larger in a homologous series, the intermolecular (van der Waals) forces increase, requiring more energy to overcome. This results in gradually increasing boiling points.",
        "h": "Larger molecule = stronger intermolecular forces = higher boiling point.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an unsaturated hydrocarbon?",
        "o": ["Methane (CH₄)", "Ethane (C₂H₆)", "Propane (C₃H₈)", "Ethene (C₂H₄)"],
        "a": 3,
        "e": "Ethene (C₂H₄) is an unsaturated hydrocarbon — it contains a C=C double bond. Alkenes and alkynes are unsaturated. Alkanes (methane, ethane, propane) are saturated.",
        "h": "Unsaturated = contains C=C or C≡C. Ethene is unsaturated.",
        "yr": "SS1"
      },
      {
        "q": "The study of organic chemistry is important because:",
        "o": ["Carbon compounds are simple and easy to study", "Organic compounds form the basis of all living organisms and most industrial materials", "There are only a few hundred known organic compounds", "Carbon compounds do not react with other chemicals"],
        "a": 1,
        "e": "Organic chemistry is crucial because carbon compounds form the molecular basis of all life (DNA, proteins, lipids) and most manufactured materials (plastics, medicines, fuels, dyes).",
        "h": "Organic compounds: basis of life and most industrial materials.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Alkanes, Alkenes, and Alkynes",
    "topicCode": "SS1-CHEM-20",
    "module": "Organic Chemistry",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Alkanes</span>, <span class=\"learn-keyword\">alkenes</span>, and <span class=\"learn-keyword\">alkynes</span> are the three main families of hydrocarbons. Their properties and reactions differ significantly based on whether they contain single, double, or triple carbon-carbon bonds.\n</div>\n\n<h3 class=\"learn-subheading\">1. Alkanes — Saturated Hydrocarbons</h3>\n<p class=\"learn-p\">Alkanes have only C−C single bonds (saturated). They are relatively unreactive but undergo combustion and substitution reactions.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Name</th><th>Formula</th><th>State at RTP</th></tr></thead>\n    <tbody>\n      <tr><td>Methane</td><td>CH₄</td><td>Gas</td></tr>\n      <tr><td>Ethane</td><td>C₂H₆</td><td>Gas</td></tr>\n      <tr><td>Propane</td><td>C₃H₈</td><td>Gas</td></tr>\n      <tr><td>Butane</td><td>C₄H₁₀</td><td>Gas</td></tr>\n      <tr><td>Pentane</td><td>C₅H₁₂</td><td>Liquid</td></tr>\n    </tbody>\n  </table>\n</div>\n<p class=\"learn-p\"><strong>Combustion of alkanes:</strong> CH₄ + 2O₂ → CO₂ + 2H₂O (complete); produces CO and H₂O if incomplete.</p>\n<p class=\"learn-p\"><strong>Substitution (halogenation):</strong> In UV light, alkanes react with halogens: CH₄ + Cl₂ → CH₃Cl + HCl.</p>\n\n<h3 class=\"learn-subheading\">2. Alkenes — Unsaturated Hydrocarbons</h3>\n<p class=\"learn-p\">Alkenes have at least one C=C double bond. They are more reactive than alkanes due to the double bond.</p>\n<p class=\"learn-p\"><strong>Addition reaction:</strong> Alkenes add molecules across the double bond.<br>C₂H₄ + H₂ → C₂H₆ (hydrogenation)<br>C₂H₄ + Br₂ → CH₂BrCH₂Br (decolourises bromine water — test for alkene)</p>\n\n<h3 class=\"learn-subheading\">3. Alkynes — Triple Bond Hydrocarbons</h3>\n<p class=\"learn-p\">Alkynes have a C≡C triple bond. Ethyne (C₂H₂, acetylene) is the most important. Used in welding (oxy-acetylene torch). Also undergo addition reactions.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Test for alkenes: add bromine water — it decolourises (orange → colourless). Alkanes do NOT decolourise bromine water. Complete combustion = CO₂ + H₂O. Incomplete combustion = CO + H₂O (or soot/carbon).\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Alkanes: single bonds, saturated, relatively unreactive — combustion and substitution. Alkenes: double bond, unsaturated, reactive — addition reactions, decolourise bromine water. Alkynes: triple bond — addition reactions; ethyne used in welding.\n</div>\n  ",
    "questions": [
      {
        "q": "What is the molecular formula of methane?",
        "o": ["C₂H₄", "CH₄", "C₂H₆", "C₃H₈"],
        "a": 1,
        "e": "Methane is the first member of the alkane series with formula CH₄ (n=1 in CₙH₂ₙ₊₂). It is the main component of natural gas.",
        "h": "Methane = CH₄.",
        "yr": "SS1"
      },
      {
        "q": "Alkanes are described as 'saturated' because:",
        "o": ["They dissolve easily in water", "They contain only single carbon-carbon bonds and cannot add more hydrogen", "They have a high density", "They contain the maximum number of oxygen atoms"],
        "a": 1,
        "e": "Alkanes are saturated because all carbon-carbon bonds are single bonds. They already contain the maximum number of hydrogen atoms possible — they cannot add more.",
        "h": "Saturated = all single C−C bonds, max H atoms.",
        "yr": "SS1"
      },
      {
        "q": "Alkenes decolourise bromine water because they:",
        "o": ["React with bromine in a substitution reaction", "React with bromine in an addition reaction across the double bond", "Dissolve bromine to form a colourless solution", "Reduce bromine to bromide ions"],
        "a": 1,
        "e": "Alkenes add bromine across the C=C double bond in an addition reaction: C₂H₄ + Br₂ → CH₂BrCH₂Br. The orange bromine water is decolourised to colourless.",
        "h": "Alkene + Br₂ → addition product; orange → colourless.",
        "yr": "SS1"
      },
      {
        "q": "Which test distinguishes an alkene from an alkane?",
        "o": ["Burning in oxygen", "Adding bromine water — alkene decolourises it, alkane does not", "Dissolving in water", "Testing with litmus paper"],
        "a": 1,
        "e": "Adding bromine water is the standard test: alkenes decolourise bromine water (orange → colourless) due to the addition reaction across the C=C double bond. Alkanes have no effect.",
        "h": "Bromine water test: alkene decolourises; alkane does not.",
        "yr": "SS1"
      },
      {
        "q": "The complete combustion of methane (CH₄) produces:",
        "o": ["CO and H₂", "CO₂ and H₂O", "CO and H₂O", "C and H₂O"],
        "a": 1,
        "e": "Complete combustion of methane: CH₄ + 2O₂ → CO₂ + 2H₂O. Complete combustion of any hydrocarbon produces CO₂ and H₂O when there is excess oxygen.",
        "h": "Complete combustion: hydrocarbon + excess O₂ → CO₂ + H₂O.",
        "yr": "SS1"
      },
      {
        "q": "Incomplete combustion of alkanes produces:",
        "o": ["CO₂ and H₂O only", "CO and H₂O (or soot)", "Only water", "Nitrogen and water"],
        "a": 1,
        "e": "Incomplete combustion (insufficient oxygen) produces carbon monoxide (CO) or carbon (soot) instead of CO₂. This is dangerous because CO is a toxic gas.",
        "h": "Incomplete combustion (limited O₂) → CO or soot + H₂O.",
        "yr": "SS1"
      },
      {
        "q": "The formula for ethene is:",
        "o": ["C₂H₆", "C₂H₄", "C₂H₂", "C₃H₆"],
        "a": 1,
        "e": "Ethene is the simplest alkene with formula C₂H₄ (n=2 in CₙH₂ₙ = 2×2=4). It contains one C=C double bond.",
        "h": "Ethene = C₂H₄ (simplest alkene).",
        "yr": "SS1"
      },
      {
        "q": "The reaction of methane with chlorine in ultraviolet light is an example of:",
        "o": ["Addition reaction", "Combustion reaction", "Substitution reaction", "Elimination reaction"],
        "a": 2,
        "e": "CH₄ + Cl₂ → CH₃Cl + HCl (in UV light). A hydrogen atom is substituted (replaced) by a chlorine atom. This is a free-radical substitution reaction.",
        "h": "Alkane + halogen (UV light) = substitution reaction.",
        "yr": "SS1"
      },
      {
        "q": "Ethyne (C₂H₂) is used in:",
        "o": ["Inflating balloons", "Oxy-acetylene welding torches", "Refrigerators", "Carbonated drinks"],
        "a": 1,
        "e": "Ethyne (acetylene, C₂H₂) burns with an extremely hot flame when mixed with oxygen. This makes it ideal for oxy-acetylene welding and cutting torches.",
        "h": "Ethyne (acetylene) = oxy-acetylene welding.",
        "yr": "SS1"
      },
      {
        "q": "The hydrogenation of ethene produces:",
        "o": ["Ethanol", "Ethane", "Propane", "Ethanoic acid"],
        "a": 1,
        "e": "C₂H₄ + H₂ → C₂H₆. Hydrogen is added across the double bond, converting ethene (alkene) to ethane (alkane). This is an addition reaction (hydrogenation).",
        "h": "Ethene + H₂ → ethane (addition of hydrogen).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT a property of alkanes?",
        "o": ["They burn in oxygen", "They react readily with bromine water at room temperature", "They undergo substitution with halogens in UV light", "They are used as fuels"],
        "a": 1,
        "e": "Alkanes do NOT react with bromine water at room temperature. This is because they are saturated (no double bond). Only alkenes decolourise bromine water readily.",
        "h": "Alkanes do NOT react with bromine water — that is the alkene test.",
        "yr": "SS1"
      },
      {
        "q": "The first four members of the alkane series, in order, are:",
        "o": ["Methane, ethane, propane, butane", "Ethane, propane, butane, pentane", "Methane, propane, butane, hexane", "Ethene, propene, butene, pentene"],
        "a": 0,
        "e": "The first four alkanes are: methane (CH₄), ethane (C₂H₆), propane (C₃H₈), and butane (C₄H₁₀). The prefixes meth-, eth-, prop-, but- indicate 1, 2, 3, 4 carbons.",
        "h": "Methane, ethane, propane, butane (1C, 2C, 3C, 4C).",
        "yr": "SS1"
      },
      {
        "q": "Polymerisation of ethene produces:",
        "o": ["Ethanol", "Ethanoic acid", "Poly(ethene) — polythene", "Propene"],
        "a": 2,
        "e": "When many ethene molecules (monomers) are joined together by addition polymerisation, they form poly(ethene) — polythene. The double bond breaks and the molecules link end-to-end.",
        "h": "Ethene → poly(ethene) (polythene) by addition polymerisation.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following alkanes is a gas at room temperature?",
        "o": ["Pentane (C₅H₁₂)", "Hexane (C₆H₁₄)", "Propane (C₃H₈)", "Decane (C₁₀H₂₂)"],
        "a": 2,
        "e": "Propane (C₃H₈) is a gas at room temperature. The first four alkanes (methane, ethane, propane, butane) are all gases at room temperature and pressure.",
        "h": "Propane (C₃H₈) is a gas at room temperature.",
        "yr": "SS1"
      },
      {
        "q": "Addition reactions are characteristic of:",
        "o": ["Alkanes", "Noble gases", "Alkenes and alkynes (unsaturated hydrocarbons)", "All organic compounds"],
        "a": 2,
        "e": "Addition reactions are characteristic of unsaturated compounds (alkenes and alkynes) that contain double or triple bonds. The bond opens up to add other atoms/molecules.",
        "h": "Addition reactions = alkenes and alkynes (unsaturated).",
        "yr": "SS1"
      },
      {
        "q": "What product is formed when ethene reacts with water (steam) in the presence of a catalyst?",
        "o": ["Ethane", "Ethanol", "Ethanoic acid", "Ethanal"],
        "a": 1,
        "e": "C₂H₄ + H₂O → C₂H₅OH (ethanol). This is the industrial process for producing ethanol — the addition of steam to ethene over a phosphoric acid catalyst.",
        "h": "Ethene + H₂O → ethanol (industrial production of alcohol).",
        "yr": "SS1"
      },
      {
        "q": "The molecular formula of ethyne (acetylene) is:",
        "o": ["C₂H₆", "C₂H₄", "C₂H₂", "C₃H₄"],
        "a": 2,
        "e": "Ethyne has the formula C₂H₂ (n=2 in CₙH₂ₙ₋₂ = 2×2−2 = 2). It contains a triple bond (HC≡CH).",
        "h": "Ethyne = C₂H₂ (simplest alkyne).",
        "yr": "SS1"
      },
      {
        "q": "The reaction type that characterises alkanes (reaction with halogens under UV light) is:",
        "o": ["Addition", "Substitution", "Elimination", "Combustion"],
        "a": 1,
        "e": "Alkanes undergo free-radical substitution with halogens under UV light. A H atom is replaced by a halogen atom: CH₄ + Cl₂ → CH₃Cl + HCl.",
        "h": "Alkane + halogen (UV) = substitution.",
        "yr": "SS1"
      },
      {
        "q": "Carbon monoxide (CO) produced by incomplete combustion is dangerous because:",
        "o": ["It is explosive", "It is acidic and corrodes metals", "It binds to haemoglobin in red blood cells, preventing oxygen transport", "It reacts violently with water"],
        "a": 2,
        "e": "CO binds to haemoglobin about 200 times more strongly than oxygen, forming carboxyhaemoglobin. This prevents blood from carrying oxygen, causing CO poisoning which can be fatal.",
        "h": "CO binds to haemoglobin → prevents O₂ transport → poisoning.",
        "yr": "SS1"
      },
      {
        "q": "Propene has the molecular formula:",
        "o": ["C₃H₈", "C₃H₆", "C₃H₄", "C₃H₂"],
        "a": 1,
        "e": "Propene is the third alkene (n=3 in CₙH₂ₙ = 3×2=6 H atoms). Formula: C₃H₆. It has one C=C double bond.",
        "h": "Propene: n=3 in CₙH₂ₙ → C₃H₆.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Isomerism",
    "topicCode": "SS1-CHEM-21",
    "module": "Organic Chemistry",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Isomers</span> are compounds that have the <span class=\"learn-keyword\">same molecular formula</span> but <span class=\"learn-keyword\">different structural arrangements</span> of atoms. Isomerism explains why so many organic compounds are possible and why compounds with the same formula can have different properties.\n</div>\n\n<h3 class=\"learn-subheading\">1. Structural Isomerism</h3>\n<p class=\"learn-p\">Structural isomers have the same molecular formula but different connectivity (different structural formulae). Types include:</p>\n<ul class=\"learn-list\">\n  <li><strong>Chain isomerism:</strong> Different carbon chain arrangements (straight chain vs branched). Example: butane and methylpropane (both C₄H₁₀).</li>\n  <li><strong>Position isomerism:</strong> Same functional group at different positions. Example: 1-bromopropane and 2-bromopropane (both C₃H₇Br).</li>\n  <li><strong>Functional group isomerism:</strong> Different functional groups. Example: ethanol (C₂H₅OH) and methoxymethane (CH₃OCH₃) — both C₂H₆O.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Chain Isomers of Butane (C₄H₁₀)</h3>\n<p class=\"learn-p\"><strong>n-Butane:</strong> CH₃−CH₂−CH₂−CH₃ (straight chain)</p>\n<p class=\"learn-p\"><strong>2-Methylpropane (isobutane):</strong> CH₃−CH(CH₃)−CH₃ (branched)</p>\n<p class=\"learn-p\">Both have molecular formula C₄H₁₀ but different structures and slightly different physical properties (different boiling points).</p>\n\n<h3 class=\"learn-subheading\">3. Why Isomers Have Different Properties</h3>\n<p class=\"learn-p\">Structural isomers have the same molecular formula but different arrangements of atoms. This leads to different physical properties (boiling points, melting points, solubility) and sometimes different chemical properties if they have different functional groups.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Isomers = same molecular formula, different structural arrangement. Three types: chain isomers (different chain shape), position isomers (functional group at different position), functional group isomers (different functional group). Always check the molecular formula is the same.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Isomers share the same molecular formula but differ in structure. Types: chain isomers, position isomers, functional group isomers. Structural isomers may have different physical and/or chemical properties.\n</div>\n  ",
    "questions": [
      {
        "q": "Isomers are defined as compounds that have:",
        "o": ["The same structure but different molecular formulae", "The same molecular formula but different structural arrangements", "The same physical properties and same structure", "Different molecular formulae and different structures"],
        "a": 1,
        "e": "Isomers are compounds with the same molecular formula but different structural arrangements of atoms. They are different compounds with different physical properties.",
        "h": "Isomers: same molecular formula, different structure.",
        "yr": "SS1"
      },
      {
        "q": "n-Butane and 2-methylpropane (isobutane) are isomers because they:",
        "o": ["Have different molecular formulae", "Have the same structure", "Both have the molecular formula C₄H₁₀ but different structural arrangements", "Have the same boiling point"],
        "a": 2,
        "e": "Both n-butane and 2-methylpropane have the molecular formula C₄H₁₀. n-Butane has a straight chain; 2-methylpropane has a branched chain. They are chain isomers.",
        "h": "Both C₄H₁₀: n-butane straight, isobutane branched = chain isomers.",
        "yr": "SS1"
      },
      {
        "q": "Chain isomers differ in:",
        "o": ["The type of functional group", "The position of the functional group", "The carbon chain structure (straight vs branched)", "The number of carbon atoms"],
        "a": 2,
        "e": "Chain isomers have the same molecular formula but different arrangements of the carbon chain — one is straight (normal) and one or more are branched.",
        "h": "Chain isomers: different carbon chain arrangement (straight vs branched).",
        "yr": "SS1"
      },
      {
        "q": "1-bromopropane and 2-bromopropane are examples of:",
        "o": ["Chain isomers", "Position isomers", "Functional group isomers", "Geometric isomers"],
        "a": 1,
        "e": "1-bromopropane (Br on carbon 1) and 2-bromopropane (Br on carbon 2) have the same molecular formula C₃H₇Br but the bromine is at different positions. These are position isomers.",
        "h": "Same group at different positions = position isomers.",
        "yr": "SS1"
      },
      {
        "q": "Ethanol (C₂H₅OH) and methoxymethane (CH₃OCH₃) are examples of:",
        "o": ["Chain isomers", "Position isomers", "Functional group isomers", "Optical isomers"],
        "a": 2,
        "e": "Both have molecular formula C₂H₆O, but ethanol has an −OH group (alcohol) while methoxymethane has an −O− ether linkage. These are functional group isomers.",
        "h": "Same formula, different functional groups = functional group isomers.",
        "yr": "SS1"
      },
      {
        "q": "How many structural isomers does butane (C₄H₁₀) have?",
        "o": ["1", "2", "3", "4"],
        "a": 1,
        "e": "Butane (C₄H₁₀) has 2 structural isomers: n-butane (straight chain) and 2-methylpropane (branched). Both have the formula C₄H₁₀.",
        "h": "C₄H₁₀ has 2 isomers: n-butane and 2-methylpropane.",
        "yr": "SS1"
      },
      {
        "q": "Do isomers have the same physical properties?",
        "o": ["Yes, always", "Only if they are chain isomers", "No — they can have different boiling points, melting points, and densities", "Yes, but different chemical properties"],
        "a": 2,
        "e": "Structural isomers have different structural arrangements, which affects intermolecular forces. Therefore they can have different physical properties such as boiling points, melting points, and density.",
        "h": "Same molecular formula ≠ same physical properties.",
        "yr": "SS1"
      },
      {
        "q": "The two structural isomers of propanol (C₃H₇OH) are distinguished by:",
        "o": ["Different numbers of carbons", "The −OH group being on carbon 1 or carbon 2", "Different molecular formulae", "One being an alkane and the other an alkene"],
        "a": 1,
        "e": "Propan-1-ol has −OH on carbon 1; propan-2-ol has −OH on carbon 2. Both have formula C₃H₇OH. These are position isomers.",
        "h": "−OH on C1 vs C2 in propanol = position isomers.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following pairs are isomers?",
        "o": ["CH₄ and C₂H₆", "C₂H₅OH and CH₃OCH₃", "C₂H₄ and C₃H₈", "CH₄ and C₂H₄"],
        "a": 1,
        "e": "C₂H₅OH (ethanol) and CH₃OCH₃ (dimethyl ether) both have the molecular formula C₂H₆O — they are functional group isomers.",
        "h": "C₂H₅OH and CH₃OCH₃ both = C₂H₆O → isomers.",
        "yr": "SS1"
      },
      {
        "q": "n-Butane boils at −1°C while 2-methylpropane boils at −12°C. This difference occurs because:",
        "o": ["They have different molecular formulae", "The straight chain isomer has stronger van der Waals forces due to more surface contact", "Branched isomers have stronger intermolecular forces", "They contain different functional groups"],
        "a": 1,
        "e": "n-Butane has a straight chain with more surface area for contact between molecules, creating stronger van der Waals forces. 2-Methylpropane is more compact (spherical), with less surface contact and weaker forces — hence lower boiling point.",
        "h": "Straight chain = more surface contact = stronger intermolecular forces = higher bp.",
        "yr": "SS1"
      },
      {
        "q": "Pentane (C₅H₁₂) has how many structural isomers?",
        "o": ["2", "3", "4", "5"],
        "a": 1,
        "e": "Pentane (C₅H₁₂) has 3 structural isomers: n-pentane (straight), 2-methylbutane (one branch), and 2,2-dimethylpropane (two branches).",
        "h": "C₅H₁₂ has 3 structural isomers.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is true about all isomers?",
        "o": ["They have the same boiling points", "They have the same molecular formula", "They have identical chemical properties", "They all have straight carbon chains"],
        "a": 1,
        "e": "All isomers share the same molecular formula — this is the defining property. They differ in structural arrangement and can have different physical and chemical properties.",
        "h": "All isomers = same molecular formula.",
        "yr": "SS1"
      },
      {
        "q": "What type of isomers have different functional groups?",
        "o": ["Chain isomers", "Position isomers", "Functional group isomers", "Optical isomers"],
        "a": 2,
        "e": "Functional group isomers have the same molecular formula but different functional groups. For example, an alcohol and an ether with the same molecular formula.",
        "h": "Different functional groups = functional group isomers.",
        "yr": "SS1"
      },
      {
        "q": "If two compounds have different molecular formulae, they are:",
        "o": ["Isomers of each other", "Different members of the same homologous series or different compounds — but not isomers", "Identical compounds", "Always in the same homologous series"],
        "a": 1,
        "e": "Isomers must have the same molecular formula. If two compounds have different molecular formulae, they cannot be isomers — they are simply different compounds.",
        "h": "Different molecular formula = NOT isomers.",
        "yr": "SS1"
      },
      {
        "q": "Isomerism is important in chemistry because:",
        "o": ["It reduces the number of possible organic compounds", "The same molecular formula can give rise to compounds with very different properties", "Isomers always have the same physical properties", "It only applies to inorganic compounds"],
        "a": 1,
        "e": "Isomerism is important because the same molecular formula can produce structurally different compounds with distinct physical and chemical properties — greatly increasing the diversity of organic chemistry.",
        "h": "Same formula, different structure = different properties → vast diversity.",
        "yr": "SS1"
      },
      {
        "q": "The number of structural isomers increases with increasing:",
        "o": ["Temperature", "Number of carbon atoms in the molecule", "Atomic mass of the molecule", "Number of hydrogen atoms only"],
        "a": 1,
        "e": "As the number of carbon atoms increases, more possibilities for chain arrangements and branching exist. For example: C₄H₁₀ has 2 isomers; C₅H₁₂ has 3; C₆H₁₄ has 5.",
        "h": "More carbons = more possible arrangements = more isomers.",
        "yr": "SS1"
      },
      {
        "q": "The structural formulae of n-butane and 2-methylpropane differ in:",
        "o": ["Their molecular formula", "Their chain arrangement — one straight, one branched", "Their functional groups", "Their number of hydrogen atoms"],
        "a": 1,
        "e": "n-Butane has a straight chain (4C in a row), while 2-methylpropane has a 3C main chain with a CH₃ branch. Both have formula C₄H₁₀.",
        "h": "n-Butane straight; 2-methylpropane branched — same formula.",
        "yr": "SS1"
      },
      {
        "q": "Glucose (C₆H₁₂O₆) and fructose (C₆H₁₂O₆) are:",
        "o": ["The same compound", "Structural isomers with the same formula but different structures", "Members of different homologous series", "Polymers of each other"],
        "a": 1,
        "e": "Glucose and fructose both have molecular formula C₆H₁₂O₆ but different structural arrangements. Glucose is an aldehyde sugar; fructose is a ketone sugar. They are structural isomers.",
        "h": "Glucose and fructose: same C₆H₁₂O₆, different structure = isomers.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of position isomerism?",
        "o": ["n-butane and 2-methylpropane", "Propan-1-ol and propan-2-ol", "Ethanol and dimethyl ether", "Methane and ethane"],
        "a": 1,
        "e": "Propan-1-ol (OH on C1) and propan-2-ol (OH on C2) have the same molecular formula C₃H₈O but the −OH group is at different positions on the carbon chain. Position isomers.",
        "h": "Different position of −OH in propanol = position isomers.",
        "yr": "SS1"
      },
      {
        "q": "Structural isomers that differ only in the length and branching of the carbon chain belong to:",
        "o": ["Position isomers", "Functional group isomers", "Chain isomers", "Geometric isomers"],
        "a": 2,
        "e": "Chain isomers differ only in the arrangement of the carbon skeleton (straight vs branched). The functional groups are the same type and the molecular formula is the same.",
        "h": "Different chain shape (straight vs branched) = chain isomers.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Petroleum and Petrochemicals",
    "topicCode": "SS1-CHEM-22",
    "module": "Industrial Chemistry",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Petroleum</span> (crude oil) is a complex mixture of hydrocarbons formed over millions of years from the remains of marine organisms. It is refined by <span class=\"learn-keyword\">fractional distillation</span> into useful fractions, and these fractions form the basis of <span class=\"learn-keyword\">petrochemicals</span> — thousands of useful products.\n</div>\n\n<h3 class=\"learn-subheading\">1. Origin of Petroleum</h3>\n<p class=\"learn-p\">Petroleum formed from the remains of tiny marine organisms (plankton) buried under sediment over millions of years. Heat and pressure transformed the organic matter into crude oil and natural gas (fossil fuels).</p>\n\n<h3 class=\"learn-subheading\">2. Fractional Distillation of Crude Oil</h3>\n<p class=\"learn-p\">Crude oil is heated and fed into a fractionating column where different fractions condense at different levels.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Fraction</th><th>Approx boiling range</th><th>Uses</th></tr></thead>\n    <tbody>\n      <tr><td>Petroleum gases</td><td>Below 25°C</td><td>LPG (cooking, heating)</td></tr>\n      <tr><td>Petrol (gasoline)</td><td>25–75°C</td><td>Motor fuel</td></tr>\n      <tr><td>Naphtha</td><td>75–160°C</td><td>Chemical feedstock</td></tr>\n      <tr><td>Kerosene</td><td>160–250°C</td><td>Jet fuel, paraffin</td></tr>\n      <tr><td>Diesel</td><td>250–350°C</td><td>Lorries, buses, trains</td></tr>\n      <tr><td>Fuel oil</td><td>350–400°C</td><td>Ships, power stations</td></tr>\n      <tr><td>Bitumen</td><td>Above 400°C</td><td>Road surfacing, roofing</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Cracking</h3>\n<p class=\"learn-p\"><strong>Cracking</strong> breaks down large hydrocarbon molecules (from high-boiling fractions) into smaller, more useful molecules — especially alkenes and petrol-range alkanes. It uses high temperature and/or a catalyst.</p>\n<p class=\"learn-p\">Example: C₁₆H₃₄ → C₈H₁₈ + C₄H₈ + C₄H₈</p>\n\n<h3 class=\"learn-subheading\">4. Petrochemicals</h3>\n<p class=\"learn-p\">Petrochemicals are chemicals derived from petroleum or natural gas. Examples: ethene (for plastics), propene (for polypropylene), benzene (for dyes and medicines), methanol.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> In fractional distillation, temperature decreases from bottom to top. Low boiling fractions (gases, petrol) rise to the top; high boiling fractions (bitumen) stay at the bottom. Cracking converts large molecules to small, useful ones including alkenes.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Petroleum is a mixture of hydrocarbons separated by fractional distillation. Fractions include gases, petrol, naphtha, kerosene, diesel, fuel oil, and bitumen. Cracking breaks large molecules into smaller useful ones. Petrochemicals are chemicals made from petroleum.\n</div>\n  ",
    "questions": [
      {
        "q": "Petroleum (crude oil) is mainly composed of:",
        "o": ["Alcohols and organic acids", "A mixture of hydrocarbons", "Pure alkanes only", "Salts and water"],
        "a": 1,
        "e": "Petroleum is primarily a complex mixture of hydrocarbons (mainly alkanes) of varying chain lengths, along with small amounts of sulfur, nitrogen, and oxygen compounds.",
        "h": "Petroleum = complex mixture of hydrocarbons.",
        "yr": "SS1"
      },
      {
        "q": "Petroleum was formed over millions of years from:",
        "o": ["Volcanic lava cooling underground", "Remains of marine organisms buried under sediment", "Chemical reactions between minerals in rocks", "Decomposition of land animals only"],
        "a": 1,
        "e": "Petroleum formed from the remains of tiny marine organisms (mainly plankton) buried under sediment. Heat and pressure over millions of years converted the organic matter into crude oil.",
        "h": "Petroleum = fossilised marine organism remains.",
        "yr": "SS1"
      },
      {
        "q": "The process used to separate crude oil into useful fractions is:",
        "o": ["Simple distillation", "Fractional distillation", "Crystallisation", "Filtration"],
        "a": 1,
        "e": "Fractional distillation separates crude oil into fractions. The oil is heated, vaporises, and rises up the column. Different fractions condense at different levels due to their different boiling points.",
        "h": "Crude oil → fractional distillation → useful fractions.",
        "yr": "SS1"
      },
      {
        "q": "In fractional distillation of crude oil, fractions with the lowest boiling points are collected:",
        "o": ["At the bottom of the column", "At the middle of the column", "At the top of the column", "As solids at room temperature"],
        "a": 2,
        "e": "Temperature decreases from bottom to top of the fractionating column. Fractions with the lowest boiling points (petroleum gases, petrol) condense and are collected near the top.",
        "h": "Low boiling point = rises to top of fractionating column.",
        "yr": "SS1"
      },
      {
        "q": "Which fraction of petroleum is used as motor fuel for cars?",
        "o": ["Kerosene", "Diesel", "Petrol (gasoline)", "Bitumen"],
        "a": 2,
        "e": "Petrol (gasoline) is the fraction used in petrol engines for cars. It has a boiling range of about 25–75°C and is mainly C₅–C₁₀ alkanes.",
        "h": "Cars use petrol (gasoline) fraction.",
        "yr": "SS1"
      },
      {
        "q": "Kerosene (paraffin) is used as:",
        "o": ["Road surfacing", "Fuel for ships and power stations", "Jet fuel and domestic heating", "LPG for cooking"],
        "a": 2,
        "e": "Kerosene is used as jet fuel (aviation fuel) and as paraffin for domestic heating and lamps. It has a boiling range of 160–250°C.",
        "h": "Kerosene = jet fuel and domestic paraffin.",
        "yr": "SS1"
      },
      {
        "q": "Bitumen (tar) is the petroleum fraction that:",
        "o": ["Rises to the top of the column during distillation", "Has the lowest boiling point and is a gas", "Has the highest boiling point and remains as a thick residue", "Is used as fuel in car engines"],
        "a": 2,
        "e": "Bitumen has the highest boiling point (above 400°C) and remains as a thick, viscous residue at the bottom of the column. It is used for road surfacing and waterproofing.",
        "h": "Bitumen = highest boiling point; used in road surfacing.",
        "yr": "SS1"
      },
      {
        "q": "Cracking in petroleum refining means:",
        "o": ["Splitting the fractionating column into sections", "Breaking down large hydrocarbon molecules into smaller, more useful ones", "Purifying crude oil by removing impurities", "Joining small molecules together to form polymers"],
        "a": 1,
        "e": "Cracking breaks large hydrocarbon molecules (long chains from high-boiling fractions) into smaller molecules including petrol-range alkanes and alkenes. This increases the supply of petrol and produces alkene monomers.",
        "h": "Cracking = breaking large hydrocarbons into smaller useful ones.",
        "yr": "SS1"
      },
      {
        "q": "Cracking produces alkenes in addition to smaller alkanes. This is important because alkenes are used as:",
        "o": ["Fuels only", "Monomers for making plastics by polymerisation", "Lubricants for engines", "Solvents for paint"],
        "a": 1,
        "e": "Alkenes produced by cracking (e.g., ethene, propene) serve as monomers for making plastics through addition polymerisation. Ethene → poly(ethene); propene → poly(propylene).",
        "h": "Alkenes from cracking → monomers → plastics by polymerisation.",
        "yr": "SS1"
      },
      {
        "q": "LPG (liquefied petroleum gas) used for cooking is mainly:",
        "o": ["Methane", "Propane and butane", "Petrol-range alkanes", "Diesel"],
        "a": 1,
        "e": "LPG is mainly propane (C₃H₈) and butane (C₄H₁₀) — the lowest boiling petroleum fractions. They are liquefied under pressure for storage in cylinders.",
        "h": "LPG = mainly propane and butane.",
        "yr": "SS1"
      },
      {
        "q": "Petrochemicals are best defined as:",
        "o": ["All chemicals found underground", "Chemicals derived from petroleum or natural gas", "Only fuels produced from crude oil", "The heavy residues left after distillation"],
        "a": 1,
        "e": "Petrochemicals are chemicals derived from petroleum or natural gas. Examples include ethene (for plastics), propene, benzene, methanol, and ethanoic acid.",
        "h": "Petrochemicals = chemicals derived from petroleum or natural gas.",
        "yr": "SS1"
      },
      {
        "q": "Natural gas is mainly composed of:",
        "o": ["Ethane", "Propane", "Methane", "Butane"],
        "a": 2,
        "e": "Natural gas is mainly methane (CH₄) — typically 70–90%. It also contains small amounts of ethane, propane, and butane. Methane is the simplest alkane.",
        "h": "Natural gas = mainly methane (CH₄).",
        "yr": "SS1"
      },
      {
        "q": "During fractional distillation of crude oil, what determines at which level a fraction condenses?",
        "o": ["The density of the fraction", "The boiling point of the fraction", "The colour of the fraction", "The molecular formula of the fraction"],
        "a": 1,
        "e": "Each fraction condenses at the level in the column where the temperature equals its boiling point. Fractions with lower boiling points condense higher up in the cooler part of the column.",
        "h": "Fraction condenses where temperature = its boiling point.",
        "yr": "SS1"
      },
      {
        "q": "Diesel is used for:",
        "o": ["Car petrol engines", "Jet aircraft", "Lorries, buses, and trains", "Road surfacing"],
        "a": 2,
        "e": "Diesel fuel (boiling range 250–350°C) is used in diesel engines — found in lorries, buses, trains, and some cars. It is heavier and less volatile than petrol.",
        "h": "Diesel = lorries, buses, trains.",
        "yr": "SS1"
      },
      {
        "q": "Catalytic cracking differs from thermal cracking in that it uses:",
        "o": ["Very high temperatures only", "High pressure and no catalyst", "A catalyst at lower temperatures to improve efficiency", "Water as a reagent"],
        "a": 2,
        "e": "Catalytic cracking uses a catalyst (aluminium oxide and silica zeolites) at lower temperatures (~500°C) to crack heavy fractions more efficiently and produce better quality products than thermal cracking.",
        "h": "Catalytic cracking = catalyst at lower temperature.",
        "yr": "SS1"
      },
      {
        "q": "Petroleum is described as a 'fossil fuel' because:",
        "o": ["It contains fossilised minerals", "It was formed from the remains of organisms millions of years ago", "It produces fossils when burned", "It is found in fossil rock formations only"],
        "a": 1,
        "e": "Fossil fuels (petroleum, coal, natural gas) are formed from the fossilised remains of ancient organisms. Petroleum formed from marine organisms over millions of years under heat and pressure.",
        "h": "Fossil fuel = formed from ancient fossilised organisms.",
        "yr": "SS1"
      },
      {
        "q": "Naphtha, obtained from crude oil, is used mainly as:",
        "o": ["Road surfacing", "Jet fuel", "Chemical feedstock for making petrochemicals", "LPG for cooking"],
        "a": 2,
        "e": "Naphtha (boiling range 75–160°C) is used as a chemical feedstock — raw material for manufacturing petrochemicals such as ethene, propene, benzene, and other important chemicals.",
        "h": "Naphtha = chemical feedstock for petrochemical production.",
        "yr": "SS1"
      },
      {
        "q": "Burning petroleum fuels releases CO₂ and contributes to climate change because:",
        "o": ["CO₂ is a poisonous gas that cools the atmosphere", "CO₂ is a greenhouse gas that traps heat in the atmosphere", "CO₂ reacts with ozone to destroy the ozone layer", "CO₂ dissolves in rain, reducing acidity"],
        "a": 1,
        "e": "CO₂ is a greenhouse gas. It allows sunlight to pass through but absorbs and re-radiates infrared radiation (heat) back to Earth, causing the greenhouse effect and contributing to global warming.",
        "h": "CO₂ is a greenhouse gas → traps heat → global warming.",
        "yr": "SS1"
      },
      {
        "q": "Which petroleum fraction is used to surface roads and waterproof roofs?",
        "o": ["Petrol", "Kerosene", "Diesel", "Bitumen"],
        "a": 3,
        "e": "Bitumen (tar) is the heaviest, most viscous fraction of crude oil with the highest boiling point. It is used for road surfacing (asphalt/tarmac) and waterproofing.",
        "h": "Bitumen = road surfacing and waterproofing.",
        "yr": "SS1"
      },
      {
        "q": "Which substance produced during cracking can be used to make polythene?",
        "o": ["Methane", "Ethane", "Ethene", "Propane"],
        "a": 2,
        "e": "Ethene (C₂H₄) produced during cracking is the monomer for poly(ethene) (polythene) — a widely used plastic. Many ethene molecules join together by addition polymerisation.",
        "h": "Ethene (from cracking) → poly(ethene) (polythene).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Chemical Reactions and Types",
    "topicCode": "SS1-CHEM-23",
    "module": "Physical Chemistry",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">chemical reaction</span> involves the rearrangement of atoms to form new substances with different properties. Reactions can be classified into several types based on what happens to the reactants and products.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Chemical Reactions</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Type</th><th>Description</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Combination/Synthesis</td><td>Two or more substances combine to form one product</td><td>2Mg + O₂ → 2MgO</td></tr>\n      <tr><td>Decomposition</td><td>One substance breaks down into two or more simpler substances</td><td>2H₂O₂ → 2H₂O + O₂</td></tr>\n      <tr><td>Displacement</td><td>A more reactive element displaces a less reactive one from its compound</td><td>Zn + CuSO₄ → ZnSO₄ + Cu</td></tr>\n      <tr><td>Precipitation</td><td>Two solutions react to form an insoluble solid (precipitate)</td><td>AgNO₃ + NaCl → AgCl↓ + NaNO₃</td></tr>\n      <tr><td>Neutralisation</td><td>Acid + base → salt + water</td><td>HCl + NaOH → NaCl + H₂O</td></tr>\n      <tr><td>Combustion</td><td>Substance reacts with oxygen, releasing energy</td><td>CH₄ + 2O₂ → CO₂ + 2H₂O</td></tr>\n      <tr><td>Redox</td><td>Simultaneous oxidation and reduction</td><td>Fe + CuSO₄ → FeSO₄ + Cu</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Signs of a Chemical Reaction</h3>\n<ul class=\"learn-list\">\n  <li>Change in colour</li>\n  <li>Formation of a gas (effervescence)</li>\n  <li>Formation of a precipitate</li>\n  <li>Change in temperature (heat absorbed or released)</li>\n  <li>Change in smell</li>\n  <li>Formation of light or electricity</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Reversible and Irreversible Reactions</h3>\n<p class=\"learn-p\">An <strong>irreversible reaction</strong> goes to completion in one direction. An <strong>reversible reaction</strong> can proceed in both directions and is shown with a double arrow (⇌).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Know all 7 types of chemical reactions with examples. Displacement requires a more reactive element displacing a less reactive one. Precipitation forms an insoluble product (↓). Neutralisation always gives salt + water.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Types of reactions: combination, decomposition, displacement, precipitation, neutralisation, combustion, and redox. Signs of chemical change include gas evolution, precipitate, colour change, and temperature change.\n</div>\n  ",
    "questions": [
      {
        "q": "In the reaction 2Mg + O₂ → 2MgO, the type of reaction is:",
        "o": ["Decomposition", "Displacement", "Combination (synthesis)", "Neutralisation"],
        "a": 2,
        "e": "Two substances (Mg and O₂) combine to form one product (MgO). This is a combination (synthesis) reaction — two reactants forming one product.",
        "h": "Two → one = combination/synthesis reaction.",
        "yr": "SS1"
      },
      {
        "q": "The decomposition of hydrogen peroxide: 2H₂O₂ → 2H₂O + O₂ is an example of:",
        "o": ["Combination reaction", "Decomposition reaction", "Displacement reaction", "Neutralisation reaction"],
        "a": 1,
        "e": "One reactant (H₂O₂) breaks down into two simpler products (H₂O and O₂). This is a decomposition reaction — one substance splitting into two or more.",
        "h": "One reactant → two products = decomposition.",
        "yr": "SS1"
      },
      {
        "q": "In a displacement reaction, the displacing element must be:",
        "o": ["Less reactive than the element it displaces", "More reactive than the element it displaces", "The same element as the one displaced", "A noble gas"],
        "a": 1,
        "e": "In a displacement reaction, a more reactive element replaces a less reactive element from its compound. For example, Zn displaces Cu from CuSO₄ because Zn is more reactive than Cu.",
        "h": "More reactive element displaces less reactive one.",
        "yr": "SS1"
      },
      {
        "q": "The reaction Zn + CuSO₄ → ZnSO₄ + Cu is a:",
        "o": ["Combination reaction", "Decomposition reaction", "Displacement reaction", "Precipitation reaction"],
        "a": 2,
        "e": "Zinc (more reactive) displaces copper (less reactive) from copper sulfate solution. This is a displacement reaction — also a redox reaction.",
        "h": "Zn displaces Cu from solution = displacement reaction.",
        "yr": "SS1"
      },
      {
        "q": "When AgNO₃(aq) is added to NaCl(aq), a white precipitate of AgCl forms. This is a:",
        "o": ["Displacement reaction", "Decomposition reaction", "Precipitation reaction", "Neutralisation reaction"],
        "a": 2,
        "e": "AgNO₃ + NaCl → AgCl↓ + NaNO₃. The insoluble white precipitate of silver chloride (AgCl) forms when the two solutions are mixed. This is a precipitation reaction.",
        "h": "Insoluble solid formed when two solutions mix = precipitation.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a sign that a chemical reaction has occurred?",
        "o": ["A substance dissolves in water", "A change in temperature, colour, or gas evolution", "Ice melts when heated", "Salt is dissolved in water"],
        "a": 1,
        "e": "Signs of chemical reactions include: temperature change, colour change, gas evolution, precipitate formation, change in smell, or light/electricity production. Physical changes like dissolving or melting are not chemical reactions.",
        "h": "Chemical signs: temperature change, colour change, gas, precipitate.",
        "yr": "SS1"
      },
      {
        "q": "Combustion is a reaction in which a substance:",
        "o": ["Breaks down into simpler substances", "Combines with oxygen releasing energy", "Displaces another element", "Forms a precipitate"],
        "a": 1,
        "e": "Combustion is the reaction of a substance with oxygen to release energy (heat and light). Complete combustion of hydrocarbons produces CO₂ and H₂O.",
        "h": "Combustion = reaction with oxygen releasing heat/light.",
        "yr": "SS1"
      },
      {
        "q": "The double arrow (⇌) in a chemical equation indicates:",
        "o": ["The reaction is very fast", "The reaction is reversible and can proceed in both directions", "The reaction is irreversible", "The reaction produces two products only"],
        "a": 1,
        "e": "The double arrow (⇌) shows a reversible reaction — one that can proceed in both the forward and backward directions. At equilibrium, both forward and reverse reactions occur at equal rates.",
        "h": "⇌ = reversible reaction (both directions).",
        "yr": "SS1"
      },
      {
        "q": "The thermal decomposition of calcium carbonate is: CaCO₃ → CaO + CO₂. What type of reaction is this?",
        "o": ["Combination", "Displacement", "Combustion", "Decomposition"],
        "a": 3,
        "e": "One reactant (CaCO₃) breaks down into two products (CaO and CO₂) when heated. This is a decomposition reaction.",
        "h": "One reactant → two products = decomposition.",
        "yr": "SS1"
      },
      {
        "q": "The reaction HCl + NaOH → NaCl + H₂O is a:",
        "o": ["Decomposition reaction", "Displacement reaction", "Neutralisation reaction", "Combustion reaction"],
        "a": 2,
        "e": "An acid (HCl) reacts with a base (NaOH) to form a salt (NaCl) and water (H₂O). This is neutralisation — a specific type of combination/double displacement reaction.",
        "h": "Acid + base → salt + water = neutralisation.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following pairs forms a precipitate when their solutions are mixed?",
        "o": ["NaCl and KNO₃", "BaCl₂ and Na₂SO₄", "NaOH and KCl", "HCl and NaNO₃"],
        "a": 1,
        "e": "BaCl₂ + Na₂SO₄ → BaSO₄↓ + 2NaCl. Barium sulfate (BaSO₄) is an insoluble white precipitate formed when these solutions mix.",
        "h": "BaCl₂ + Na₂SO₄ → BaSO₄↓ (white precipitate).",
        "yr": "SS1"
      },
      {
        "q": "An irreversible reaction is one that:",
        "o": ["Proceeds in both directions", "Goes to completion in one direction only", "Has equal rates in both directions", "Produces no products"],
        "a": 1,
        "e": "An irreversible reaction proceeds in one direction only and goes to completion. The products cannot react to reform the original reactants under normal conditions.",
        "h": "Irreversible = one direction, goes to completion.",
        "yr": "SS1"
      },
      {
        "q": "Electrolysis is an example of which type of reaction?",
        "o": ["Combustion", "Decomposition (using electrical energy)", "Combination", "Displacement"],
        "a": 1,
        "e": "Electrolysis uses electrical energy to decompose an ionic compound into its elements. For example, electrolysis of water: 2H₂O → 2H₂ + O₂. This is an electrochemical decomposition.",
        "h": "Electrolysis = decomposition using electricity.",
        "yr": "SS1"
      },
      {
        "q": "What type of reaction occurs when iron rusts? 4Fe + 3O₂ → 2Fe₂O₃",
        "o": ["Decomposition", "Neutralisation", "Combination (and redox)", "Displacement"],
        "a": 2,
        "e": "Iron reacts with oxygen to form iron oxide — two reactants combining to form one product = combination. It is also a redox reaction (Fe is oxidised).",
        "h": "Iron + oxygen → iron oxide = combination reaction (also redox).",
        "yr": "SS1"
      },
      {
        "q": "In a precipitation reaction, the precipitate is:",
        "o": ["A gas that escapes the solution", "An insoluble solid that forms in solution", "A liquid that separates from the solution", "A dissolved product that changes colour"],
        "a": 1,
        "e": "A precipitate is an insoluble solid that forms when two solutions are mixed and react to produce an insoluble product. It is denoted by (↓) in equations.",
        "h": "Precipitate = insoluble solid formed in solution (↓).",
        "yr": "SS1"
      },
      {
        "q": "The reaction N₂(g) + 3H₂(g) ⇌ 2NH₃(g) is:",
        "o": ["An irreversible reaction", "A combustion reaction", "A reversible reaction at equilibrium", "A decomposition reaction"],
        "a": 2,
        "e": "The double arrow (⇌) indicates this is a reversible reaction. This is the Haber process for making ammonia, which reaches a dynamic equilibrium under industrial conditions.",
        "h": "⇌ means reversible reaction → equilibrium.",
        "yr": "SS1"
      },
      {
        "q": "Which reaction type always produces a salt as one of its products?",
        "o": ["Combustion", "Decomposition", "Neutralisation", "Combination"],
        "a": 2,
        "e": "Neutralisation (acid + base → salt + water) always produces a salt. The type of salt depends on the acid and base used.",
        "h": "Neutralisation always produces a salt + water.",
        "yr": "SS1"
      },
      {
        "q": "The addition of magnesium to dilute hydrochloric acid produces bubbles. This gas is:",
        "o": ["Oxygen", "Carbon dioxide", "Chlorine", "Hydrogen"],
        "a": 3,
        "e": "Mg + 2HCl → MgCl₂ + H₂. Magnesium displaces hydrogen from hydrochloric acid. The bubbles are hydrogen gas — tested with a burning splint (squeaky pop).",
        "h": "Metal + dilute acid → salt + hydrogen gas.",
        "yr": "SS1"
      },
      {
        "q": "Thermal decomposition means decomposition caused by:",
        "o": ["Water", "Electricity", "Heat", "Light"],
        "a": 2,
        "e": "Thermal decomposition is decomposition caused by heat. Example: CaCO₃ → CaO + CO₂ when strongly heated.",
        "h": "Thermal = heat; thermal decomposition = heat breaks compound down.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following reactions is a redox reaction?",
        "o": ["HCl + NaOH → NaCl + H₂O", "AgNO₃ + NaCl → AgCl + NaNO₃", "Fe + CuSO₄ → FeSO₄ + Cu", "CaCO₃ → CaO + CO₂"],
        "a": 2,
        "e": "Fe + CuSO₄ → FeSO₄ + Cu. Fe is oxidised (0 → +2); Cu²⁺ is reduced (+2 → 0). This is both a displacement reaction and a redox reaction.",
        "h": "Fe loses electrons, Cu²⁺ gains electrons = redox.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Energy Changes in Reactions",
    "topicCode": "SS1-CHEM-24",
    "module": "Physical Chemistry",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  All chemical reactions involve <span class=\"learn-keyword\">energy changes</span>. Reactions that release energy to the surroundings are <span class=\"learn-keyword\">exothermic</span>; those that absorb energy from the surroundings are <span class=\"learn-keyword\">endothermic</span>. Energy changes are measured in kilojoules per mole (kJ/mol).\n</div>\n\n<h3 class=\"learn-subheading\">1. Exothermic Reactions</h3>\n<p class=\"learn-p\">Exothermic reactions release energy (heat) to the surroundings. The products have less energy than the reactants. Temperature of surroundings increases.</p>\n<p class=\"learn-p\"><strong>ΔH is negative</strong> for exothermic reactions.</p>\n<p class=\"learn-p\"><strong>Examples:</strong> Combustion, neutralisation, respiration, rusting of iron, condensation, freezing.</p>\n\n<h3 class=\"learn-subheading\">2. Endothermic Reactions</h3>\n<p class=\"learn-p\">Endothermic reactions absorb energy from the surroundings. The products have more energy than the reactants. Temperature of surroundings decreases.</p>\n<p class=\"learn-p\"><strong>ΔH is positive</strong> for endothermic reactions.</p>\n<p class=\"learn-p\"><strong>Examples:</strong> Thermal decomposition, photosynthesis, dissolving ammonium nitrate in water, melting ice.</p>\n\n<h3 class=\"learn-subheading\">3. Energy Level Diagrams</h3>\n<p class=\"learn-p\">Energy level (profile) diagrams show the energy of reactants and products:</p>\n<ul class=\"learn-list\">\n  <li><strong>Exothermic:</strong> Products are at a lower energy level than reactants. ΔH = negative.</li>\n  <li><strong>Endothermic:</strong> Products are at a higher energy level than reactants. ΔH = positive.</li>\n  <li><strong>Activation energy (Eₐ):</strong> The minimum energy required to start a reaction — shown as the hump in the energy profile diagram.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Bond Breaking and Bond Making</h3>\n<p class=\"learn-p\">Energy is <strong>absorbed</strong> when bonds are broken. Energy is <strong>released</strong> when bonds are made. If energy released (bond making) > energy absorbed (bond breaking) → exothermic overall.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Exothermic: releases heat, ΔH negative, surroundings get warmer. Endothermic: absorbs heat, ΔH positive, surroundings get cooler. Bond breaking = absorbs energy; bond making = releases energy. Activation energy = minimum energy to start the reaction.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Exothermic (ΔH < 0): releases heat — combustion, neutralisation, respiration. Endothermic (ΔH > 0): absorbs heat — decomposition, photosynthesis. Bond breaking absorbs energy; bond making releases energy. Activation energy = energy barrier to start the reaction.\n</div>\n  ",
    "questions": [
      {
        "q": "An exothermic reaction is one that:",
        "o": ["Absorbs heat from the surroundings", "Releases heat to the surroundings", "Has no energy change", "Produces a gas only"],
        "a": 1,
        "e": "Exothermic reactions release heat to the surroundings. The temperature of the surroundings increases. Examples: combustion, neutralisation, and respiration.",
        "h": "Exothermic = releases heat; surroundings get warmer.",
        "yr": "SS1"
      },
      {
        "q": "An endothermic reaction is one that:",
        "o": ["Releases heat to the surroundings", "Absorbs heat from the surroundings", "Only occurs at very high temperatures", "Produces a precipitate"],
        "a": 1,
        "e": "Endothermic reactions absorb heat from the surroundings. The temperature of the surroundings decreases. Examples: thermal decomposition, photosynthesis, dissolving ammonium nitrate.",
        "h": "Endothermic = absorbs heat; surroundings get cooler.",
        "yr": "SS1"
      },
      {
        "q": "For an exothermic reaction, the sign of ΔH is:",
        "o": ["Positive (+)", "Negative (−)", "Zero (0)", "Undefined"],
        "a": 1,
        "e": "ΔH is negative for exothermic reactions because the products have less energy than the reactants. Energy is released to the surroundings.",
        "h": "Exothermic: ΔH is negative (−).",
        "yr": "SS1"
      },
      {
        "q": "For an endothermic reaction, the sign of ΔH is:",
        "o": ["Negative (−)", "Positive (+)", "Zero (0)", "Always very large"],
        "a": 1,
        "e": "ΔH is positive for endothermic reactions because the products have more energy than the reactants. Energy is absorbed from the surroundings.",
        "h": "Endothermic: ΔH is positive (+).",
        "yr": "SS1"
      },
      {
        "q": "Combustion is an example of:",
        "o": ["An endothermic reaction", "An exothermic reaction", "A reaction with no energy change", "A reversible reaction"],
        "a": 1,
        "e": "Combustion releases heat and light to the surroundings — it is exothermic. The products (CO₂ and H₂O) are at a lower energy level than the reactants.",
        "h": "Combustion releases heat = exothermic.",
        "yr": "SS1"
      },
      {
        "q": "Photosynthesis is an example of:",
        "o": ["An exothermic reaction", "A combustion reaction", "An endothermic reaction", "A neutralisation reaction"],
        "a": 2,
        "e": "Photosynthesis absorbs light energy from the sun to convert CO₂ and H₂O into glucose and oxygen. It absorbs energy → endothermic.",
        "h": "Photosynthesis absorbs light energy = endothermic.",
        "yr": "SS1"
      },
      {
        "q": "The activation energy of a reaction is:",
        "o": ["The total energy released in the reaction", "The minimum energy required for reactant molecules to react", "The energy difference between reactants and products", "The heat absorbed during bond breaking only"],
        "a": 1,
        "e": "Activation energy (Eₐ) is the minimum energy that reactant molecules must have to collide successfully and undergo a chemical reaction. It is shown as the energy hump in an energy profile diagram.",
        "h": "Activation energy = minimum energy to start the reaction.",
        "yr": "SS1"
      },
      {
        "q": "In an energy level diagram for an exothermic reaction:",
        "o": ["Products are at a higher energy level than reactants", "Products are at the same level as reactants", "Products are at a lower energy level than reactants", "Reactants and products have no energy difference"],
        "a": 2,
        "e": "In an exothermic reaction, the products are at a lower energy level than the reactants. The difference in energy levels = ΔH (which is negative).",
        "h": "Exothermic energy diagram: products lower than reactants.",
        "yr": "SS1"
      },
      {
        "q": "In an energy level diagram for an endothermic reaction:",
        "o": ["Products are lower than reactants", "Products are at the same level as reactants", "Products are higher than reactants", "Activation energy is zero"],
        "a": 2,
        "e": "In an endothermic reaction, the products are at a higher energy level than the reactants. The reaction absorbs energy (ΔH positive).",
        "h": "Endothermic energy diagram: products higher than reactants.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an endothermic process?",
        "o": ["Burning of wood", "Neutralisation of acid with alkali", "Thermal decomposition of calcium carbonate", "Respiration"],
        "a": 2,
        "e": "Thermal decomposition of CaCO₃ requires heat energy input: CaCO₃ → CaO + CO₂. It absorbs energy → endothermic. The other options are all exothermic.",
        "h": "Thermal decomposition requires heat input = endothermic.",
        "yr": "SS1"
      },
      {
        "q": "Breaking a chemical bond requires:",
        "o": ["No energy (bonds are already weak)", "Energy to be released", "Energy to be absorbed", "A catalyst"],
        "a": 2,
        "e": "Bond breaking is always endothermic — energy must be absorbed (input) to break the attractive forces holding atoms together in a bond.",
        "h": "Bond breaking = energy absorbed (endothermic).",
        "yr": "SS1"
      },
      {
        "q": "Forming a new chemical bond:",
        "o": ["Always absorbs energy", "Always releases energy", "Has no energy change", "Only occurs in exothermic reactions"],
        "a": 1,
        "e": "Bond formation is always exothermic — energy is released when atoms come together to form bonds. The stronger the bond formed, the more energy is released.",
        "h": "Bond making = energy released (exothermic).",
        "yr": "SS1"
      },
      {
        "q": "If energy released in bond making is greater than energy absorbed in bond breaking, the reaction is:",
        "o": ["Endothermic", "Exothermic", "Neither exothermic nor endothermic", "Always very slow"],
        "a": 1,
        "e": "If energy released (bond making) > energy absorbed (bond breaking), there is a net release of energy to the surroundings — the reaction is exothermic.",
        "h": "Bond making > bond breaking → net energy release = exothermic.",
        "yr": "SS1"
      },
      {
        "q": "Dissolving ammonium nitrate (NH₄NO₃) in water causes the water to cool. This indicates the process is:",
        "o": ["Exothermic", "Endothermic", "Neutral", "Combustion"],
        "a": 1,
        "e": "When NH₄NO₃ dissolves, it absorbs heat from the water (making the water cooler). This heat absorption indicates the process is endothermic.",
        "h": "Surroundings cool down → heat absorbed = endothermic.",
        "yr": "SS1"
      },
      {
        "q": "The heat of neutralisation is always approximately the same (about −57 kJ/mol) for strong acid-strong base reactions because:",
        "o": ["All acids and bases have the same molar mass", "The net ionic reaction is always H⁺ + OH⁻ → H₂O regardless of the specific acid or base", "The activation energy is always the same", "Neutralisation is an endothermic reaction"],
        "a": 1,
        "e": "For strong acid-strong base reactions, the net ionic equation is always H⁺ + OH⁻ → H₂O. Since the same bond is formed, the energy released is always approximately the same (~−57 kJ/mol).",
        "h": "H⁺ + OH⁻ → H₂O is always the same reaction = same ΔH.",
        "yr": "SS1"
      },
      {
        "q": "A hand warmer uses an exothermic reaction. When activated, the temperature of the hand warmer:",
        "o": ["Decreases", "Stays the same", "Increases", "First decreases then increases"],
        "a": 2,
        "e": "An exothermic reaction releases heat to the surroundings. The hand warmer's temperature increases, releasing heat that warms the hand.",
        "h": "Exothermic → releases heat → temperature increases.",
        "yr": "SS1"
      },
      {
        "q": "Respiration (C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + energy) is:",
        "o": ["Endothermic because it uses glucose", "Exothermic because it releases energy", "Neither exothermic nor endothermic", "Endothermic because it produces CO₂"],
        "a": 1,
        "e": "Respiration releases energy (ATP and heat) — it is exothermic. The equation shows energy as a product, confirming energy is released to the surroundings.",
        "h": "Respiration releases energy = exothermic.",
        "yr": "SS1"
      },
      {
        "q": "A catalyst reduces activation energy. This means a reaction with a catalyst:",
        "o": ["Releases more energy overall", "Requires less energy to start but the overall ΔH is unchanged", "Absorbs more energy", "Changes from exothermic to endothermic"],
        "a": 1,
        "e": "A catalyst lowers the activation energy, allowing more molecules to react. However, the overall energy change (ΔH) between reactants and products remains the same.",
        "h": "Catalyst lowers Eₐ only; ΔH is unchanged.",
        "yr": "SS1"
      },
      {
        "q": "Melting of ice is classified as:",
        "o": ["Exothermic because ice is cold", "Endothermic because it absorbs heat to break bonds", "Neither — it is a physical process with no energy change", "Exothermic because water forms"],
        "a": 1,
        "e": "Melting absorbs heat to break the hydrogen bonds holding ice crystals together. It is an endothermic physical process.",
        "h": "Melting absorbs heat to break bonds = endothermic.",
        "yr": "SS1"
      },
      {
        "q": "In a neutralisation experiment, the temperature of the solution rises by 5°C. This shows neutralisation is:",
        "o": ["Endothermic", "Exothermic", "Reversible", "A physical change"],
        "a": 1,
        "e": "The temperature rise of the solution shows that heat is being released to the surroundings — neutralisation is exothermic.",
        "h": "Temperature rises = heat released = exothermic.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Rates of Chemical Reactions",
    "topicCode": "SS1-CHEM-25",
    "module": "Physical Chemistry",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The <span class=\"learn-keyword\">rate of a chemical reaction</span> measures how fast reactants are converted to products. Understanding reaction rates is important for controlling industrial processes, food preservation, medicine, and everyday chemistry.\n</div>\n\n<h3 class=\"learn-subheading\">1. Definition of Reaction Rate</h3>\n<p class=\"learn-p\">Rate of reaction = change in concentration (or amount) of reactant or product ÷ time taken.</p>\n<p class=\"learn-p\">Rate can be measured by: decrease in mass of reactant, increase in gas volume, change in colour or turbidity.</p>\n\n<h3 class=\"learn-subheading\">2. Factors Affecting Reaction Rate</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Factor</th><th>Effect on rate</th><th>Reason</th></tr></thead>\n    <tbody>\n      <tr><td>Temperature</td><td>Increases</td><td>More kinetic energy → more frequent, more energetic collisions</td></tr>\n      <tr><td>Concentration</td><td>Increases</td><td>More particles per unit volume → more frequent collisions</td></tr>\n      <tr><td>Pressure (gases)</td><td>Increases</td><td>Higher pressure = higher concentration of gas molecules</td></tr>\n      <tr><td>Surface area</td><td>Increases</td><td>More exposed surface = more collision opportunities</td></tr>\n      <tr><td>Catalyst</td><td>Increases</td><td>Provides alternative pathway with lower activation energy</td></tr>\n      <tr><td>Light</td><td>Can increase</td><td>Provides energy for photochemical reactions</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Collision Theory</h3>\n<p class=\"learn-p\">For a reaction to occur, reactant particles must:</p>\n<ol class=\"learn-list\">\n  <li>Collide with each other (collision must occur)</li>\n  <li>Have energy ≥ activation energy (successful collision)</li>\n  <li>Have the correct orientation (for complex molecules)</li>\n</ol>\n<p class=\"learn-p\">A reaction can only occur if there is a <strong>successful collision</strong> — one with sufficient energy (≥ Eₐ) and correct orientation.</p>\n\n<h3 class=\"learn-subheading\">4. Catalysts</h3>\n<p class=\"learn-p\">A catalyst increases reaction rate by providing an alternative reaction pathway with a lower activation energy. The catalyst is not consumed in the reaction. Examples: MnO₂ catalyses H₂O₂ decomposition; Fe catalyses the Haber process; enzymes in biological systems.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> All factors that increase rate do so by increasing the frequency or energy of collisions. Temperature is the most effective: even a 10°C rise roughly doubles the reaction rate. Catalysts don't change ΔH — they only lower activation energy.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Rate of reaction depends on temperature, concentration, pressure, surface area, catalysts, and light. Collision theory: successful collisions require sufficient energy (≥ Eₐ) and correct orientation. Catalysts lower Eₐ without being consumed.\n</div>\n  ",
    "questions": [
      {
        "q": "The rate of a chemical reaction measures:",
        "o": ["The total amount of product formed", "How quickly reactants are converted to products per unit time", "The energy released in the reaction", "The concentration of the catalyst"],
        "a": 1,
        "e": "Reaction rate = change in amount of reactant or product ÷ time. It measures how quickly the reaction proceeds.",
        "h": "Rate = how quickly reactants → products per unit time.",
        "yr": "SS1"
      },
      {
        "q": "Increasing the temperature increases the reaction rate because:",
        "o": ["Particles become smaller and collide more easily", "Particles have more kinetic energy, leading to more frequent and more energetic collisions", "The activation energy increases", "Bonds become weaker at higher temperatures"],
        "a": 1,
        "e": "Higher temperature gives particles more kinetic energy. They move faster, collide more frequently, and a greater proportion of collisions have energy ≥ activation energy — increasing the rate.",
        "h": "Higher T = more KE = more frequent, more energetic collisions.",
        "yr": "SS1"
      },
      {
        "q": "Increasing the concentration of a reactant increases the reaction rate because:",
        "o": ["Each particle has more energy", "There are more particles per unit volume, increasing collision frequency", "The activation energy decreases", "The temperature increases automatically"],
        "a": 1,
        "e": "Higher concentration means more reactant particles per unit volume. This increases the frequency of collisions between particles, increasing the reaction rate.",
        "h": "Higher concentration = more particles = more frequent collisions.",
        "yr": "SS1"
      },
      {
        "q": "Powdering a solid reactant increases the reaction rate because:",
        "o": ["Powder particles are lighter and move faster", "Smaller particles have a greater surface area exposed to collision", "The concentration of the solid increases", "Powder has a lower activation energy"],
        "a": 1,
        "e": "Dividing a solid into powder increases its surface area. More surface area means more reactant particles are exposed and available for collisions with other reactants — increasing the rate.",
        "h": "Smaller particles = greater surface area = more collisions.",
        "yr": "SS1"
      },
      {
        "q": "A catalyst increases reaction rate by:",
        "o": ["Increasing the activation energy", "Increasing the temperature of the reaction", "Providing an alternative pathway with lower activation energy", "Increasing the concentration of reactants"],
        "a": 2,
        "e": "A catalyst provides an alternative reaction pathway with a lower activation energy. More particles then have energy ≥ the (lower) activation energy, so more successful collisions occur per unit time.",
        "h": "Catalyst lowers activation energy → more successful collisions.",
        "yr": "SS1"
      },
      {
        "q": "A catalyst is described as 'not consumed' in a reaction because:",
        "o": ["It does not participate in the reaction at all", "It is regenerated at the end of the reaction", "It is always a solid", "It reacts with the product, not the reactant"],
        "a": 1,
        "e": "A catalyst participates in the reaction mechanism but is regenerated (reformed) at the end. Its net amount does not change — it can be recovered and reused.",
        "h": "Catalyst regenerated at end = not consumed overall.",
        "yr": "SS1"
      },
      {
        "q": "According to collision theory, a reaction occurs only when:",
        "o": ["Any collision occurs between reactant particles", "Particles collide with energy greater than or equal to the activation energy", "Particles reach the boiling point", "Particles dissolve in water"],
        "a": 1,
        "e": "Collision theory states that reactions occur only when particles collide with sufficient energy (≥ activation energy) and the correct orientation. Not all collisions lead to reactions.",
        "h": "Successful collision: energy ≥ Eₐ and correct orientation.",
        "yr": "SS1"
      },
      {
        "q": "Increasing gas pressure increases reaction rate because:",
        "o": ["Pressure breaks chemical bonds", "Gas molecules have higher energy at higher pressure", "Higher pressure increases the concentration of gas molecules, increasing collision frequency", "Pressure lowers the activation energy"],
        "a": 2,
        "e": "Higher pressure compresses the gas into a smaller volume, increasing the concentration of gas molecules. This leads to more frequent collisions and a faster reaction rate.",
        "h": "Higher pressure = higher gas concentration = more collisions.",
        "yr": "SS1"
      },
      {
        "q": "The reaction H₂O₂ → H₂O + ½O₂ is slow at room temperature. Adding MnO₂ speeds it up. MnO₂ is:",
        "o": ["A reactant that is used up", "A product of the reaction", "A catalyst", "A solvent"],
        "a": 2,
        "e": "MnO₂ (manganese dioxide) is a catalyst for the decomposition of hydrogen peroxide. It speeds up the reaction without being consumed — it can be recovered at the end.",
        "h": "MnO₂ speeds up H₂O₂ decomposition without being consumed = catalyst.",
        "yr": "SS1"
      },
      {
        "q": "Why does food decay faster in warm temperatures than in a refrigerator?",
        "o": ["Warm temperatures lower activation energy permanently", "Higher temperature increases the rate of bacterial and chemical decay reactions", "Warm temperatures increase pressure on the food", "Cold temperatures increase the concentration of bacteria"],
        "a": 1,
        "e": "Warm temperatures increase the kinetic energy of molecules and bacteria, speeding up the chemical and biological reactions responsible for food decay. Refrigeration slows these reactions down.",
        "h": "Higher temperature = faster decay reactions = food spoils quicker.",
        "yr": "SS1"
      },
      {
        "q": "Enzymes are biological catalysts. They work best at:",
        "o": ["Very high temperatures (above 80°C)", "Very low temperatures (below 0°C)", "A specific optimum temperature (usually around 37°C for human enzymes)", "Any temperature equally"],
        "a": 2,
        "e": "Enzymes have an optimum temperature at which they are most active. For human body enzymes, this is about 37°C. Above or below this, activity decreases; above ~60°C enzymes denature (permanently lose shape).",
        "h": "Enzymes have an optimum temperature (~37°C for human enzymes).",
        "yr": "SS1"
      },
      {
        "q": "Marble chips (CaCO₃) react with HCl more quickly when crushed into powder than as large lumps because:",
        "o": ["Powder has a higher concentration than lumps", "Powder has a greater surface area exposed to the acid", "Powder is a catalyst for the reaction", "Powder has a lower activation energy"],
        "a": 1,
        "e": "Crushing marble chips into powder greatly increases the total surface area in contact with the acid. More surface area means more acid particles can collide with carbonate particles per unit time.",
        "h": "Powder = more surface area = more collisions = faster reaction.",
        "yr": "SS1"
      },
      {
        "q": "The rate of a reaction can be measured by monitoring:",
        "o": ["Only the temperature change", "Only the colour change", "Changes in mass, gas volume, colour, or turbidity over time", "Only the concentration of the catalyst"],
        "a": 2,
        "e": "Rate can be measured by several methods depending on the reaction: loss in mass (gas escaping), gas collection, colour change (colorimetry), or formation of turbidity (precipitate).",
        "h": "Rate measured by: mass loss, gas volume, colour change, turbidity.",
        "yr": "SS1"
      },
      {
        "q": "Light can increase the rate of some reactions. This is called:",
        "o": ["Thermal activation", "Photocatalysis or photochemical reaction", "Electrolysis", "Fermentation"],
        "a": 1,
        "e": "Some reactions are initiated or accelerated by light — these are photochemical reactions. Example: the reaction of H₂ and Cl₂ in sunlight, and photosynthesis.",
        "h": "Light-driven reactions = photochemical reactions.",
        "yr": "SS1"
      },
      {
        "q": "A 10°C rise in temperature roughly doubles the reaction rate. This is because:",
        "o": ["Activation energy doubles at higher temperatures", "Twice as many particles have energy exceeding the activation energy", "Concentration doubles with temperature", "Catalyst effectiveness doubles"],
        "a": 1,
        "e": "A 10°C rise approximately doubles the number of particles with energy ≥ Eₐ. This doubles the frequency of successful collisions and therefore approximately doubles the reaction rate.",
        "h": "10°C rise → ~twice as many particles exceed Eₐ → ~double rate.",
        "yr": "SS1"
      },
      {
        "q": "A catalyst does NOT change which of the following?",
        "o": ["Activation energy", "Reaction pathway", "Overall energy change ΔH", "Reaction rate"],
        "a": 2,
        "e": "A catalyst lowers activation energy and provides an alternative pathway, increasing reaction rate. However, it does not change the overall energy change (ΔH) — the reactants and products remain the same.",
        "h": "Catalyst changes Eₐ and rate, NOT ΔH.",
        "yr": "SS1"
      },
      {
        "q": "For a reaction between marble (CaCO₃) and HCl, which experiment would give the fastest initial rate?",
        "o": ["Large marble lumps + dilute HCl at 20°C", "Marble powder + dilute HCl at 20°C", "Large marble lumps + concentrated HCl at 20°C", "Marble powder + concentrated HCl at 40°C"],
        "a": 3,
        "e": "Marble powder (largest surface area) + concentrated HCl (highest concentration) + 40°C (highest temperature) combines all three rate-increasing factors — giving the fastest initial rate.",
        "h": "Powder + concentrated + higher temperature = all factors maximised.",
        "yr": "SS1"
      },
      {
        "q": "In industry, the Haber process uses iron as a catalyst to make ammonia. This is because:",
        "o": ["Iron reacts with nitrogen to form ammonia", "Iron lowers the activation energy, allowing ammonia to be produced faster at lower temperatures", "Iron increases the equilibrium yield of ammonia", "Iron provides nitrogen for the reaction"],
        "a": 1,
        "e": "Iron catalyst in the Haber process lowers the activation energy for N₂ + 3H₂ → 2NH₃, allowing the reaction to proceed at a commercially viable rate at lower temperatures (~450°C).",
        "h": "Fe catalyst lowers Eₐ in Haber process → faster ammonia production.",
        "yr": "SS1"
      },
      {
        "q": "Successful collisions in collision theory require:",
        "o": ["Any energy and any direction", "Energy less than activation energy", "Energy equal to or greater than activation energy and correct orientation", "High temperature only"],
        "a": 2,
        "e": "A successful collision requires two conditions: (1) energy ≥ activation energy to break bonds, and (2) correct orientation so atoms are positioned to form new bonds.",
        "h": "Successful collision: E ≥ Eₐ AND correct orientation.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following would NOT increase the rate of a reaction?",
        "o": ["Adding a catalyst", "Increasing temperature", "Adding an inert gas at constant volume (for gas reactions)", "Increasing concentration of reactants"],
        "a": 2,
        "e": "Adding an inert gas at constant volume does not change the partial pressures or concentrations of the reactants, so it has no effect on reaction rate.",
        "h": "Inert gas at constant volume = no effect on rate.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Reversible Reactions and Chemical Equilibrium",
    "topicCode": "SS1-CHEM-26",
    "module": "Physical Chemistry",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Some chemical reactions are <span class=\"learn-keyword\">reversible</span> — they can proceed in both the forward and reverse directions. When the rates of the forward and reverse reactions become equal, the system reaches <span class=\"learn-keyword\">dynamic equilibrium</span>. Understanding equilibrium is essential for industrial processes like the Haber process.\n</div>\n\n<h3 class=\"learn-subheading\">1. Reversible Reactions</h3>\n<p class=\"learn-p\">A reversible reaction can proceed in both directions. It is shown with a double arrow: A + B ⇌ C + D.</p>\n<p class=\"learn-p\"><strong>Example:</strong> N₂(g) + 3H₂(g) ⇌ 2NH₃(g) (Haber process)</p>\n<p class=\"learn-p\"><strong>Example:</strong> CuSO₄·5H₂O ⇌ CuSO₄ + 5H₂O (on heating/cooling)</p>\n\n<h3 class=\"learn-subheading\">2. Dynamic Equilibrium</h3>\n<p class=\"learn-p\">At dynamic equilibrium:</p>\n<ul class=\"learn-list\">\n  <li>The rate of the forward reaction = rate of the reverse reaction.</li>\n  <li>Concentrations of reactants and products remain constant (but not necessarily equal).</li>\n  <li>The reaction appears to have stopped but both forward and reverse reactions are still occurring.</li>\n  <li>It is a dynamic (not static) equilibrium — both reactions continue simultaneously.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Le Chatelier's Principle</h3>\n<p class=\"learn-p\"><em>\"If a change is applied to a system at equilibrium, the system will respond in a way that opposes the change and re-establishes equilibrium.\"</em></p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Change applied</th><th>System response</th></tr></thead>\n    <tbody>\n      <tr><td>Increase concentration of reactant</td><td>Equilibrium shifts forward (→) to use up extra reactant</td></tr>\n      <tr><td>Increase temperature</td><td>Equilibrium shifts in endothermic direction</td></tr>\n      <tr><td>Increase pressure (gas reactions)</td><td>Equilibrium shifts toward side with fewer gas moles</td></tr>\n      <tr><td>Add catalyst</td><td>Reaches equilibrium faster; yield unchanged</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Dynamic equilibrium: forward rate = reverse rate; concentrations constant. Le Chatelier: system opposes the change. For N₂ + 3H₂ ⇌ 2NH₃ (ΔH = −92 kJ/mol): high pressure → more NH₃; low temperature → more NH₃ (but slower rate). Catalyst reaches equilibrium faster, not more NH₃.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Reversible reactions shown with ⇌. Dynamic equilibrium: both reactions ongoing at equal rates; concentrations constant. Le Chatelier's principle: system opposes applied change. Haber process: N₂ + 3H₂ ⇌ 2NH₃ (compromise conditions: ~450°C, 200 atm, Fe catalyst).\n</div>\n  ",
    "questions": [
      {
        "q": "A reversible reaction is shown in an equation using:",
        "o": ["A single forward arrow (→)", "An equals sign (=)", "A double arrow (⇌)", "A dotted arrow"],
        "a": 2,
        "e": "A reversible reaction is written with a double arrow (⇌), showing that the reaction can proceed in both forward and reverse directions.",
        "h": "⇌ = reversible reaction symbol.",
        "yr": "SS1"
      },
      {
        "q": "At dynamic equilibrium, the concentrations of reactants and products:",
        "o": ["Are zero (all reactant is used up)", "Are equal to each other", "Remain constant over time", "Continue to change indefinitely"],
        "a": 2,
        "e": "At dynamic equilibrium, concentrations of reactants and products remain constant over time — the forward and reverse reaction rates are equal, so there is no net change.",
        "h": "Equilibrium: concentrations remain constant (not equal).",
        "yr": "SS1"
      },
      {
        "q": "At dynamic equilibrium, which of the following is true?",
        "o": ["The reaction has stopped completely", "Only the forward reaction is occurring", "Both forward and reverse reactions occur at equal rates", "Concentrations of reactants and products are equal"],
        "a": 2,
        "e": "Dynamic equilibrium means both forward and reverse reactions are still occurring simultaneously at equal rates. The word 'dynamic' reflects this ongoing activity.",
        "h": "Dynamic equilibrium: BOTH reactions ongoing at equal rates.",
        "yr": "SS1"
      },
      {
        "q": "Le Chatelier's Principle states that when a change is applied to a system at equilibrium, the system will:",
        "o": ["Amplify the change", "Stop all reactions", "Oppose the change and re-establish equilibrium", "Always shift to produce more products"],
        "a": 2,
        "e": "Le Chatelier's Principle: a system at equilibrium responds to any change by shifting in the direction that opposes the change and restores equilibrium.",
        "h": "Le Chatelier: system opposes the applied change.",
        "yr": "SS1"
      },
      {
        "q": "In the reaction N₂ + 3H₂ ⇌ 2NH₃, increasing pressure shifts the equilibrium because:",
        "o": ["Pressure always favours the reverse reaction", "The system shifts to the side with fewer moles of gas to reduce pressure", "Pressure always increases temperature", "Pressure removes the catalyst"],
        "a": 1,
        "e": "Increasing pressure shifts equilibrium toward the side with fewer moles of gas. Left side: 1+3=4 moles gas. Right side: 2 moles gas. Equilibrium shifts right (forward) → more NH₃.",
        "h": "High pressure → fewer gas moles side (right, 2 moles NH₃).",
        "yr": "SS1"
      },
      {
        "q": "The Haber process produces ammonia: N₂ + 3H₂ ⇌ 2NH₃ (ΔH = −92 kJ/mol). Increasing temperature shifts the equilibrium:",
        "o": ["Forward (to produce more NH₃)", "Backward (to produce more N₂ and H₂)", "Has no effect", "Only if pressure is also increased"],
        "a": 1,
        "e": "The forward reaction is exothermic (ΔH = −92 kJ/mol). Increasing temperature favours the endothermic (reverse) reaction. Equilibrium shifts backward → less NH₃ produced.",
        "h": "Forward is exothermic; raising T shifts to endothermic direction = backward.",
        "yr": "SS1"
      },
      {
        "q": "Adding a catalyst to a reaction at equilibrium:",
        "o": ["Increases the yield of the forward reaction", "Shifts equilibrium toward the products", "Allows equilibrium to be reached faster but does not change the yield", "Changes the equilibrium constant"],
        "a": 2,
        "e": "A catalyst speeds up both the forward and reverse reactions equally, so equilibrium is reached faster. The position of equilibrium (and therefore yield) is unchanged.",
        "h": "Catalyst: equilibrium reached faster; yield/position unchanged.",
        "yr": "SS1"
      },
      {
        "q": "In the reaction 2SO₂(g) + O₂(g) ⇌ 2SO₃(g), increasing pressure would shift the equilibrium:",
        "o": ["Backward (to produce more SO₂ and O₂)", "Forward (to produce more SO₃)", "Have no effect", "Forward only if a catalyst is present"],
        "a": 1,
        "e": "Left side: 2+1 = 3 moles gas. Right side: 2 moles gas. Increasing pressure shifts equilibrium toward fewer moles (right) → more SO₃.",
        "h": "3 mol gas → 2 mol gas: high pressure shifts right → more SO₃.",
        "yr": "SS1"
      },
      {
        "q": "If more N₂ is added to N₂ + 3H₂ ⇌ 2NH₃ at equilibrium, the equilibrium shifts:",
        "o": ["Backward, producing more N₂ and H₂", "Forward, producing more NH₃", "Has no effect", "Backward because N₂ is added"],
        "a": 1,
        "e": "Increasing the concentration of a reactant (N₂) shifts equilibrium forward to use up the extra N₂, producing more NH₃ (Le Chatelier's principle).",
        "h": "More N₂ → equilibrium shifts forward → more NH₃.",
        "yr": "SS1"
      },
      {
        "q": "The Haber process uses compromise conditions of ~450°C. This temperature is chosen because:",
        "o": ["Higher temperatures always produce more ammonia", "Lower temperatures give a faster rate but less ammonia", "450°C gives an acceptable yield and a commercially viable rate", "It is the temperature at which the catalyst works best only"],
        "a": 2,
        "e": "Lower T gives more NH₃ (higher yield) but very slow rate. Higher T gives faster rate but less NH₃. 450°C is a compromise — acceptable yield at a commercially viable reaction rate.",
        "h": "450°C = compromise between yield (lower T better) and rate (higher T better).",
        "yr": "SS1"
      },
      {
        "q": "For H₂(g) + I₂(g) ⇌ 2HI(g), changing pressure has no effect because:",
        "o": ["The reaction is irreversible", "There are equal moles of gas on both sides (1+1=2 and 2=2)", "HI is a solid", "The reaction is endothermic"],
        "a": 1,
        "e": "Pressure change only affects equilibrium if there is a different number of gas moles on each side. Here: left = 1+1 = 2 moles gas; right = 2 moles gas. Equal moles → no pressure effect.",
        "h": "Equal gas moles on both sides → pressure change has no effect.",
        "yr": "SS1"
      },
      {
        "q": "The equilibrium CuSO₄·5H₂O (blue) ⇌ CuSO₄ (white) + 5H₂O can be shifted backward by:",
        "o": ["Heating the blue crystals", "Adding water to white anhydrous CuSO₄", "Reducing pressure", "Adding more CuSO₄"],
        "a": 1,
        "e": "Adding water to white anhydrous CuSO₄ shifts equilibrium backward (toward CuSO₄·5H₂O). The crystals turn blue as the hydrated form is reformed.",
        "h": "Adding water → backward shift → blue CuSO₄·5H₂O forms.",
        "yr": "SS1"
      },
      {
        "q": "In an endothermic reaction at equilibrium, increasing temperature:",
        "o": ["Shifts equilibrium backward (toward reactants)", "Has no effect on equilibrium", "Shifts equilibrium forward (toward products)", "Only affects the rate, not the position"],
        "a": 2,
        "e": "For an endothermic reaction, increasing temperature favours the forward (endothermic) direction — more products are formed. Equilibrium shifts right.",
        "h": "Endothermic reaction: raise T → shifts forward → more products.",
        "yr": "SS1"
      },
      {
        "q": "Which condition in the Haber process maximises the yield of ammonia?",
        "o": ["High temperature and low pressure", "Low temperature and high pressure", "High temperature and high pressure", "Low temperature and low pressure"],
        "a": 1,
        "e": "The forward reaction N₂+3H₂→2NH₃ is exothermic and produces fewer moles of gas. Low temperature (favours exothermic direction) and high pressure (favours fewer gas moles = right side) both maximise yield.",
        "h": "Maximum NH₃ yield: low temperature + high pressure.",
        "yr": "SS1"
      },
      {
        "q": "At equilibrium, the concentrations of reactants and products are constant because:",
        "o": ["No reactions are taking place", "Both reactions have stopped", "The rate of the forward reaction equals the rate of the reverse reaction", "All reactants have been converted to products"],
        "a": 2,
        "e": "Concentrations remain constant because the forward reaction converts reactants to products at the same rate the reverse reaction converts products back to reactants. There is no net change.",
        "h": "Constant concentrations because forward rate = reverse rate.",
        "yr": "SS1"
      },
      {
        "q": "Removing a product from a reaction at equilibrium causes the equilibrium to:",
        "o": ["Shift backward toward reactants", "Shift forward to replace the removed product", "Have no effect", "Stop completely"],
        "a": 1,
        "e": "Removing a product (decreasing its concentration) causes the equilibrium to shift forward to replace the removed product — Le Chatelier's principle.",
        "h": "Remove product → equilibrium shifts forward to replace it.",
        "yr": "SS1"
      },
      {
        "q": "The iron catalyst in the Haber process is used at ~450°C because:",
        "o": ["Iron reacts with nitrogen at this temperature", "The catalyst is most active (least deactivated) at this temperature", "450°C is the minimum temperature needed to melt iron", "The catalyst only works above 1000°C"],
        "a": 1,
        "e": "The iron catalyst is most active in the 400–500°C range. Below this, it is less effective; above this, it can be deactivated (poisoned). 450°C balances catalyst activity with a reasonable reaction rate.",
        "h": "Fe catalyst most active around 450°C.",
        "yr": "SS1"
      },
      {
        "q": "Which statement correctly describes dynamic equilibrium?",
        "o": ["The reaction has reached completion", "The concentrations are equal on both sides", "Both reactions proceed at equal rates with constant concentrations", "Only the products are present"],
        "a": 2,
        "e": "Dynamic equilibrium: both forward and reverse reactions proceed at the same rate; concentrations of reactants and products remain constant (not necessarily equal).",
        "h": "Both reactions at equal rates + constant concentrations = dynamic equilibrium.",
        "yr": "SS1"
      },
      {
        "q": "In the reaction PCl₅(g) ⇌ PCl₃(g) + Cl₂(g), increasing pressure shifts the equilibrium:",
        "o": ["Forward (toward PCl₃ + Cl₂)", "Backward (toward PCl₅)", "Has no effect", "Only if temperature is also increased"],
        "a": 1,
        "e": "Left: 1 mole gas. Right: 1+1 = 2 moles gas. Increasing pressure shifts equilibrium toward fewer moles (left, 1 mole) → backward, producing more PCl₅.",
        "h": "1 mol ← 2 mol; high P → backward (fewer moles = left).",
        "yr": "SS1"
      },
      {
        "q": "Why is the Haber process important to Nigeria and the world?",
        "o": ["It produces petrol for cars", "It produces ammonia used to make fertilisers which feed billions of people", "It removes CO₂ from the atmosphere", "It produces drinking water from seawater"],
        "a": 1,
        "e": "The Haber process produces ammonia (NH₃), which is converted to nitrogen-based fertilisers (e.g., urea, ammonium nitrate). These fertilisers are essential for agricultural productivity — feeding billions worldwide.",
        "h": "Haber process → NH₃ → fertilisers → feeds the world.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Metals and Non-Metals",
    "topicCode": "SS1-CHEM-27",
    "module": "Inorganic Chemistry",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Elements in the Periodic Table are broadly classified as <span class=\"learn-keyword\">metals</span>, <span class=\"learn-keyword\">non-metals</span>, or metalloids (semi-metals). Understanding the properties and reactions of metals and non-metals is fundamental to inorganic chemistry and has wide practical applications.\n</div>\n\n<h3 class=\"learn-subheading\">1. Properties of Metals</h3>\n<ul class=\"learn-list\">\n  <li>Good conductors of heat and electricity</li>\n  <li>Malleable (can be beaten into sheets) and ductile (can be drawn into wires)</li>\n  <li>Lustrous (shiny) when polished</li>\n  <li>High melting and boiling points (exceptions: Na, K are soft; Hg is liquid)</li>\n  <li>High density (exceptions: Na, K, Li are less dense than water)</li>\n  <li>Form positive ions (cations) by losing electrons</li>\n  <li>Form ionic/basic oxides</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Properties of Non-Metals</h3>\n<ul class=\"learn-list\">\n  <li>Generally poor conductors of heat and electricity (exception: graphite)</li>\n  <li>Brittle as solids (not malleable or ductile)</li>\n  <li>Generally low melting and boiling points</li>\n  <li>Form negative ions (anions) by gaining electrons</li>\n  <li>Form acidic or neutral oxides</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Reactivity Series of Metals</h3>\n<p class=\"learn-p\">The reactivity series arranges metals in decreasing order of reactivity:</p>\n<p class=\"learn-p\">K > Na > Ca > Mg > Al > Zn > Fe > Pb > H > Cu > Ag > Au</p>\n<p class=\"learn-p\"><strong>Memory aid:</strong> \"King Sol Called Me A Zealous Iron Provider Can't Apply\"</p>\n\n<h3 class=\"learn-subheading\">4. Reactions of Metals</h3>\n<ul class=\"learn-list\">\n  <li><strong>With water:</strong> K and Na react violently; Mg reacts with steam; Fe rusts; Cu, Au — no reaction.</li>\n  <li><strong>With dilute acid:</strong> Metals above H in activity series displace H₂. Cu, Ag, Au — no reaction.</li>\n  <li><strong>With oxygen:</strong> Most metals form metal oxides on heating.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Metals: conductors, malleable, ductile, form positive ions, basic oxides. Non-metals: poor conductors (except graphite), brittle, form negative ions, acidic oxides. Reactivity series: K>Na>Ca>Mg>Al>Zn>Fe>Pb>H>Cu>Ag>Au. Metals above H displace H₂ from acids.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Metals: conductors, malleable, ductile, form cations. Non-metals: poor conductors, brittle, form anions. Reactivity series determines which metals displace others. More reactive metals displace less reactive ones from solutions.\n</div>\n  ",
    "questions": [
      {
        "q": "Which of the following is a property of metals?",
        "o": ["Poor conductors of electricity", "Brittle when solid", "Malleable and ductile", "Form negative ions"],
        "a": 2,
        "e": "Metals are malleable (can be beaten into sheets) and ductile (can be drawn into wires) due to the ability of metal layers to slide over each other without breaking.",
        "h": "Metals are malleable and ductile.",
        "yr": "SS1"
      },
      {
        "q": "Non-metals generally are:",
        "o": ["Good conductors of heat and electricity", "Malleable and shiny", "Poor conductors and form acidic oxides", "All solids at room temperature"],
        "a": 2,
        "e": "Non-metals are generally poor conductors of heat and electricity (with the exception of graphite) and their oxides are acidic or neutral. They are brittle as solids.",
        "h": "Non-metals: poor conductors, form acidic oxides.",
        "yr": "SS1"
      },
      {
        "q": "In the reactivity series, which metal is most reactive?",
        "o": ["Copper", "Iron", "Sodium", "Potassium"],
        "a": 3,
        "e": "Potassium (K) is at the top of the reactivity series — the most reactive common metal. It reacts violently with water, producing KOH and H₂ with a lilac flame.",
        "h": "K > Na > Ca > Mg ... K is most reactive.",
        "yr": "SS1"
      },
      {
        "q": "A metal above hydrogen in the reactivity series will:",
        "o": ["Not react with dilute acid", "React with dilute acid to produce hydrogen gas", "React with dilute acid to produce oxygen", "Dissolve in acid without producing a gas"],
        "a": 1,
        "e": "Metals above hydrogen in the reactivity series displace H₂ from dilute acids. Example: Mg + 2HCl → MgCl₂ + H₂. Metals below H (Cu, Ag, Au) do not react with dilute acids.",
        "h": "Metals above H react with acids to give H₂ gas.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is the correct order of reactivity (most to least)?",
        "o": ["Cu > Zn > Fe > Mg", "K > Na > Mg > Al > Zn > Fe > Cu", "Fe > Na > K > Mg", "Cu > Ag > Au > Fe"],
        "a": 1,
        "e": "The correct reactivity order (most to least reactive) is K > Na > Ca > Mg > Al > Zn > Fe > Pb > H > Cu > Ag > Au.",
        "h": "K>Na>Mg>Al>Zn>Fe... then Cu>Ag>Au (least reactive).",
        "yr": "SS1"
      },
      {
        "q": "Sodium reacts vigorously with water. This is because sodium is:",
        "o": ["Below copper in the reactivity series", "Less reactive than potassium but very reactive", "More reactive than potassium", "Not in the reactivity series"],
        "a": 1,
        "e": "Sodium is a very reactive metal (2nd most reactive common metal after potassium). It reacts vigorously with water: 2Na + 2H₂O → 2NaOH + H₂, floating, fizzing, and sometimes catching fire.",
        "h": "Na is very reactive (K>Na>Ca...) — reacts vigorously with water.",
        "yr": "SS1"
      },
      {
        "q": "The oxide of a metal is typically:",
        "o": ["Acidic", "Neutral", "Basic", "Neither acidic nor basic"],
        "a": 2,
        "e": "Metal oxides are typically basic — they react with acids to form salts and water. Examples: CuO + H₂SO₄ → CuSO₄ + H₂O; MgO + 2HCl → MgCl₂ + H₂O.",
        "h": "Metal oxides are basic (react with acids).",
        "yr": "SS1"
      },
      {
        "q": "The oxide of a non-metal is typically:",
        "o": ["Basic (reacts with acids)", "Acidic or neutral", "A strong conductor of electricity", "Insoluble in water"],
        "a": 1,
        "e": "Non-metal oxides are typically acidic — they dissolve in water to form acids. Examples: CO₂ → H₂CO₃; SO₃ → H₂SO₄; NO₂ → HNO₃.",
        "h": "Non-metal oxides are acidic (dissolve in water to form acids).",
        "yr": "SS1"
      },
      {
        "q": "Which metal does NOT react with dilute hydrochloric acid?",
        "o": ["Zinc", "Iron", "Copper", "Magnesium"],
        "a": 2,
        "e": "Copper is below hydrogen in the reactivity series and does not react with dilute acids. Zinc, iron, and magnesium are all above hydrogen and react to give H₂ gas.",
        "h": "Copper (below H in reactivity series) does not react with dilute HCl.",
        "yr": "SS1"
      },
      {
        "q": "A displacement reaction occurs when:",
        "o": ["Two metals both react with water simultaneously", "A more reactive metal displaces a less reactive metal from its salt solution", "A non-metal reacts with an acid", "Two non-metals combine to form a compound"],
        "a": 1,
        "e": "A more reactive metal displaces a less reactive metal from its salt solution. Example: Zn + CuSO₄ → ZnSO₄ + Cu. Zinc is more reactive than copper.",
        "h": "More reactive metal displaces less reactive metal from solution.",
        "yr": "SS1"
      },
      {
        "q": "Graphite is a non-metal that conducts electricity. This is because:",
        "o": ["Graphite contains metal impurities", "Graphite has delocalised electrons that are free to move", "Non-metals always conduct electricity", "Graphite is actually a metalloid"],
        "a": 1,
        "e": "In graphite, each carbon atom forms 3 covalent bonds, leaving one delocalised electron per atom free to move throughout the layers. These mobile electrons allow graphite to conduct electricity.",
        "h": "Graphite: delocalised electrons → conducts electricity.",
        "yr": "SS1"
      },
      {
        "q": "Rusting of iron requires both:",
        "o": ["Oxygen and high temperature", "Oxygen and water (moisture)", "Water and carbon dioxide", "Heat and salt water only"],
        "a": 1,
        "e": "Iron rusts in the presence of both oxygen and water. If either is absent, rusting does not occur. The process forms hydrated iron(III) oxide (Fe₂O₃·nH₂O) — rust.",
        "h": "Rusting requires both oxygen AND water.",
        "yr": "SS1"
      },
      {
        "q": "Which method can prevent rusting by excluding oxygen and water?",
        "o": ["Alloying with chromium only", "Painting or coating with a protective layer", "Heating the metal to very high temperatures", "Dissolving in acid first"],
        "a": 1,
        "e": "Painting or coating (with oil, grease, or another metal) creates a physical barrier that excludes oxygen and water, preventing rust formation.",
        "h": "Painting/coating = physical barrier against oxygen and water.",
        "yr": "SS1"
      },
      {
        "q": "Metals form positive ions (cations) by:",
        "o": ["Gaining electrons", "Losing electrons", "Gaining protons", "Losing neutrons"],
        "a": 1,
        "e": "Metals form cations by losing their valence electrons. For example, Na → Na⁺ + e⁻. The positive ion forms because protons now outnumber electrons.",
        "h": "Metals lose electrons → positive ions (cations).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of a metalloid (semi-metal)?",
        "o": ["Iron", "Sulfur", "Silicon", "Chlorine"],
        "a": 2,
        "e": "Silicon is a metalloid — it has properties intermediate between metals and non-metals. It is a semiconductor, used in computer chips and solar cells.",
        "h": "Silicon is a metalloid (semiconductor).",
        "yr": "SS1"
      },
      {
        "q": "Aluminium is resistant to corrosion despite being a reactive metal because:",
        "o": ["It is below copper in the reactivity series", "It forms a thin, protective layer of aluminium oxide on its surface", "It does not react with oxygen", "Aluminium is actually a non-metal"],
        "a": 1,
        "e": "Aluminium reacts quickly with oxygen in air to form a thin, hard, transparent layer of Al₂O₃ on its surface. This oxide layer protects the metal underneath from further corrosion.",
        "h": "Al forms protective Al₂O₃ layer → resists further corrosion.",
        "yr": "SS1"
      },
      {
        "q": "The reactivity of alkali metals increases going down Group I because:",
        "o": ["They have more protons down the group", "The outermost electron is further from the nucleus and more easily lost", "They react with fewer substances down the group", "Their atomic radius decreases down the group"],
        "a": 1,
        "e": "Going down Group I, the outer electron is in a shell further from the nucleus and more shielded. It is easier to lose → greater reactivity.",
        "h": "Down Group I: outer electron further/more shielded → easier to lose → more reactive.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following metals would be produced at the cathode during electrolysis of its molten chloride?",
        "o": ["Chlorine gas", "Chloride ion", "The metal ion reduced to the metal", "Water"],
        "a": 2,
        "e": "At the cathode (negative electrode) during electrolysis of molten metal chloride, metal ions (M⁺ or M²⁺) are reduced by gaining electrons: M⁺ + e⁻ → M (metal). For example, Na⁺ + e⁻ → Na.",
        "h": "Cathode: metal ions reduced → metal deposited.",
        "yr": "SS1"
      },
      {
        "q": "Non-metals form negative ions (anions) by:",
        "o": ["Losing electrons", "Gaining protons", "Gaining electrons", "Losing neutrons"],
        "a": 2,
        "e": "Non-metals form anions by gaining electrons to complete their outer shells. For example, Cl + e⁻ → Cl⁻; O + 2e⁻ → O²⁻.",
        "h": "Non-metals gain electrons → negative ions (anions).",
        "yr": "SS1"
      },
      {
        "q": "Gold (Au) does not tarnish or corrode because it is:",
        "o": ["A very light metal", "Below copper in the reactivity series — very unreactive", "A non-metal", "Above copper in the reactivity series"],
        "a": 1,
        "e": "Gold is at the very bottom of the reactivity series — it is extremely unreactive. It does not react with oxygen, water, or most acids, which is why it maintains its shine indefinitely.",
        "h": "Gold is at the bottom of reactivity series = very unreactive.",
        "yr": "SS1"
      }
    ]
  }
];
